/**
 * Copyright (c) 2024 Cisco Systems, Inc. and/or its affiliated entities.
 * 
 * Your use of the AI effects in this library (e.g., noise reduction, virtual background, etc.) are subject to the Cisco End User License Agreement located at: https://www.cisco.com/c/dam/en_us/about/doing_business/legal/eula/cisco_end_user_license_agreement-eng.pdf
 */

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

// Unique ID creation requires a high quality random # generator. In the browser we therefore
// require the crypto API and do not support built-in fallback to lower quality random number
// generators (like Math.random()).
let getRandomValues;
const rnds8 = new Uint8Array(16);
function rng() {
  // lazy load so that environments that need to polyfill have a chance to do so
  if (!getRandomValues) {
    // getRandomValues needs to be invoked in a context where "this" is a Crypto implementation.
    getRandomValues = typeof crypto !== 'undefined' && crypto.getRandomValues && crypto.getRandomValues.bind(crypto);

    if (!getRandomValues) {
      throw new Error('crypto.getRandomValues() not supported. See https://github.com/uuidjs/uuid#getrandomvalues-not-supported');
    }
  }

  return getRandomValues(rnds8);
}

/**
 * Convert array of 16 byte values to UUID string format of the form:
 * XXXXXXXX-XXXX-XXXX-XXXX-XXXXXXXXXXXX
 */

const byteToHex = [];

for (let i = 0; i < 256; ++i) {
  byteToHex.push((i + 0x100).toString(16).slice(1));
}

function unsafeStringify(arr, offset = 0) {
  // Note: Be careful editing this code!  It's been tuned for performance
  // and works in ways you may not expect. See https://github.com/uuidjs/uuid/pull/434
  return byteToHex[arr[offset + 0]] + byteToHex[arr[offset + 1]] + byteToHex[arr[offset + 2]] + byteToHex[arr[offset + 3]] + '-' + byteToHex[arr[offset + 4]] + byteToHex[arr[offset + 5]] + '-' + byteToHex[arr[offset + 6]] + byteToHex[arr[offset + 7]] + '-' + byteToHex[arr[offset + 8]] + byteToHex[arr[offset + 9]] + '-' + byteToHex[arr[offset + 10]] + byteToHex[arr[offset + 11]] + byteToHex[arr[offset + 12]] + byteToHex[arr[offset + 13]] + byteToHex[arr[offset + 14]] + byteToHex[arr[offset + 15]];
}

const randomUUID = typeof crypto !== 'undefined' && crypto.randomUUID && crypto.randomUUID.bind(crypto);
var native = {
  randomUUID
};

function v4(options, buf, offset) {
  if (native.randomUUID && !buf && !options) {
    return native.randomUUID();
  }

  options = options || {};
  const rnds = options.random || (options.rng || rng)(); // Per 4.4, set bits for version and `clock_seq_hi_and_reserved`

  rnds[6] = rnds[6] & 0x0f | 0x40;
  rnds[8] = rnds[8] & 0x3f | 0x80; // Copy bytes to buffer, if provided

  if (buf) {
    offset = offset || 0;

    for (let i = 0; i < 16; ++i) {
      buf[offset + i] = rnds[i];
    }

    return buf;
  }

  return unsafeStringify(rnds);
}

var commonjsGlobal = typeof globalThis !== 'undefined' ? globalThis : typeof window !== 'undefined' ? window : typeof global !== 'undefined' ? global : typeof self !== 'undefined' ? self : {};

var events = {exports: {}};

var R$2 = typeof Reflect === 'object' ? Reflect : null;
var ReflectApply = R$2 && typeof R$2.apply === 'function'
  ? R$2.apply
  : function ReflectApply(target, receiver, args) {
    return Function.prototype.apply.call(target, receiver, args);
  };

var ReflectOwnKeys;
if (R$2 && typeof R$2.ownKeys === 'function') {
  ReflectOwnKeys = R$2.ownKeys;
} else if (Object.getOwnPropertySymbols) {
  ReflectOwnKeys = function ReflectOwnKeys(target) {
    return Object.getOwnPropertyNames(target)
      .concat(Object.getOwnPropertySymbols(target));
  };
} else {
  ReflectOwnKeys = function ReflectOwnKeys(target) {
    return Object.getOwnPropertyNames(target);
  };
}

function ProcessEmitWarning(warning) {
  if (console && console.warn) console.warn(warning);
}

var NumberIsNaN = Number.isNaN || function NumberIsNaN(value) {
  return value !== value;
};

function EventEmitter$1() {
  EventEmitter$1.init.call(this);
}
events.exports = EventEmitter$1;
events.exports.once = once;

// Backwards-compat with node 0.10.x
EventEmitter$1.EventEmitter = EventEmitter$1;

EventEmitter$1.prototype._events = undefined;
EventEmitter$1.prototype._eventsCount = 0;
EventEmitter$1.prototype._maxListeners = undefined;

// By default EventEmitters will print a warning if more than 10 listeners are
// added to it. This is a useful default which helps finding memory leaks.
var defaultMaxListeners = 10;

function checkListener(listener) {
  if (typeof listener !== 'function') {
    throw new TypeError('The "listener" argument must be of type Function. Received type ' + typeof listener);
  }
}

Object.defineProperty(EventEmitter$1, 'defaultMaxListeners', {
  enumerable: true,
  get: function() {
    return defaultMaxListeners;
  },
  set: function(arg) {
    if (typeof arg !== 'number' || arg < 0 || NumberIsNaN(arg)) {
      throw new RangeError('The value of "defaultMaxListeners" is out of range. It must be a non-negative number. Received ' + arg + '.');
    }
    defaultMaxListeners = arg;
  }
});

EventEmitter$1.init = function() {

  if (this._events === undefined ||
      this._events === Object.getPrototypeOf(this)._events) {
    this._events = Object.create(null);
    this._eventsCount = 0;
  }

  this._maxListeners = this._maxListeners || undefined;
};

// Obviously not all Emitters should be limited to 10. This function allows
// that to be increased. Set to zero for unlimited.
EventEmitter$1.prototype.setMaxListeners = function setMaxListeners(n) {
  if (typeof n !== 'number' || n < 0 || NumberIsNaN(n)) {
    throw new RangeError('The value of "n" is out of range. It must be a non-negative number. Received ' + n + '.');
  }
  this._maxListeners = n;
  return this;
};

function _getMaxListeners(that) {
  if (that._maxListeners === undefined)
    return EventEmitter$1.defaultMaxListeners;
  return that._maxListeners;
}

EventEmitter$1.prototype.getMaxListeners = function getMaxListeners() {
  return _getMaxListeners(this);
};

EventEmitter$1.prototype.emit = function emit(type) {
  var args = [];
  for (var i = 1; i < arguments.length; i++) args.push(arguments[i]);
  var doError = (type === 'error');

  var events = this._events;
  if (events !== undefined)
    doError = (doError && events.error === undefined);
  else if (!doError)
    return false;

  // If there is no 'error' event listener then throw.
  if (doError) {
    var er;
    if (args.length > 0)
      er = args[0];
    if (er instanceof Error) {
      // Note: The comments on the `throw` lines are intentional, they show
      // up in Node's output if this results in an unhandled exception.
      throw er; // Unhandled 'error' event
    }
    // At least give some kind of context to the user
    var err = new Error('Unhandled error.' + (er ? ' (' + er.message + ')' : ''));
    err.context = er;
    throw err; // Unhandled 'error' event
  }

  var handler = events[type];

  if (handler === undefined)
    return false;

  if (typeof handler === 'function') {
    ReflectApply(handler, this, args);
  } else {
    var len = handler.length;
    var listeners = arrayClone(handler, len);
    for (var i = 0; i < len; ++i)
      ReflectApply(listeners[i], this, args);
  }

  return true;
};

function _addListener(target, type, listener, prepend) {
  var m;
  var events;
  var existing;

  checkListener(listener);

  events = target._events;
  if (events === undefined) {
    events = target._events = Object.create(null);
    target._eventsCount = 0;
  } else {
    // To avoid recursion in the case that type === "newListener"! Before
    // adding it to the listeners, first emit "newListener".
    if (events.newListener !== undefined) {
      target.emit('newListener', type,
                  listener.listener ? listener.listener : listener);

      // Re-assign `events` because a newListener handler could have caused the
      // this._events to be assigned to a new object
      events = target._events;
    }
    existing = events[type];
  }

  if (existing === undefined) {
    // Optimize the case of one listener. Don't need the extra array object.
    existing = events[type] = listener;
    ++target._eventsCount;
  } else {
    if (typeof existing === 'function') {
      // Adding the second element, need to change to array.
      existing = events[type] =
        prepend ? [listener, existing] : [existing, listener];
      // If we've already got an array, just append.
    } else if (prepend) {
      existing.unshift(listener);
    } else {
      existing.push(listener);
    }

    // Check for listener leak
    m = _getMaxListeners(target);
    if (m > 0 && existing.length > m && !existing.warned) {
      existing.warned = true;
      // No error code for this since it is a Warning
      // eslint-disable-next-line no-restricted-syntax
      var w = new Error('Possible EventEmitter memory leak detected. ' +
                          existing.length + ' ' + String(type) + ' listeners ' +
                          'added. Use emitter.setMaxListeners() to ' +
                          'increase limit');
      w.name = 'MaxListenersExceededWarning';
      w.emitter = target;
      w.type = type;
      w.count = existing.length;
      ProcessEmitWarning(w);
    }
  }

  return target;
}

EventEmitter$1.prototype.addListener = function addListener(type, listener) {
  return _addListener(this, type, listener, false);
};

EventEmitter$1.prototype.on = EventEmitter$1.prototype.addListener;

EventEmitter$1.prototype.prependListener =
    function prependListener(type, listener) {
      return _addListener(this, type, listener, true);
    };

function onceWrapper() {
  if (!this.fired) {
    this.target.removeListener(this.type, this.wrapFn);
    this.fired = true;
    if (arguments.length === 0)
      return this.listener.call(this.target);
    return this.listener.apply(this.target, arguments);
  }
}

function _onceWrap(target, type, listener) {
  var state = { fired: false, wrapFn: undefined, target: target, type: type, listener: listener };
  var wrapped = onceWrapper.bind(state);
  wrapped.listener = listener;
  state.wrapFn = wrapped;
  return wrapped;
}

EventEmitter$1.prototype.once = function once(type, listener) {
  checkListener(listener);
  this.on(type, _onceWrap(this, type, listener));
  return this;
};

EventEmitter$1.prototype.prependOnceListener =
    function prependOnceListener(type, listener) {
      checkListener(listener);
      this.prependListener(type, _onceWrap(this, type, listener));
      return this;
    };

// Emits a 'removeListener' event if and only if the listener was removed.
EventEmitter$1.prototype.removeListener =
    function removeListener(type, listener) {
      var list, events, position, i, originalListener;

      checkListener(listener);

      events = this._events;
      if (events === undefined)
        return this;

      list = events[type];
      if (list === undefined)
        return this;

      if (list === listener || list.listener === listener) {
        if (--this._eventsCount === 0)
          this._events = Object.create(null);
        else {
          delete events[type];
          if (events.removeListener)
            this.emit('removeListener', type, list.listener || listener);
        }
      } else if (typeof list !== 'function') {
        position = -1;

        for (i = list.length - 1; i >= 0; i--) {
          if (list[i] === listener || list[i].listener === listener) {
            originalListener = list[i].listener;
            position = i;
            break;
          }
        }

        if (position < 0)
          return this;

        if (position === 0)
          list.shift();
        else {
          spliceOne(list, position);
        }

        if (list.length === 1)
          events[type] = list[0];

        if (events.removeListener !== undefined)
          this.emit('removeListener', type, originalListener || listener);
      }

      return this;
    };

EventEmitter$1.prototype.off = EventEmitter$1.prototype.removeListener;

EventEmitter$1.prototype.removeAllListeners =
    function removeAllListeners(type) {
      var listeners, events, i;

      events = this._events;
      if (events === undefined)
        return this;

      // not listening for removeListener, no need to emit
      if (events.removeListener === undefined) {
        if (arguments.length === 0) {
          this._events = Object.create(null);
          this._eventsCount = 0;
        } else if (events[type] !== undefined) {
          if (--this._eventsCount === 0)
            this._events = Object.create(null);
          else
            delete events[type];
        }
        return this;
      }

      // emit removeListener for all listeners on all events
      if (arguments.length === 0) {
        var keys = Object.keys(events);
        var key;
        for (i = 0; i < keys.length; ++i) {
          key = keys[i];
          if (key === 'removeListener') continue;
          this.removeAllListeners(key);
        }
        this.removeAllListeners('removeListener');
        this._events = Object.create(null);
        this._eventsCount = 0;
        return this;
      }

      listeners = events[type];

      if (typeof listeners === 'function') {
        this.removeListener(type, listeners);
      } else if (listeners !== undefined) {
        // LIFO order
        for (i = listeners.length - 1; i >= 0; i--) {
          this.removeListener(type, listeners[i]);
        }
      }

      return this;
    };

function _listeners(target, type, unwrap) {
  var events = target._events;

  if (events === undefined)
    return [];

  var evlistener = events[type];
  if (evlistener === undefined)
    return [];

  if (typeof evlistener === 'function')
    return unwrap ? [evlistener.listener || evlistener] : [evlistener];

  return unwrap ?
    unwrapListeners(evlistener) : arrayClone(evlistener, evlistener.length);
}

EventEmitter$1.prototype.listeners = function listeners(type) {
  return _listeners(this, type, true);
};

EventEmitter$1.prototype.rawListeners = function rawListeners(type) {
  return _listeners(this, type, false);
};

EventEmitter$1.listenerCount = function(emitter, type) {
  if (typeof emitter.listenerCount === 'function') {
    return emitter.listenerCount(type);
  } else {
    return listenerCount.call(emitter, type);
  }
};

EventEmitter$1.prototype.listenerCount = listenerCount;
function listenerCount(type) {
  var events = this._events;

  if (events !== undefined) {
    var evlistener = events[type];

    if (typeof evlistener === 'function') {
      return 1;
    } else if (evlistener !== undefined) {
      return evlistener.length;
    }
  }

  return 0;
}

EventEmitter$1.prototype.eventNames = function eventNames() {
  return this._eventsCount > 0 ? ReflectOwnKeys(this._events) : [];
};

function arrayClone(arr, n) {
  var copy = new Array(n);
  for (var i = 0; i < n; ++i)
    copy[i] = arr[i];
  return copy;
}

function spliceOne(list, index) {
  for (; index + 1 < list.length; index++)
    list[index] = list[index + 1];
  list.pop();
}

function unwrapListeners(arr) {
  var ret = new Array(arr.length);
  for (var i = 0; i < ret.length; ++i) {
    ret[i] = arr[i].listener || arr[i];
  }
  return ret;
}

function once(emitter, name) {
  return new Promise(function (resolve, reject) {
    function errorListener(err) {
      emitter.removeListener(name, resolver);
      reject(err);
    }

    function resolver() {
      if (typeof emitter.removeListener === 'function') {
        emitter.removeListener('error', errorListener);
      }
      resolve([].slice.call(arguments));
    }
    eventTargetAgnosticAddListener(emitter, name, resolver, { once: true });
    if (name !== 'error') {
      addErrorHandlerIfEventEmitter(emitter, errorListener, { once: true });
    }
  });
}

function addErrorHandlerIfEventEmitter(emitter, handler, flags) {
  if (typeof emitter.on === 'function') {
    eventTargetAgnosticAddListener(emitter, 'error', handler, flags);
  }
}

function eventTargetAgnosticAddListener(emitter, name, listener, flags) {
  if (typeof emitter.on === 'function') {
    if (flags.once) {
      emitter.once(name, listener);
    } else {
      emitter.on(name, listener);
    }
  } else if (typeof emitter.addEventListener === 'function') {
    // EventTarget does not have `error` event semantics like Node
    // EventEmitters, we do not listen for `error` events here.
    emitter.addEventListener(name, function wrapListener(arg) {
      // IE does not have builtin `{ once: true }` support so we
      // have to do it manually.
      if (flags.once) {
        emitter.removeEventListener(name, wrapListener);
      }
      listener(arg);
    });
  } else {
    throw new TypeError('The "emitter" argument must be of type EventEmitter. Received type ' + typeof emitter);
  }
}

class EventEmitter extends events.exports.EventEmitter {
}

const e$5 = "1.20.1", t = "0.7.7.2", i = "segmentation.js", n$1 = { int: "https://models.int.infra.intelligence.webex.com", prod: "https://models.intelligence.webex.com" }, o$1 = { models: "ladon/models", wasm: "ladon/wasm", workers: "ladon/workers" }, l$3 = { weak: { kernelSize: 9, sigma: 2 }, moderate: { kernelSize: 15, sigma: 6 }, strong: { kernelSize: 25, sigma: 11 }, stronger: { kernelSize: 37, sigma: 17 }, strongest: { kernelSize: 49, sigma: 23 } }, h = { tiny: { modelFile: "tiny-640x360.onnx", inputSize: { width: 640, height: 360 }, outputSize: { width: 640, height: 360 }, upscaleSize: { width: 640, height: 360 }, generationIntervalMs: 48, modelRank: 4 }, low: { modelFile: "low-640x360.onnx", inputSize: { width: 640, height: 360 }, outputSize: { width: 640, height: 360 }, upscaleSize: { width: 640, height: 360 }, generationIntervalMs: 48, modelRank: 4 }, medium: { modelFile: "medium-640x360.onnx", inputSize: { width: 640, height: 360 }, outputSize: { width: 640, height: 360 }, upscaleSize: { width: 640, height: 360 }, generationIntervalMs: 36, modelRank: 4 }, high: { modelFile: "medium-640x360.onnx", inputSize: { width: 640, height: 360 }, outputSize: { width: 640, height: 360 }, upscaleSize: { width: 640, height: 360 }, generationIntervalMs: 24, modelRank: 4 }, ultra: { modelFile: "medium-640x360.onnx", inputSize: { width: 640, height: 360 }, outputSize: { width: 640, height: 360 }, upscaleSize: { width: 640, height: 360 }, generationIntervalMs: 0, modelRank: 4 } }, s = "${BASE_PATH_MODELS}/${MODEL_VERSION}/${MODEL_FILE}", a$1 = "${BASE_PATH_WASM}/${ONNX_VERSION}/", d$3 = "${BASE_PATH_WORKERS}/${LIB_VERSION}/${WORKER_FILE}", m = {
  onnxVersion: e$5,
  modelVersion: t,
  workerFile: i,
  baseUrls: n$1,
  basePaths: o$1,
  blurStrengths: l$3,
  modelQualities: h,
  modelUrlTemplate: s,
  wasmPathTemplate: a$1,
  workerUrlTemplate: d$3
};

function e$4(t) {
  return t && t.__esModule && Object.prototype.hasOwnProperty.call(t, "default") ? t.default : t;
}

var e$3 = { exports: {} };

/*!
 * js-logger - http://github.com/jonnyreeves/js-logger
 * Jonny Reeves, http://jonnyreeves.co.uk/
 * js-logger may be freely distributed under the MIT license.
 */
var R$1 = e$3.exports, E$2;
function F() {
  return E$2 ? e$3.exports : (E$2 = 1, function(d) {
    (function(g) {
      var n = {};
      n.VERSION = "1.6.1";
      var m, c = {}, f = function(e, t) {
        return function() {
          return t.apply(e, arguments);
        };
      }, h = function() {
        var e = arguments, t = e[0], a, o;
        for (o = 1; o < e.length; o++)
          for (a in e[o])
            !(a in t) && e[o].hasOwnProperty(a) && (t[a] = e[o][a]);
        return t;
      }, u = function(e, t) {
        return { value: e, name: t };
      };
      n.TRACE = u(1, "TRACE"), n.DEBUG = u(2, "DEBUG"), n.INFO = u(3, "INFO"), n.TIME = u(4, "TIME"), n.WARN = u(5, "WARN"), n.ERROR = u(8, "ERROR"), n.OFF = u(99, "OFF");
      var L = function(e) {
        this.context = e, this.setLevel(e.filterLevel), this.log = this.info;
      };
      L.prototype = {
        // Changes the current logging level for the logging instance.
        setLevel: function(e) {
          e && "value" in e && (this.context.filterLevel = e);
        },
        // Gets the current logging level for the logging instance
        getLevel: function() {
          return this.context.filterLevel;
        },
        // Is the logger configured to output messages at the supplied level?
        enabledFor: function(e) {
          var t = this.context.filterLevel;
          return e.value >= t.value;
        },
        trace: function() {
          this.invoke(n.TRACE, arguments);
        },
        debug: function() {
          this.invoke(n.DEBUG, arguments);
        },
        info: function() {
          this.invoke(n.INFO, arguments);
        },
        warn: function() {
          this.invoke(n.WARN, arguments);
        },
        error: function() {
          this.invoke(n.ERROR, arguments);
        },
        time: function(e) {
          typeof e == "string" && e.length > 0 && this.invoke(n.TIME, [e, "start"]);
        },
        timeEnd: function(e) {
          typeof e == "string" && e.length > 0 && this.invoke(n.TIME, [e, "end"]);
        },
        // Invokes the logger callback if it's not being filtered.
        invoke: function(e, t) {
          m && this.enabledFor(e) && m(t, h({ level: e }, this.context));
        }
      };
      var r = new L({ filterLevel: n.OFF });
      ((function() {
        var e = n;
        e.enabledFor = f(r, r.enabledFor), e.trace = f(r, r.trace), e.debug = f(r, r.debug), e.time = f(r, r.time), e.timeEnd = f(r, r.timeEnd), e.info = f(r, r.info), e.warn = f(r, r.warn), e.error = f(r, r.error), e.log = e.info;
      }))(), n.setHandler = function(e) {
        m = e;
      }, n.setLevel = function(e) {
        r.setLevel(e);
        for (var t in c)
          c.hasOwnProperty(t) && c[t].setLevel(e);
      }, n.getLevel = function() {
        return r.getLevel();
      }, n.get = function(e) {
        return c[e] || (c[e] = new L(h({ name: e }, r.context)));
      }, n.createDefaultHandler = function(e) {
        e = e || {}, e.formatter = e.formatter || function(i, l) {
          l.name && i.unshift("[" + l.name + "]");
        };
        var t = {}, a = function(o, i) {
          Function.prototype.apply.call(o, console, i);
        };
        return typeof console > "u" ? function() {
        } : function(o, i) {
          o = Array.prototype.slice.call(o);
          var l = console.log, s;
          i.level === n.TIME ? (s = (i.name ? "[" + i.name + "] " : "") + o[0], o[1] === "start" ? console.time ? console.time(s) : t[s] = (/* @__PURE__ */ new Date()).getTime() : console.timeEnd ? console.timeEnd(s) : a(l, [s + ": " + ((/* @__PURE__ */ new Date()).getTime() - t[s]) + "ms"])) : (i.level === n.WARN && console.warn ? l = console.warn : i.level === n.ERROR && console.error ? l = console.error : i.level === n.INFO && console.info ? l = console.info : i.level === n.DEBUG && console.debug ? l = console.debug : i.level === n.TRACE && console.trace && (l = console.trace), e.formatter(o, i), a(l, o));
        };
      }, n.useDefaults = function(e) {
        n.setLevel(e && e.defaultLevel || n.DEBUG), n.setHandler(n.createDefaultHandler(e));
      }, n.setDefaults = n.useDefaults, d.exports ? d.exports = n : (n._prevLogger = g.Logger, n.noConflict = function() {
        return g.Logger = n._prevLogger, n;
      }, g.Logger = n);
    })(R$1);
  }(e$3), e$3.exports);
}

var e$2 = F();
const a = /* @__PURE__ */ e$4(e$2);

const e$1 = a.get("ladon-ts"), g$1 = Object.assign(e$1, {
  errorAndThrow(r) {
    throw e$1.error(r), new Error(r);
  }
}), n = a.createDefaultHandler({
  /**
   * Formatter for console logs.
   *
   * @param msg - Log message.
   * @param context - Log context.
   */
  formatter: (r, t) => {
    r.unshift(t.name);
  }
});
a.setHandler(n);

const o = "5.10.0";

class g {
  constructor(e = "prod") {
    this.env = e, this.config = {};
  }
  /**
   * Sets the base URL for the configuration.
   * @param baseUrl The base URL to use.
   */
  setBaseUrl(e) {
    return this.config.baseUrl = e.replace(/\/+$/, ""), this;
  }
  /**
   * Sets the WASM URI for the configuration.
   * @param wasmUri The WASM URI to use.
   */
  setWasmUri(e) {
    return this.config.wasmUri = e, this;
  }
  /**
   * Sets the asset URL resolver.
   * @param assetUrlResolver The asset URL resolver to use
   * @returns The current ConfigurationBuilder instance.
   */
  setAssetUrlResolver(e) {
    return this.config.assetUrlResolver = e, this;
  }
  /**
   * Sets the execution providers for the ONNX runtime.
   *
   * @param executionProviders The execution providers to use.
   */
  setExecutionProviders(e) {
    return typeof e == "string" && (e = [e]), this.config.executionProviders = e, this;
  }
  /**
   * Sets the input size for the configuration.
   * @param height The height of the input.
   * @param width The width of the input.
   */
  setInputSize(e, r) {
    return this.config.input = { height: e, width: r }, this;
  }
  /**
   * Sets the input configuration.
   * @param inputConfig The input configuration to use.
   */
  setInputConfig(e) {
    return this.config.input = { ...e }, this;
  }
  /**
   * Sets the render configuration.
   * @param renderConfig The render configuration to use.
   */
  setRenderConfig(e) {
    return this.config.render = { ...e }, this;
  }
  /**
   * Sets the render type.
   * @param type The render type ('blur', 'passthrough', 'replacement').
   */
  setRenderType(e) {
    return this.config.render ? this.config.render.type = e : this.config.render = { type: e }, this;
  }
  /**
   * Sets the horizontal mirror property for the render configuration.
   * @param horizontalMirror Boolean indicating if horizontal mirror should be applied.
   */
  setRenderHorizontalMirror(e) {
    return this.config.render || g$1.errorAndThrow("Render config must be set before setting horizontal mirror."), this.config.render.horizontalMirror = e, this;
  }
  /**
   * Sets the blur strength for the render configuration.
   * @param blurStrength The blur strength to use.
   */
  setBlurStrength(e) {
    var t;
    const r = g.getBlurConfig(
      e,
      (t = this.config.render) == null ? void 0 : t.horizontalMirror
    );
    return this.config.render = { ...this.config.render, ...r }, this;
  }
  /**
   * Sets the blur kernel size.
   * @param kernelSize The kernel size for the blur effect.
   */
  setBlurKernelSize(e) {
    return (!this.config.render || this.config.render.type !== "blur") && g$1.errorAndThrow("Render config must be of type blur to set blur properties."), this.config.render.kernelSize = e, this;
  }
  /**
   * Sets the blur sigma value.
   * @param sigma The sigma value for the blur effect.
   */
  setBlurSigma(e) {
    return (!this.config.render || this.config.render.type !== "blur") && g$1.errorAndThrow("Render config must be of type blur to set blur properties."), this.config.render.sigma = e, this;
  }
  /**
   * Sets the replacement background.
   * @param background The background canvas or video element to use for replacement.
   */
  setReplacementBackground(e) {
    return (!this.config.render || this.config.render.type !== "replacement") && g$1.errorAndThrow(
      "Render config must be of type replacement to set replacement properties."
    ), this.config.render.background = e, this;
  }
  /**
   * Sets the replacement static property.
   * @param isStatic Boolean indicating if the replacement is static.
   */
  setReplacementStatic(e) {
    return (!this.config.render || this.config.render.type !== "replacement") && g$1.errorAndThrow(
      "Render config must be of type replacement to set replacement properties."
    ), this.config.render.static = e, this;
  }
  /**
   * Sets the mask configuration.
   * @param maskConfig The mask configuration to use.
   */
  setMaskConfig(e) {
    return this.config.mask = { ...e }, this;
  }
  /**
   * Sets the mask generator.
   * @param generator The generator type (most be 'worker'; 'local' is deprecated).
   */
  setMaskGenerator(e) {
    e !== "worker" && g$1.warn('Mask generator must be "worker". "local" is deprecated.');
    const r = this.getMaskConfig();
    return r.generator = e, this;
  }
  /**
   * Sets the mask generation interval in milliseconds.
   * @param intervalMs The generation interval in milliseconds.
   */
  setMaskGenerationIntervalMs(e) {
    const r = this.getMaskConfig();
    return r.generationIntervalMs = e, this;
  }
  /**
   * Sets the mask model rank.
   * @param modelRank The model rank to use.
   */
  setMaskModelRank(e) {
    const r = this.getMaskConfig();
    return r.modelRank = e, this;
  }
  /**
   * Sets the mask model URI.
   * @param modelUri The model URI to use.
   */
  setMaskModelUri(e) {
    const r = this.getMaskConfig();
    return r.modelUri = e, this;
  }
  /**
   * Sets the mask worker URI.
   * @param workerUri The worker URI to use.
   */
  setMaskWorkerUri(e) {
    const r = this.getMaskConfig();
    return r.workerUri = e, this;
  }
  /**
   * Sets the mask input size.
   * @param height The height of the mask input.
   * @param width The width of the mask input.
   */
  setMaskInputSize(e, r) {
    const t = this.getMaskConfig();
    return t.inputSize = { height: e, width: r }, this;
  }
  /**
   * Sets the mask output size.
   * @param height The height of the mask output.
   * @param width The width of the mask output.
   */
  setMaskOutputSize(e, r) {
    const t = this.getMaskConfig();
    return t.outputSize = { height: e, width: r }, this;
  }
  /**
   * Sets the mask upscaled size.
   * @param height The height of the upscaled mask.
   * @param width The width of the upscaled mask.
   */
  setMaskUpscaleSize(e, r) {
    const t = this.getMaskConfig();
    return t.upscaleSize = { height: e, width: r }, this;
  }
  /**
   * Sets the quality level and updates mask configurations accordingly.
   * @param quality The desired quality level.
   */
  setMaskQuality(e) {
    const { modelQualities: r } = m, t = this.getMaskConfig(), n = e.toLowerCase(), a = r[n];
    return a || g$1.errorAndThrow(`Unknown quality option: ${e}`), t.qualityKey = n, Object.assign(t, a), this;
  }
  /**
   * Builds and returns the PreloadConfig or PipelineConfig object.
   *
   * @param requireInputConfig Whether to require the input configuration to be set before building.
   * Input size is not available at preload so this needs to be optional. The default value is true.
   *
   */
  build(e = !0) {
    const r = this.resolveBaseUrl(), t = this.resolveModelUri(), n = this.resolveWasmUri(), a = this.resolveWorkerUri(), l = this.config.input, h = this.config.render, o = this.config.mask, c = this.config.executionProviders, u = this.config.assetUrlResolver;
    o || g$1.errorAndThrow(
      "Mask configuration must be set before building the configuration."
    );
    const m = {
      generator: o.generator,
      generationIntervalMs: o.generationIntervalMs,
      modelRank: o.modelRank,
      inputSize: o.inputSize,
      outputSize: o.outputSize,
      upscaleSize: o.upscaleSize,
      modelUri: t,
      workerUri: a
    }, f = {
      assetUrlResolver: u,
      executionProviders: c,
      baseUrl: r,
      wasmUri: n,
      mask: m
    };
    return e ? (l || g$1.errorAndThrow("Input must be set before building the configuration."), h || g$1.errorAndThrow("Render must be set before building the configuration."), {
      ...f,
      input: l,
      render: h
    }) : f;
  }
  /**
   * Returns the mask configuration, initializing it if necessary.
   */
  getMaskConfig() {
    return this.config.mask || (this.config.mask = {}), this.config.mask;
  }
  /**
   * Returns the base URL for the configuration.
   */
  resolveBaseUrl() {
    return this.config.baseUrl || m.baseUrls[this.env];
  }
  /**
   * Returns the base URL for the configuration.
   *
   * @returns The base URL for the configuration.
   */
  resolveModelUri() {
    var r;
    if ((r = this.config.mask) != null && r.modelUri)
      return this.config.mask.modelUri;
    const e = this.getMaskConfig();
    return e.qualityKey || g$1.errorAndThrow("No quality set, cannot infer modelFile."), this.resolveTemplate(m.modelUrlTemplate, {
      BASE_PATH_MODELS: m.basePaths.models,
      MODEL_VERSION: m.modelVersion,
      MODEL_FILE: m.modelQualities[e.qualityKey].modelFile
    });
  }
  resolveWasmUri() {
    return this.config.wasmUri ? this.config.wasmUri : this.resolveTemplate(m.wasmPathTemplate, {
      BASE_PATH_WASM: m.basePaths.wasm,
      ONNX_VERSION: m.onnxVersion
    });
  }
  resolveWorkerUri() {
    var e;
    return (e = this.config.mask) != null && e.workerUri ? this.config.mask.workerUri : this.resolveTemplate(m.workerUrlTemplate, {
      BASE_PATH_WORKERS: m.basePaths.workers,
      LIB_VERSION: g.getLibVersion(),
      WORKER_FILE: m.workerFile
    });
  }
  /**
   * Resolves a template string with the specified values.
   *
   * @param template The template string to resolve.
   * @param values The values to use for resolution.
   * @returns The resolved template string.
   */
  resolveTemplate(e, r) {
    return e.replace(/\$\{([A-Z_]+)\}/g, (t, n) => (r[n] || g$1.errorAndThrow(`Missing value for template variable: ${n}`), r[n]));
  }
  /**
   * Generates a blur configuration based on the specified blur strength.
   * @param blurStrength The desired blur strength.
   * @param horizontalMirror Whether to apply horizontal mirroring.
   */
  static getBlurConfig(e, r) {
    const t = e.toLowerCase(), n = m.blurStrengths[t];
    return n || g$1.errorAndThrow(`Unknown blur strength: ${e}`), {
      type: "blur",
      horizontalMirror: r,
      ...n
    };
  }
  static getPassthroughConfig(e) {
    return {
      type: "passthrough",
      horizontalMirror: e
    };
  }
  /**
   * Returns the library version.
   *
   * @returns The library version.
   */
  static getLibVersion() {
    return o;
  }
}

var E$1 = /* @__PURE__ */ ((T) => (T.TINY = "TINY", T.LOW = "LOW", T.MEDIUM = "MEDIUM", T.HIGH = "HIGH", T.ULTRA = "ULTRA", T))(E$1 || {}), R = /* @__PURE__ */ ((T) => (T.WEAK = "WEAK", T.MODERATE = "MODERATE", T.STRONG = "STRONG", T.STRONGER = "STRONGER", T.STRONGEST = "STRONGEST", T))(R || {});

const d$2 = (o, n, u, r, e) => {
  const l = new Uint8ClampedArray(r * e * 4), y = n / r, S = u / e, s = n * 4;
  let c = 0;
  for (let t = 0; t < e; t++) {
    const f = Math.floor(t * S) * s;
    for (let a = 0; a < r; a++) {
      const m = f + Math.floor(a * y) * 4;
      l[c++] = o[m], l[c++] = o[m + 1], l[c++] = o[m + 2], l[c++] = o[m + 3];
    }
  }
  return l;
};
function L(o, n, u, r, e) {
  const l = new Uint8ClampedArray(r * e);
  if (n === r && u === e) {
    for (let s = 0; s < o.length; s++)
      l[s] = Math.min(Math.max(o[s], 0), 255);
    return l;
  }
  const y = (n - 1) / (r - 1), S = (u - 1) / (e - 1);
  for (let s = 0; s < e; s++)
    for (let c = 0; c < r; c++) {
      const t = y * c, f = S * s, a = Math.floor(t), m = Math.floor(f), w = Math.min(Math.ceil(t), n - 1), x = Math.min(Math.ceil(f), u - 1), p = t - a, i = f - m, M = o[m * n + a], h = o[m * n + w], T = o[x * n + a], A = o[x * n + w], k = M * (1 - p) * (1 - i) + h * p * (1 - i) + T * (1 - p) * i + A * p * i;
      l[s * r + c] = Math.min(Math.max(k, 0), 255);
    }
  return l;
}
function C(o, n, u) {
  const r = o.createShader(n);
  if (r || g$1.errorAndThrow("Error creating shader"), o.shaderSource(r, u), o.compileShader(r), !o.getShaderParameter(r, o.COMPILE_STATUS)) {
    const e = o.getShaderInfoLog(r) ?? "unknown error compiling shader.";
    g$1.errorAndThrow(e);
  }
  return r;
}
function b(o, n, u) {
  const r = o.createProgram();
  if (r || g$1.errorAndThrow("Error creating GPU program"), o.attachShader(r, n), o.attachShader(r, u), o.linkProgram(r), !o.getProgramParameter(r, o.LINK_STATUS)) {
    const e = o.getProgramInfoLog(r) ?? "unknown error linking program.";
    g$1.errorAndThrow(e);
  }
  return r;
}
function z(o) {
  if (o.startsWith("blob:"))
    return !0;
  try {
    return new URL(o), !0;
  } catch {
    return !1;
  }
}
const E = (o, n) => !n || o.startsWith("http") ? new URL(o).href : o ? n.replace(/\/+$/, "") + "/" + o.replace(/^\/+/, "") : n;

class d$1 {
  constructor() {
    this.resolvedUriCache = /* @__PURE__ */ new Map();
  }
  /**
   * Resolves the full URL for a given asset. Non-file URIs are treated as paths and resolved
   * using the `toAbsoluteURI` method. If the assetUri is already resolved, or if it's found in
   * cache, it will be returned directly. If an assetUrlResolver function is provided in the
   * configuration, it will be used to resolve the URL if the assetUri is not fully qualified,
   * a blob URL, or in the cache.
   *
   * @param config - The preload configuration.
   * @param assetUri - The asset URI from the config.
   * @returns The resolved full URL.
   */
  async resolveUri(o, e) {
    if (e || g$1.errorAndThrow("Asset URI is undefined."), this.resolvedUriCache.has(e))
      return this.resolvedUriCache.get(e);
    let r;
    return z(e) ? r = e : e.match(/\.[a-z0-9]+$/i) ? typeof o.assetUrlResolver == "function" && (r = await o.assetUrlResolver(e, o)) : r = E(e, o.baseUrl), r || g$1.errorAndThrow(`Failed to resolve asset URI: ${e}`), this.resolvedUriCache.set(e, r), r;
  }
}
const c$1 = new d$1();

class l$2 {
  constructor() {
    this.destroyPromise = null, this.onInitializedCallbacks = [], this.onGeneratedCallbacks = [], this.onDroppedCallbacks = [], this.onLogCallbacks = [];
  }
  /**
   * Get the current worker instance.
   *
   * @returns The current worker instance or undefined if not initialized.
   */
  getWorker() {
    return this.worker;
  }
  /**
   * Initialize the worker using the provided configuration.
   *
   * @param config - The configuration object.
   * @returns A promise that resolves when the worker is initialized.
   */
  async initializeWorker(e) {
    typeof Worker > "u" && g$1.errorAndThrow("Web workers are not supported in this environment"), this.worker && await this.destroyWorker();
    const r = await c$1.resolveUri(e, e.mask.workerUri), o = await (await fetch(r, { cache: "force-cache" })).text();
    return this.workerUrl = URL.createObjectURL(new Blob([o], { type: "text/javascript" })), this.worker = new Worker(this.workerUrl, { type: "module" }), this.worker.addEventListener("message", (t) => {
      if (t.data.type === "log") {
        const { message: s, severity: k } = t.data;
        this.onLogCallbacks.forEach((d) => d(s, k));
      } else t.data.status === "generated" ? this.onGeneratedCallbacks.forEach((s) => s(t.data)) : t.data.status === "dropped" ? this.onDroppedCallbacks.forEach((s) => s(t.data.timestamp)) : t.data.status === "initialized" && this.onInitializedCallbacks.forEach((s) => s());
    }), this.worker;
  }
  /**
   * Destroys the worker gracefully by sending a destroy message and waiting for confirmation.
   *
   * @returns A promise that resolves when the worker is destroyed.
   *          If no worker is available, it resolves immediately.
   */
  destroyWorker() {
    return this.destroyPromise || (this.destroyPromise = new Promise((e) => {
      const r = (i) => {
        var o;
        i.data.status === "destroyed" && ((o = this.worker) == null || o.removeEventListener("message", r), this.terminateWorker(), this.destroyPromise = null, e());
      };
      if (!this.worker) {
        g$1.warn("No worker to destroy"), e();
        return;
      }
      this.worker.addEventListener("message", r), this.worker.postMessage({ type: "destroy" });
    })), this.destroyPromise;
  }
  /**
   * Terminate the worker.
   */
  terminateWorker() {
    this.worker && (this.worker.terminate(), this.worker = void 0), this.workerUrl && (URL.revokeObjectURL(this.workerUrl), this.workerUrl = void 0);
  }
  /**
   * Preload the worker using the provided configuration.
   *
   * @param config - The configuration object.
   * @returns A promise that resolves when the worker is preloaded.
   */
  async preloadWorker(e) {
    const r = await this.prepareWorkerConfig(e);
    this.worker = await this.initializeWorker(r), this.worker.postMessage({ type: "preload", config: r });
  }
  /**
   * Prepare the worker configuration by resolving the final URLs and paths to send to the
   * worker. This is necessary because we can't send functions to the worker. We also need to
   * remove the assetUrlResolver function from the configuration.
   *
   * @param config - The preload configuration.
   * @returns The worker configuration.
   */
  async prepareWorkerConfig(e) {
    const r = await c$1.resolveUri(e, e.mask.modelUri), i = await c$1.resolveUri(e, e.mask.workerUri), o = await c$1.resolveUri(e, e.wasmUri), s = { ...{
      ...e,
      mask: { ...e.mask, modelUri: r, workerUri: i },
      wasmUri: o
    }, assetUrlResolver: void 0 };
    return JSON.parse(JSON.stringify(s));
  }
  /**
   * Register a callback for when the worker is initialized.
   */
  onInitialized(e) {
    this.onInitializedCallbacks.push(e);
  }
  /**
   * Register a callback for when the worker generates output.
   *
   * @param callback - The callback function to be called with the generated data.
   * @param data - The generated data from the worker.
   * @param data.mask - The generated mask.
   * @param data.facesAndLandmarks - The detected faces and landmarks.
   * @param data.gesture - The detected gesture.
   * @param data.motion - The detected motion.
   */
  onGenerated(e) {
    this.onGeneratedCallbacks.push(e);
  }
  /**
   * Register a callback for when the worker drops a frame.
   * This is useful for handling cases where the worker cannot keep up with the input frame rate.
   * The callback will be called with the timestamp of the dropped frame.
   *
   * @param callback - The callback function to be called with the timestamp of the dropped frame.
   */
  onDropped(e) {
    this.onDroppedCallbacks.push(e);
  }
  /**
   * Register a callback for log messages from the worker.
   *
   * @param callback - The callback function to be called with the log message and severity.
   * @param severity - The severity of the log message (e.g., 'info', 'warn', 'error').
   */
  onLog(e) {
    this.onLogCallbacks.push(e);
  }
  /**
   * Send an init message to the worker.
   *
   * @param config - The configuration object to initialize the worker.
   * @param config.baseUrl - The base URL for loading assets.
   * @param config.input - The input configuration for the worker.
   * @param config.mask - The mask configuration for the worker.
   * @param config.wasmUri - The URI for the WebAssembly module.
   * @param config.workerUri - The URI for the worker script.
   * @param config.assetUrlResolver - The function to resolve asset URLs.
   */
  sendInit(e) {
    this.worker || g$1.errorAndThrow("Worker is not initialized, cannot send init"), this.worker.postMessage({ type: "init", config: e });
  }
  /**
   * Send a generate message to the worker.
   *
   * @param input - The input image data as a Uint8ClampedArray.
   * @param timestamp - The timestamp of the input image.
   */
  sendGenerate(e, r) {
    this.worker || g$1.errorAndThrow("Worker is not initialized, cannot send generate"), this.worker.postMessage(
      {
        type: "generate",
        image: e,
        timestamp: r
      },
      [e.buffer]
    );
  }
}
const p = new l$2();

class d {
  constructor(e) {
    this.config = e, this.pending = /* @__PURE__ */ new Map(), this.newMask = !0, this.lastResult = {
      data: new Float32Array(
        this.config.mask.outputSize.height * this.config.mask.outputSize.width
      ),
      facesAndLandmarks: { data: new Float32Array(0), dims: [] },
      gesture: new Float32Array(0),
      motion: 0,
      timestamp: 0,
      warm: !1
    };
  }
  isLoaded() {
    return !!p.getWorker();
  }
  async getLadonOutput() {
    const e = {
      data: this.lastResult.data,
      facesAndLandmarks: this.lastResult.facesAndLandmarks,
      gesture: this.lastResult.gesture,
      motion: this.lastResult.motion,
      timestamp: this.lastResult.timestamp,
      warm: this.newMask
    };
    return this.newMask = !1, e;
  }
  /**
   * Returns a promise that resolves when the worker returns the mask for the given timestamp.
   * This is useful for synchronizing the processing of frames with their corresponding masks.
   * The promise will be resolved with the mask data when it is available.
   *
   * @param timestamp - The timestamp of the frame for which the mask is requested.
   * @returns A promise that resolves with the mask data when available.
   */
  async getLadonOutputForTimestamp(e) {
    return new Promise((n) => {
      this.pending.set(e, n);
    });
  }
  /**
   * Loads the worker and initializes it with the provided configuration.
   * The worker is responsible for processing the input image and generating the mask.
   * Prepares the worker configuration by resolving the final URLs and paths to send to the worker.
   *
   * @returns A promise that resolves when the worker is loaded and initialized.
   */
  async load() {
    const e = await p.prepareWorkerConfig(this.config);
    return new Promise((n) => {
      p.onInitialized(() => {
        n();
      }), p.onGenerated((t) => {
        const r = {
          data: t.mask,
          facesAndLandmarks: t.facesAndLandmarks,
          gesture: t.gesture,
          motion: t.motion,
          timestamp: t.timestamp,
          warm: !0
        };
        this.lastResult = r, this.newMask = !0;
        const a = this.pending.get(t.timestamp);
        a && (a(r), this.pending.delete(t.timestamp));
      }), p.onDropped((t) => {
        const r = this.pending.get(t);
        r && (r(this.lastResult), this.pending.delete(t));
      }), p.onLog((t, r) => {
        const a = `Message received from worker: ${t}`;
        switch (r) {
          case "warn":
            g$1.warn(a);
            break;
          case "error":
            g$1.error(a);
            break;
          default:
            g$1.log(a);
        }
      }), (async () => (await (p.getWorker() ?? p.initializeWorker(e)), p.sendInit({
        baseUrl: e.baseUrl,
        input: e.input,
        mask: e.mask,
        wasmUri: e.wasmUri,
        render: { type: "passthrough" }
      })))();
    });
  }
  /**
   * Sends the input image to the worker for processing.
   * The input image is expected to be a Uint8ClampedArray.
   * Claims ownership of the input tensor's buffer and forwards it to the worker.
   *
   * @param input - The input image as a Uint8ClampedArray.
   * @param timestamp - The timestamp of the input image.
   */
  async process(e, n) {
    p.sendGenerate(e, n);
  }
  /**
   * Destroys the worker and cleans up resources.
   * First it sends a message to the worker to destroy itself,
   * then it waits for the worker to respond with a 'destroyed' message.
   * After that, it terminates the worker and revokes the URL if it exists.
   *
   * @returns A promise that resolves when the worker is destroyed.
   */
  destroy() {
    return p.destroyWorker();
  }
}

class l$1 {
  constructor(t, i) {
    this.output_canvas = t, this.config = i, this.verts = new Float32Array([
      -1,
      -1,
      0,
      // Vertex 1
      -1,
      1,
      0,
      // Vertex 2
      1,
      1,
      0,
      // Vertex 3
      1,
      1,
      0,
      // Vertex 4
      1,
      -1,
      0,
      // Vertex 5
      -1,
      -1,
      0
      // Vertex 6
    ]), this.vertex_shader_src = `#version 300 es

in vec4 position;

void main() {
    gl_Position = position;
}
`, this.fragment_shader_header = `#version 300 es
precision highp float;

out vec4 outColor;

uniform sampler2D u_image;
uniform sampler2D u_mask;
uniform vec2 u_resolution;
`, this.shared_shader_functions = `
float get_mask(vec2 uv) {
    float maskValue = texture(u_mask, uv).r;
    return smoothstep(0.0, 1.0, maskValue);
}
`;
    const e = this.output_canvas.getContext("webgl2", { antialias: !0 });
    if (e)
      this.gl = e;
    else {
      const s = this.output_canvas.getContext("2d");
      s || g$1.errorAndThrow("Output canvas context must be webgl2 or 2d"), this.output_canvas_2d = s, this.intermediate_canvas = document.createElement("canvas"), this.intermediate_canvas.height = this.config.input.height, this.intermediate_canvas.width = this.config.input.width;
      const h = this.intermediate_canvas.getContext("webgl2", {
        antialias: !0
      });
      h || g$1.errorAndThrow("Error getting output context webgl"), this.gl = h;
    }
    this.initTexturesAndBuffers();
  }
  initTexturesAndBuffers() {
    this.image_texture = this.createTexture(
      this.gl.RGBA,
      this.config.input.width,
      this.config.input.height
    ), this.mask_texture = this.createTexture(
      this.gl.R8,
      this.config.mask.upscaleSize.width,
      this.config.mask.upscaleSize.height
    );
    const t = this.gl.createVertexArray();
    t || g$1.errorAndThrow("Error creating vertex array"), this.gl.bindVertexArray(t);
    const i = this.gl.createBuffer();
    i || g$1.errorAndThrow("Error creating buffer"), this.gl.bindBuffer(this.gl.ARRAY_BUFFER, i), this.gl.bufferData(this.gl.ARRAY_BUFFER, this.verts, this.gl.STATIC_DRAW), this.gl.vertexAttribPointer(0, 3, this.gl.FLOAT, !1, 0, 0), this.gl.enableVertexAttribArray(0), this.gl.viewport(0, 0, this.config.input.width, this.config.input.height);
  }
  createTexture(t, i, e, s = () => {
    this.gl.texImage2D(
      this.gl.TEXTURE_2D,
      0,
      t,
      i,
      e,
      0,
      t === this.gl.RGBA ? this.gl.RGBA : this.gl.RED,
      this.gl.UNSIGNED_BYTE,
      null
    );
  }) {
    const h = this.gl.createTexture();
    return h || g$1.errorAndThrow("Error creating texture"), this.gl.bindTexture(this.gl.TEXTURE_2D, h), this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_MIN_FILTER, this.gl.LINEAR), this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_MAG_FILTER, this.gl.LINEAR), this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_WRAP_S, this.gl.CLAMP_TO_EDGE), this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_WRAP_T, this.gl.CLAMP_TO_EDGE), s(), this.gl.bindTexture(this.gl.TEXTURE_2D, null), h;
  }
  getUVCalculation() {
    return this.config.render.horizontalMirror ? "vec2 st = vec2(1.0 - gl_FragCoord.x / u_resolution.x, 1.0 - gl_FragCoord.y / u_resolution.y);" : "vec2 st = vec2(gl_FragCoord.x / u_resolution.x, 1.0 - gl_FragCoord.y / u_resolution.y);";
  }
  setSharedUniforms(t) {
    const i = this.gl.getUniformLocation(t, "u_resolution");
    i || g$1.errorAndThrow('Error finding "u_resolution" uniform'), this.gl.uniform2f(i, this.config.input.width, this.config.input.height);
    const e = this.gl.getUniformLocation(t, "u_image");
    e || g$1.errorAndThrow('Error finding "u_image" uniform'), this.gl.activeTexture(this.gl.TEXTURE0), this.gl.bindTexture(this.gl.TEXTURE_2D, this.image_texture), this.gl.uniform1i(e, 0);
    const s = this.gl.getUniformLocation(t, "u_mask");
    s && (this.gl.activeTexture(this.gl.TEXTURE1), this.gl.bindTexture(this.gl.TEXTURE_2D, this.mask_texture), this.gl.uniform1i(s, 1));
  }
  uploadVideoStream(t) {
    this.gl.activeTexture(this.gl.TEXTURE0), this.gl.bindTexture(this.gl.TEXTURE_2D, this.image_texture), t instanceof HTMLVideoElement ? this.gl.texImage2D(
      this.gl.TEXTURE_2D,
      0,
      this.gl.RGBA,
      this.gl.RGBA,
      this.gl.UNSIGNED_BYTE,
      t
    ) : this.gl.texImage2D(
      this.gl.TEXTURE_2D,
      0,
      this.gl.RGBA,
      this.config.input.width,
      this.config.input.height,
      0,
      this.gl.RGBA,
      this.gl.UNSIGNED_BYTE,
      t
    );
  }
  uploadMask(t) {
    if (t != null && t.warm) {
      const i = L(
        t.data,
        this.config.mask.outputSize.width,
        this.config.mask.outputSize.height,
        this.config.mask.upscaleSize.width,
        this.config.mask.upscaleSize.height
      );
      this.gl.activeTexture(this.gl.TEXTURE1), this.gl.bindTexture(this.gl.TEXTURE_2D, this.mask_texture), this.gl.texImage2D(
        this.gl.TEXTURE_2D,
        0,
        this.gl.R8,
        this.config.mask.upscaleSize.width,
        this.config.mask.upscaleSize.height,
        0,
        this.gl.RED,
        this.gl.UNSIGNED_BYTE,
        i
      );
    }
  }
  postDraw() {
    this.output_canvas_2d && this.intermediate_canvas && this.output_canvas_2d.drawImage(
      this.intermediate_canvas,
      0,
      0,
      this.config.input.width,
      this.config.input.height
    );
  }
}

class c extends l$1 {
  constructor(e, i) {
    super(e, i), this.output_canvas = e, this.config = i, this.fragment_shader_common = `#version 300 es
precision highp float;

#define KERNEL_SIZE     ${this.config.render.kernelSize}
#define HORIZONTAL      vec2(1.0, 0.0)
#define VERTICAL        vec2(0.0, 1.0)

out vec4 outColor;

uniform sampler2D       u_image;
uniform sampler2D       u_mask;
uniform vec2            u_resolution;
uniform float           u_kernel[KERNEL_SIZE];

// Function to retrieve the mask value
float get_mask(vec2 st) {
    return texture(u_mask, st).r;
}

// Get the inverse of the masked area
float get_inv_mask(vec2 st) {
    return 1.0 - get_mask(st);
}

// Blurring function
vec3 blur(sampler2D src, vec2 uv, vec2 direction) {
    vec4 accum = vec4(0.0);
    vec2 step = direction / u_resolution;
    int offset = (KERNEL_SIZE - 1) / 2;

    float weightSum = 0.0;
    for (int i = 0; i < KERNEL_SIZE; i++) {
        vec2 sampleUV = uv + step * float(i - offset);
        vec4 sampleTex = texture(src, sampleUV);
        float weight = u_kernel[i];
        accum += sampleTex * weight;
        weightSum += weight;
    }

    weightSum = max(weightSum, 0.001); // Avoid division by zero
    return accum.rgb / weightSum;
}
`, this.fragment_shader_pass_1_src = this.fragment_shader_common + `
void main() {
    vec2 st = gl_FragCoord.xy / u_resolution;
    outColor = vec4(blur(u_image, st, HORIZONTAL), 1.0);
}
`, this.fragment_shader_pass_2_src = this.fragment_shader_common + `
uniform sampler2D   u_blur;

void main() {
    ${this.getUVCalculation()};

    vec3 blurred = blur(u_blur, st, VERTICAL);
    vec3 original = texture(u_image, st).rgb;
    float mask = get_inv_mask(st); // Use inverse mask to blur background, not user

    // Correct blending based on the mask
    vec3 color = mix(original, blurred, mask); // Ensure mask properly determines blur influence on background
    outColor = vec4(color, 1.0);
}
`, this.config.render.kernelSize % 2 === 0 && g$1.errorAndThrow("Kernel_size must be odd");
    {
      const r = C(
        this.gl,
        this.gl.VERTEX_SHADER,
        this.vertex_shader_src
      ), t = C(
        this.gl,
        this.gl.FRAGMENT_SHADER,
        this.fragment_shader_pass_1_src
      );
      this.program_1 = b(this.gl, r, t), this.gl.useProgram(this.program_1);
      const o = this.gl.createFramebuffer();
      o || g$1.errorAndThrow("Error creating framebuffer"), this.framebuffer = o;
      const n = this.gl.createTexture();
      n || g$1.errorAndThrow("Error creating output texture"), this.transfer_texture = n, this.gl.bindFramebuffer(this.gl.FRAMEBUFFER, this.framebuffer), this.gl.bindTexture(this.gl.TEXTURE_2D, this.transfer_texture), this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_MIN_FILTER, this.gl.LINEAR), this.gl.texParameteri(
        this.gl.TEXTURE_2D,
        this.gl.TEXTURE_WRAP_S,
        this.gl.CLAMP_TO_EDGE
      ), this.gl.texParameteri(
        this.gl.TEXTURE_2D,
        this.gl.TEXTURE_WRAP_T,
        this.gl.CLAMP_TO_EDGE
      ), this.gl.texImage2D(
        this.gl.TEXTURE_2D,
        0,
        this.gl.RGBA,
        this.config.input.width,
        this.config.input.height,
        0,
        this.gl.RGBA,
        this.gl.UNSIGNED_BYTE,
        null
      ), this.gl.framebufferTexture2D(
        this.gl.FRAMEBUFFER,
        this.gl.COLOR_ATTACHMENT0,
        this.gl.TEXTURE_2D,
        this.transfer_texture,
        0
      ), this.gl.bindFramebuffer(this.gl.FRAMEBUFFER, null), this.setSharedUniforms(this.program_1);
      const l = this.gl.getUniformLocation(this.program_1, "u_kernel");
      l || g$1.errorAndThrow('Error finding "u_kernel" uniform'), this.gl.uniform1fv(l, this.getBlurKernel());
    }
    {
      const r = C(
        this.gl,
        this.gl.VERTEX_SHADER,
        this.vertex_shader_src
      ), t = C(
        this.gl,
        this.gl.FRAGMENT_SHADER,
        this.fragment_shader_pass_2_src
      );
      this.program_2 = b(this.gl, r, t), this.gl.useProgram(this.program_2);
      const o = this.gl.getUniformLocation(this.program_2, "u_blur");
      o || g$1.errorAndThrow('Error finding "u_blur" uniform'), this.u_blur = o, this.gl.activeTexture(this.gl.TEXTURE2), this.gl.bindTexture(this.gl.TEXTURE_2D, this.transfer_texture), this.gl.uniform1i(this.u_blur, 2), this.setSharedUniforms(this.program_2);
      const n = this.gl.getUniformLocation(this.program_2, "u_kernel");
      n || g$1.errorAndThrow('Error finding "u_kernel" uniform'), this.gl.uniform1fv(n, this.getBlurKernel());
    }
  }
  async render(e, i) {
    this.uploadVideoStream(e), this.uploadMask(i), this.gl.useProgram(this.program_1), this.gl.bindFramebuffer(this.gl.FRAMEBUFFER, this.framebuffer), this.gl.drawArrays(this.gl.TRIANGLES, 0, this.verts.length / 3), this.gl.bindFramebuffer(this.gl.FRAMEBUFFER, null), this.gl.useProgram(this.program_2), this.gl.drawArrays(this.gl.TRIANGLES, 0, this.verts.length / 3), this.postDraw();
  }
  async load() {
  }
  destroy() {
  }
  getBlurKernel() {
    const e = [];
    let i = 0;
    for (let r = 0; r < this.config.render.kernelSize; r++) {
      let t = r - (this.config.render.kernelSize - 1) / 2;
      t = Math.exp(-(t ** 2) / this.config.render.sigma ** 2), e.push(t), i += t;
    }
    for (let r = 0; r < this.config.render.kernelSize; r++)
      e[r] = e[r] / i;
    return new Float32Array(e);
  }
}

class l extends l$1 {
  constructor(r, s) {
    super(r, s), this.output_canvas = r, this.config = s, this.fragment_shader = `#version 300 es
precision highp float;

out vec4 outColor;

uniform sampler2D       u_image;
uniform vec2            u_resolution;


void main() {
    ${this.getUVCalculation()}

    outColor = vec4(texture(u_image, st).rgb, 1.0);
}
`;
    const e = C(this.gl, this.gl.VERTEX_SHADER, this.vertex_shader_src), i = C(
      this.gl,
      this.gl.FRAGMENT_SHADER,
      this.fragment_shader
    );
    this.program = b(this.gl, e, i), this.gl.useProgram(this.program), this.setSharedUniforms(this.program);
  }
  async render(r) {
    this.uploadVideoStream(r), this.gl.useProgram(this.program), this.gl.drawArrays(this.gl.TRIANGLES, 0, this.verts.length / 3), this.postDraw();
  }
  async load() {
  }
  destroy() {
  }
}

class T extends l$1 {
  /**
   * Create a new replacement renderer instance.
   *
   * @param outputCanvas - The canvas element where the rendered output will be displayed.
   * @param config - Pipeline configuration containing render settings and background source.
   */
  constructor(t, e) {
    super(t, e), this.outputCanvas = t, this.config = e, this.lastVideoFrameTime = 0;
    const s = this.config.render.background;
    if (s instanceof HTMLVideoElement) {
      const n = this.getVideoFrameRate(s) || 30;
      this.videoFrameInterval = 1e3 / n;
    } else
      this.videoFrameInterval = 0;
    this.fragmentShader = this.buildFragmentShader();
    const i = C(this.gl, this.gl.VERTEX_SHADER, this.vertex_shader_src), o = C(this.gl, this.gl.FRAGMENT_SHADER, this.fragmentShader);
    this.program = b(this.gl, i, o), this.gl.useProgram(this.program), this.setSharedUniforms(this.program), this.setupBackgroundTexture();
  }
  /**
   * Build the fragment shader source code for the replacement effect.
   * Creates a shader that blends the background with the input video based on the mask.
   *
   * @returns The complete fragment shader source code.
   */
  buildFragmentShader() {
    return `${this.fragment_shader_header}
    
uniform sampler2D u_background;

${this.shared_shader_functions}

void main() {
    ${this.getUVCalculation()}

    vec3 background = texture(u_background, st).rgb;
    vec3 image = texture(u_image, st).rgb;
    float mask = get_mask(st);

    vec3 color = mix(background, image, mask);
    outColor = vec4(color, 1.0);
}`;
  }
  /**
   * Set up the WebGL texture for the background image or video.
   * Creates and configures the texture that will be used as the replacement background.
   */
  setupBackgroundTexture() {
    const e = this.config.render.background;
    this.backgroundTexture = this.createTexture(
      this.gl.RGBA,
      e.width,
      e.height,
      () => {
        this.gl.texImage2D(
          this.gl.TEXTURE_2D,
          0,
          this.gl.RGBA,
          this.gl.RGBA,
          this.gl.UNSIGNED_BYTE,
          e
        );
      }
    );
    const r = this.gl.getUniformLocation(this.program, "u_background");
    r || g$1.errorAndThrow('Error finding "u_background" uniform'), this.gl.activeTexture(this.gl.TEXTURE2), this.gl.bindTexture(this.gl.TEXTURE_2D, this.backgroundTexture), this.gl.uniform1i(r, 2);
  }
  /**
   * Render a frame with the replacement background effect.
   * Combines the input video stream with the background based on the provided mask.
   * Includes frame rate throttling for video backgrounds to prevent stuttering.
   * Canvas backgrounds are updated every frame to support dynamic content.
   *
   * @param input - The input frame data (video element or image data).
   * @param mask - Optional mask data for determining foreground/background blend.
   * @returns Promise that resolves when rendering is complete.
   */
  async render(t, e) {
    this.uploadVideoStream(t), this.uploadMask(e);
    const r = this.config.render, s = !r.static, i = r.background;
    s && (i instanceof HTMLVideoElement ? await this.updateVideoBackgroundTexture(i) : (this.gl.activeTexture(this.gl.TEXTURE2), this.gl.bindTexture(this.gl.TEXTURE_2D, this.backgroundTexture), this.gl.texImage2D(
      this.gl.TEXTURE_2D,
      0,
      this.gl.RGBA,
      this.gl.RGBA,
      this.gl.UNSIGNED_BYTE,
      i
    ))), this.gl.useProgram(this.program), this.gl.drawArrays(this.gl.TRIANGLES, 0, this.verts.length / 3), this.postDraw();
  }
  /**
   * Throttle video background updates and upload texture only when needed.
   * This prevents stuttering and unnecessary GPU uploads when inference is skipped.
   *
   * @param backgroundSource - The video element to potentially upload.
   * @returns Promise that resolves after appropriate throttling and upload.
   */
  async updateVideoBackgroundTexture(t) {
    if (this.videoFrameInterval <= 0) return;
    const e = performance.now();
    e - this.lastVideoFrameTime >= this.videoFrameInterval && (this.gl.activeTexture(this.gl.TEXTURE2), this.gl.bindTexture(this.gl.TEXTURE_2D, this.backgroundTexture), this.gl.texImage2D(
      this.gl.TEXTURE_2D,
      0,
      this.gl.RGBA,
      this.gl.RGBA,
      this.gl.UNSIGNED_BYTE,
      t
    ), this.lastVideoFrameTime = e);
  }
  /**
   * Attempt to determine the frame rate of a video element.
   * Tries to extract frame rate from video track settings if available.
   *
   * @param video - The video element to analyze.
   * @returns The video's frame rate in frames per second, or undefined if not determinable.
   */
  getVideoFrameRate(t) {
    if (t.srcObject instanceof MediaStream) {
      const e = t.srcObject.getVideoTracks()[0];
      if (e != null && e.getSettings)
        return e.getSettings().frameRate;
    }
  }
  /**
   * Load and initialize the renderer.
   * Currently a no-op for this renderer implementation.
   *
   * @returns Promise that resolves when loading is complete.
   */
  async load() {
  }
  /**
   * Clean up resources and destroy the renderer.
   * Currently a no-op for this renderer implementation.
   */
  destroy() {
  }
}

class D {
  /**
   * Create a new pipeline instance.
   *
   * @param inputCanvas - Canvas element for input frame processing.
   * @param outputCanvas - Canvas element where the final rendered output will be displayed.
   * @param config - Pipeline configuration containing processing and rendering settings.
   * @param inputVideoElement - Video element serving as the input source.
   * @throws Error if the input canvas 2D context cannot be obtained.
   */
  constructor(e, t, r, a) {
    this.inputCanvas = e, this.outputCanvas = t, this.config = r, this.inputVideoElement = a, this.loaded = !1, this.isProcessingFrame = !1, this.pendingFramePromise = null;
    const n = this.inputCanvas.getContext("2d", {
      alpha: !1,
      willReadFrequently: !0
    });
    n || g$1.errorAndThrow("Error getting input context 2d"), this.inputCanvasContext = n;
  }
  /**
   * Process a single frame and return the inference result.
   * This method handles frame data, performs inference if needed, and renders the output.
   * Includes frame processing serialization to prevent overlapping operations.
   *
   * @param timestamp - The timestamp of the frame in milliseconds.
   * @param performInference - Whether to perform mask inference or reuse the last result.
   * @param frameData - Optional frame data to process instead of capturing from input.
   * @returns Promise resolving to the frame processing result with timing statistics.
   * @throws Error if the pipeline (generator or renderer) is not loaded.
   */
  async onFrame(e, t = !0, r) {
    this.isProcessingFrame && this.pendingFramePromise && await this.pendingFramePromise, this.isProcessingFrame = !0;
    try {
      return this.pendingFramePromise = this.processFrameInternal(
        e,
        t,
        r
      ), await this.pendingFramePromise;
    } finally {
      this.isProcessingFrame = !1, this.pendingFramePromise = null;
    }
  }
  /**
   * Internal frame processing implementation.
   * Handles the actual frame processing logic including timing measurements.
   *
   * @param timestamp - The timestamp of the frame in milliseconds.
   * @param performInference - Whether to perform mask inference or reuse the last result.
   * @param frameData - Optional frame data to process instead of capturing from input.
   * @returns Promise resolving to the frame processing result with timing statistics.
   * @throws Error if the pipeline (generator or renderer) is not loaded.
   */
  async processFrameInternal(e, t = !0, r) {
    const a = performance.now();
    (!this.generator || !this.renderer) && g$1.errorAndThrow("Pipeline is not loaded.");
    let n = 0, o = 0, i;
    if (t || !this.lastMaskResult) {
      const g = performance.now();
      r ?? (r = this.getInputFrameData()), i = this.createFrameDataCopy(r), n = performance.now() - g;
      const c = performance.now(), p = r, f = this.getMaskImageData(p);
      await this.generator.process(f, e), this.lastMaskResult = await this.generator.getLadonOutputForTimestamp(e), o = performance.now() - c;
    } else
      i = this.inputVideoElement;
    const l = performance.now();
    await this.renderer.render(i, this.lastMaskResult);
    const m = performance.now() - l, h = performance.now(), u = h - a, d = this.createInferenceResult(e, t);
    return d.stats = {
      totalFrameTime: u,
      modelInferenceTime: o,
      preprocessingTime: n,
      postprocessingTime: m,
      frameStartTime: a,
      frameEndTime: h,
      inferenceSkipped: !t
    }, d;
  }
  /**
   * Get the raw image data from the input canvas context.
   *
   * @returns The frame data as a Uint8ClampedArray.
   */
  getInputFrameData() {
    return this.inputCanvasContext.getImageData(
      0,
      0,
      this.config.input.width,
      this.config.input.height
    ).data;
  }
  /**
   * Create a copy of frame data to prevent modifications during async operations.
   */
  createFrameDataCopy(e) {
    var t;
    return e instanceof Uint8ClampedArray ? (((t = this.frameDataBuffer) == null ? void 0 : t.length) !== e.length && (this.frameDataBuffer = new Uint8ClampedArray(e.length)), this.frameDataBuffer.set(e), this.frameDataBuffer) : e;
  }
  /**
   * Resize the image data if the model expects a different input size.
   * Handles scaling between input dimensions and model requirements.
   *
   * @param rawImageData - The raw image data to potentially resize.
   * @returns The processed mask image data, resized if necessary.
   */
  getMaskImageData(e) {
    const t = this.config.mask.inputSize, r = this.config.input;
    return t.width === r.width && t.height === r.height ? e : d$2(
      e,
      r.width,
      r.height,
      t.width,
      t.height
    );
  }
  /**
   * Create the inference result object with appropriate data.
   * Handles both successful inference results and fallback cases.
   *
   * @param timestamp - The timestamp of the frame in milliseconds.
   * @param performedInference - Whether inference was actually performed for this frame.
   * @returns The inference result object containing mask data and metadata.
   */
  createInferenceResult(e, t) {
    return this.lastMaskResult ? {
      facesAndLandmarks: this.lastMaskResult.facesAndLandmarks,
      gesture: this.lastMaskResult.gesture,
      motion: this.lastMaskResult.motion,
      skipped: !t,
      timestamp: e
      // Stats will be added by the caller if needed
    } : {
      facesAndLandmarks: { data: new Float32Array(0), dims: [] },
      gesture: new Float32Array(0),
      motion: 0,
      skipped: !0,
      timestamp: e
      // Stats will be added by the caller if needed
    };
  }
  /**
   * Check if the pipeline is fully loaded and ready for processing.
   *
   * @returns True if the pipeline is loaded and ready, false otherwise.
   */
  isLoaded() {
    return this.loaded;
  }
  /**
   * Load and initialize the pipeline components.
   * Creates the appropriate renderer and generator based on configuration.
   *
   * @returns Promise that resolves when all components are loaded and ready.
   * @throws Error if an unsupported renderer type is specified in the configuration.
   */
  async load() {
    switch (g$1.log("Creating renderer"), this.config.render.type) {
      case "blur":
        this.renderer = new c(this.outputCanvas, this.config);
        break;
      case "passthrough":
        this.renderer = new l(this.outputCanvas, this.config);
        break;
      case "replacement":
        this.renderer = new T(this.outputCanvas, this.config);
        break;
      default:
        g$1.errorAndThrow("Renderer not implemented.");
    }
    g$1.log("Loading renderer"), await this.renderer.load(), g$1.log("Creating generator"), this.generator = new d(this.config), g$1.log("Loading generator"), await this.generator.load(), this.loaded = !0;
  }
  /**
   * Reset the pipeline state by clearing cached mask results.
   * Useful when switching between different processing modes or sources.
   */
  reset() {
    this.lastMaskResult = void 0;
  }
  /**
   * Destroy the pipeline and clean up all resources.
   * Properly disposes of the renderer and generator to prevent memory leaks.
   *
   * @returns Promise that resolves when cleanup is complete.
   */
  async destroy() {
    var e, t;
    this.reset(), (e = this.renderer) == null || e.destroy(), await ((t = this.generator) == null ? void 0 : t.destroy()), this.loaded = !1;
  }
}

const e = async (r) => {
  await p.preloadWorker(r);
};

var logger$1 = {exports: {}};

/*!
 * js-logger - http://github.com/jonnyreeves/js-logger
 * Jonny Reeves, http://jonnyreeves.co.uk/
 * js-logger may be freely distributed under the MIT license.
 */

(function (module) {
(function (global) {

	// Top level module for the global, static logger instance.
	var Logger = { };

	// For those that are at home that are keeping score.
	Logger.VERSION = "1.6.1";

	// Function which handles all incoming log messages.
	var logHandler;

	// Map of ContextualLogger instances by name; used by Logger.get() to return the same named instance.
	var contextualLoggersByNameMap = {};

	// Polyfill for ES5's Function.bind.
	var bind = function(scope, func) {
		return function() {
			return func.apply(scope, arguments);
		};
	};

	// Super exciting object merger-matron 9000 adding another 100 bytes to your download.
	var merge = function () {
		var args = arguments, target = args[0], key, i;
		for (i = 1; i < args.length; i++) {
			for (key in args[i]) {
				if (!(key in target) && args[i].hasOwnProperty(key)) {
					target[key] = args[i][key];
				}
			}
		}
		return target;
	};

	// Helper to define a logging level object; helps with optimisation.
	var defineLogLevel = function(value, name) {
		return { value: value, name: name };
	};

	// Predefined logging levels.
	Logger.TRACE = defineLogLevel(1, 'TRACE');
	Logger.DEBUG = defineLogLevel(2, 'DEBUG');
	Logger.INFO = defineLogLevel(3, 'INFO');
	Logger.TIME = defineLogLevel(4, 'TIME');
	Logger.WARN = defineLogLevel(5, 'WARN');
	Logger.ERROR = defineLogLevel(8, 'ERROR');
	Logger.OFF = defineLogLevel(99, 'OFF');

	// Inner class which performs the bulk of the work; ContextualLogger instances can be configured independently
	// of each other.
	var ContextualLogger = function(defaultContext) {
		this.context = defaultContext;
		this.setLevel(defaultContext.filterLevel);
		this.log = this.info;  // Convenience alias.
	};

	ContextualLogger.prototype = {
		// Changes the current logging level for the logging instance.
		setLevel: function (newLevel) {
			// Ensure the supplied Level object looks valid.
			if (newLevel && "value" in newLevel) {
				this.context.filterLevel = newLevel;
			}
		},
		
		// Gets the current logging level for the logging instance
		getLevel: function () {
			return this.context.filterLevel;
		},

		// Is the logger configured to output messages at the supplied level?
		enabledFor: function (lvl) {
			var filterLevel = this.context.filterLevel;
			return lvl.value >= filterLevel.value;
		},

		trace: function () {
			this.invoke(Logger.TRACE, arguments);
		},

		debug: function () {
			this.invoke(Logger.DEBUG, arguments);
		},

		info: function () {
			this.invoke(Logger.INFO, arguments);
		},

		warn: function () {
			this.invoke(Logger.WARN, arguments);
		},

		error: function () {
			this.invoke(Logger.ERROR, arguments);
		},

		time: function (label) {
			if (typeof label === 'string' && label.length > 0) {
				this.invoke(Logger.TIME, [ label, 'start' ]);
			}
		},

		timeEnd: function (label) {
			if (typeof label === 'string' && label.length > 0) {
				this.invoke(Logger.TIME, [ label, 'end' ]);
			}
		},

		// Invokes the logger callback if it's not being filtered.
		invoke: function (level, msgArgs) {
			if (logHandler && this.enabledFor(level)) {
				logHandler(msgArgs, merge({ level: level }, this.context));
			}
		}
	};

	// Protected instance which all calls to the to level `Logger` module will be routed through.
	var globalLogger = new ContextualLogger({ filterLevel: Logger.OFF });

	// Configure the global Logger instance.
	(function() {
		// Shortcut for optimisers.
		var L = Logger;

		L.enabledFor = bind(globalLogger, globalLogger.enabledFor);
		L.trace = bind(globalLogger, globalLogger.trace);
		L.debug = bind(globalLogger, globalLogger.debug);
		L.time = bind(globalLogger, globalLogger.time);
		L.timeEnd = bind(globalLogger, globalLogger.timeEnd);
		L.info = bind(globalLogger, globalLogger.info);
		L.warn = bind(globalLogger, globalLogger.warn);
		L.error = bind(globalLogger, globalLogger.error);

		// Don't forget the convenience alias!
		L.log = L.info;
	}());

	// Set the global logging handler.  The supplied function should expect two arguments, the first being an arguments
	// object with the supplied log messages and the second being a context object which contains a hash of stateful
	// parameters which the logging function can consume.
	Logger.setHandler = function (func) {
		logHandler = func;
	};

	// Sets the global logging filter level which applies to *all* previously registered, and future Logger instances.
	// (note that named loggers (retrieved via `Logger.get`) can be configured independently if required).
	Logger.setLevel = function(level) {
		// Set the globalLogger's level.
		globalLogger.setLevel(level);

		// Apply this level to all registered contextual loggers.
		for (var key in contextualLoggersByNameMap) {
			if (contextualLoggersByNameMap.hasOwnProperty(key)) {
				contextualLoggersByNameMap[key].setLevel(level);
			}
		}
	};

	// Gets the global logging filter level
	Logger.getLevel = function() {
		return globalLogger.getLevel();
	};

	// Retrieve a ContextualLogger instance.  Note that named loggers automatically inherit the global logger's level,
	// default context and log handler.
	Logger.get = function (name) {
		// All logger instances are cached so they can be configured ahead of use.
		return contextualLoggersByNameMap[name] ||
			(contextualLoggersByNameMap[name] = new ContextualLogger(merge({ name: name }, globalLogger.context)));
	};

	// CreateDefaultHandler returns a handler function which can be passed to `Logger.setHandler()` which will
	// write to the window's console object (if present); the optional options object can be used to customise the
	// formatter used to format each log message.
	Logger.createDefaultHandler = function (options) {
		options = options || {};

		options.formatter = options.formatter || function defaultMessageFormatter(messages, context) {
			// Prepend the logger's name to the log message for easy identification.
			if (context.name) {
				messages.unshift("[" + context.name + "]");
			}
		};

		// Map of timestamps by timer labels used to track `#time` and `#timeEnd()` invocations in environments
		// that don't offer a native console method.
		var timerStartTimeByLabelMap = {};

		// Support for IE8+ (and other, slightly more sane environments)
		var invokeConsoleMethod = function (hdlr, messages) {
			Function.prototype.apply.call(hdlr, console, messages);
		};

		// Check for the presence of a logger.
		if (typeof console === "undefined") {
			return function () { /* no console */ };
		}

		return function(messages, context) {
			// Convert arguments object to Array.
			messages = Array.prototype.slice.call(messages);

			var hdlr = console.log;
			var timerLabel;

			if (context.level === Logger.TIME) {
				timerLabel = (context.name ? '[' + context.name + '] ' : '') + messages[0];

				if (messages[1] === 'start') {
					if (console.time) {
						console.time(timerLabel);
					}
					else {
						timerStartTimeByLabelMap[timerLabel] = new Date().getTime();
					}
				}
				else {
					if (console.timeEnd) {
						console.timeEnd(timerLabel);
					}
					else {
						invokeConsoleMethod(hdlr, [ timerLabel + ': ' +
							(new Date().getTime() - timerStartTimeByLabelMap[timerLabel]) + 'ms' ]);
					}
				}
			}
			else {
				// Delegate through to custom warn/error loggers if present on the console.
				if (context.level === Logger.WARN && console.warn) {
					hdlr = console.warn;
				} else if (context.level === Logger.ERROR && console.error) {
					hdlr = console.error;
				} else if (context.level === Logger.INFO && console.info) {
					hdlr = console.info;
				} else if (context.level === Logger.DEBUG && console.debug) {
					hdlr = console.debug;
				} else if (context.level === Logger.TRACE && console.trace) {
					hdlr = console.trace;
				}

				options.formatter(messages, context);
				invokeConsoleMethod(hdlr, messages);
			}
		};
	};

	// Configure and example a Default implementation which writes to the `window.console` (if present).  The
	// `options` hash can be used to configure the default logLevel and provide a custom message formatter.
	Logger.useDefaults = function(options) {
		Logger.setLevel(options && options.defaultLevel || Logger.DEBUG);
		Logger.setHandler(Logger.createDefaultHandler(options));
	};

	// Createa an alias to useDefaults to avoid reaking a react-hooks rule.
	Logger.setDefaults = Logger.useDefaults;

	// Export to popular environments boilerplate.
	if (module.exports) {
		module.exports = Logger;
	}
	else {
		Logger._prevLogger = global.Logger;

		Logger.noConflict = function () {
			global.Logger = Logger._prevLogger;
			return Logger;
		};

		global.Logger = Logger;
	}
}(commonjsGlobal));
}(logger$1));

var Logger = logger$1.exports;

const logger = Logger.get('web-media-effects');
function setLogLevel(level) {
    logger.setLevel(level);
    g$1.setLevel(level);
}
function setLogHandler(logHandler) {
    Logger.setHandler(logHandler);
    a.setHandler(logHandler);
}
const consoleLogHandler = Logger.createDefaultHandler({
    formatter: (msg, context) => {
        msg.unshift(context.name);
    },
});
setLogHandler(consoleLogHandler);

var EffectEnv;
(function (EffectEnv) {
    EffectEnv["Prod"] = "prod";
    EffectEnv["Int"] = "int";
})(EffectEnv || (EffectEnv = {}));
var EffectState;
(function (EffectState) {
    EffectState["Idle"] = "idle";
    EffectState["Init"] = "init";
    EffectState["Disabled"] = "disabled";
    EffectState["Enabled"] = "enabled";
    EffectState["Disposed"] = "disposed";
    EffectState["Error"] = "error";
})(EffectState || (EffectState = {}));
var EffectEvent;
(function (EffectEvent) {
    EffectEvent["TrackUpdated"] = "track-updated";
    EffectEvent["Enabled"] = "enabled";
    EffectEvent["Disabled"] = "disabled";
    EffectEvent["Disposed"] = "disposed";
    EffectEvent["ModelChanged"] = "model-changed";
})(EffectEvent || (EffectEvent = {}));
class BaseEffect extends EventEmitter {
    constructor() {
        super();
        this.state = EffectState.Idle;
        this.id = v4();
        this.isEnabled = false;
        const constructor = this.constructor;
        if (constructor.kind === undefined) {
            throw new Error("base effect: derived class must define a static 'kind' property.");
        }
        this.kind = constructor.kind;
    }
    get underlyingStream() {
        return this.getUnderlyingStream();
    }
    get inputTrack() {
        var _a;
        return (_a = this.inputStream) === null || _a === void 0 ? void 0 : _a.getTracks()[0];
    }
    getOutputTrack() {
        return this.outputTrack;
    }
    getOutputStream() {
        return this.outputStream;
    }
    getUnderlyingStream() {
        logger.warn('getUnderlyingStream() is deprecated and will be removed in a future major release. Please use getOutputStream() instead.');
        return this.getOutputStream();
    }
    preloadAssets() {
        return __awaiter(this, void 0, void 0, function* () { });
    }
    load(input) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!input) {
                throw new Error('base effect: track is a required parameter');
            }
            if (input instanceof MediaStream) {
                return this.loadMediaStream(input);
            }
            if (input instanceof MediaStreamTrack) {
                return this.loadMediaStreamTrack(input);
            }
            this.isEnabled = false;
            throw new Error('base effect: invalid argument type');
        });
    }
    loadMediaStream(input) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.loadMedia(input.getTracks()[0], input);
            if (!this.outputStream) {
                throw new Error('base effect: load failed to create output stream');
            }
            return this.outputStream;
        });
    }
    loadMediaStreamTrack(input) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.loadMedia(input);
            if (!this.outputTrack) {
                throw new Error('base effect: load failed to create output track');
            }
            return this.outputTrack;
        });
    }
    loadMedia(inputTrack, inputStream) {
        return __awaiter(this, void 0, void 0, function* () {
            this.outputTrack = inputTrack;
            if (!inputStream) {
                this.inputStream = new MediaStream([inputTrack]);
                return;
            }
            this.inputStream = inputStream;
            this.outputStream = new MediaStream();
            this.outputStream.addTrack(this.outputTrack);
        });
    }
    replaceInputTrack(track) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!track) {
                throw new Error('base effect: track is a required parameter');
            }
            if (this.inputStream && this.inputTrack) {
                this.inputStream.removeTrack(this.inputTrack);
            }
            if (this.inputStream) {
                this.inputStream.addTrack(track);
            }
        });
    }
    enable() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.effectTrack) {
                throw new Error('base effect: effect track is undefined');
            }
            this.isEnabled = true;
            this.outputTrack = this.effectTrack;
            this.emit(EffectEvent.Enabled);
            this.emit(EffectEvent.TrackUpdated, this.effectTrack);
        });
    }
    disable() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.inputTrack) {
                throw new Error('base effect: input track is undefined');
            }
            this.isEnabled = false;
            this.outputTrack = this.inputTrack;
            this.emit(EffectEvent.Disabled);
            this.emit(EffectEvent.TrackUpdated, this.inputTrack);
        });
    }
    dispose() {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            (_a = this.effectTrack) === null || _a === void 0 ? void 0 : _a.stop();
            delete this.outputTrack;
            delete this.effectTrack;
            this.emit(EffectEvent.Disposed);
        });
    }
    setEnabled(enable) {
        return __awaiter(this, void 0, void 0, function* () {
            if (enable) {
                yield this.enable();
            }
            else {
                yield this.disable();
            }
        });
    }
}
const createEffect = (EffectClass, options = {}, preloadAssets = false) => __awaiter(void 0, void 0, void 0, function* () {
    const effectInstance = new EffectClass(options);
    if (preloadAssets) {
        yield effectInstance.preloadAssets().catch((e) => {
            logger.error('base effect: effect preload failed:', e);
            throw e;
        });
    }
    return effectInstance;
});

class BaseCameraEffect extends BaseEffect {
}

class BaseMicrophoneEffect extends BaseEffect {
    constructor(audioCtx) {
        super();
        this.audioContext = audioCtx || new AudioContext();
    }
    updateAudioContext(audioContext) {
        this.audioContext = audioContext;
    }
}

const defaultOptions$4 = {
    maxGain: 2,
    rampTime: 1,
};
class GainEffect extends BaseMicrophoneEffect {
    constructor(options) {
        super(options.audioContext);
        this.gainValue = 1;
        this.options = Object.assign(Object.assign({}, defaultOptions$4), options);
        logger.log('GainEffect init', { options: this.options });
    }
    loadMediaStream(input) {
        const _super = Object.create(null, {
            loadMediaStream: { get: () => super.loadMediaStream }
        });
        return __awaiter(this, void 0, void 0, function* () {
            yield _super.loadMediaStream.call(this, input);
            this.outputStream = yield this.createOutputStream();
            if (!this.outputStream) {
                throw new Error('gain effect: failed to create output stream');
            }
            this.outputStream.getAudioTracks()[0].enabled = input.getTracks()[0].enabled;
            return this.outputStream;
        });
    }
    loadMediaStreamTrack(input) {
        const _super = Object.create(null, {
            loadMediaStreamTrack: { get: () => super.loadMediaStreamTrack }
        });
        return __awaiter(this, void 0, void 0, function* () {
            yield _super.loadMediaStreamTrack.call(this, input);
            [this.outputTrack] = (yield this.createOutputStream()).getTracks();
            if (!this.outputTrack) {
                throw new Error('gain effect: failed to create output track');
            }
            return this.outputTrack;
        });
    }
    createOutputStream() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.inputStream) {
                throw new Error('gain effect: failed to create input stream');
            }
            const source = this.audioContext.createMediaStreamSource(this.inputStream);
            this.gainNode = new GainNode(this.audioContext);
            source.connect(this.gainNode);
            const mediaStreamDestination = this.audioContext.createMediaStreamDestination();
            this.gainNode.connect(mediaStreamDestination);
            [this.effectTrack] = mediaStreamDestination.stream.getTracks();
            return mediaStreamDestination.stream;
        });
    }
    setGainValue(value) {
        if (value > this.options.maxGain) {
            value = this.options.maxGain;
        }
        this.gainValue = value;
        this.gainNode.gain.linearRampToValueAtTime(value, this.audioContext.currentTime + this.options.rampTime);
    }
    enable() {
        const _super = Object.create(null, {
            enable: { get: () => super.enable }
        });
        return __awaiter(this, void 0, void 0, function* () {
            this.gainNode.gain.linearRampToValueAtTime(this.gainValue, this.audioContext.currentTime + this.options.rampTime);
            yield _super.enable.call(this);
        });
    }
    disable() {
        const _super = Object.create(null, {
            disable: { get: () => super.disable }
        });
        return __awaiter(this, void 0, void 0, function* () {
            this.gainNode.gain.linearRampToValueAtTime(1, this.audioContext.currentTime + this.options.rampTime);
            yield _super.disable.call(this);
        });
    }
    dispose() {
        const _super = Object.create(null, {
            dispose: { get: () => super.dispose }
        });
        return __awaiter(this, void 0, void 0, function* () {
            this.gainNode.disconnect();
            yield _super.dispose.call(this);
        });
    }
}
GainEffect.kind = 'gain-effect';

var NoiseReductionMode;
(function (NoiseReductionMode) {
    NoiseReductionMode["Legacy"] = "LEGACY";
    NoiseReductionMode["Worklet"] = "WORKLET";
})(NoiseReductionMode || (NoiseReductionMode = {}));
const NoiseReductionModels = {
    BNR: 'bnr',
    ST: 'st',
};
const DEFAULT_NOISE_REDUCTION_MODEL = NoiseReductionModels.BNR;

const defaultOptions$3 = Object.freeze({
    mode: NoiseReductionMode.Worklet,
    model: DEFAULT_NOISE_REDUCTION_MODEL,
    avoidSimd: false,
    authToken: '',
});

const appendScript = (url) => __awaiter(void 0, void 0, void 0, function* () {
    return new Promise((resolve, reject) => {
        var _a;
        const fs = document.getElementsByTagName('script')[0];
        const s = document.createElement('script');
        s.type = 'text/javascript';
        s.async = true;
        s.src = url;
        s.onload = resolve;
        s.onerror = reject;
        (_a = fs.parentNode) === null || _a === void 0 ? void 0 : _a.insertBefore(s, fs);
    });
});

function stripQueryParams(url) {
    try {
        const u = new URL(url);
        u.search = '';
        return u.toString();
    }
    catch (_a) {
        return url;
    }
}
function fetchAndCacheBlobUrl(url) {
    return __awaiter(this, void 0, void 0, function* () {
        let response;
        if (url.startsWith('http://') || url.startsWith('https://')) {
            const cacheKey = stripQueryParams(url);
            const cache = yield caches.open('wfx-cache');
            response = yield cache.match(cacheKey);
            if (!response) {
                response = yield fetch(url);
                if (response.ok) {
                    const scriptText = yield response.clone().text();
                    const contentType = response.headers.get('Content-Type');
                    if (!contentType) {
                        throw new Error(`Unexpected MIME type: ${contentType} for ${url}`);
                    }
                    const blob = new Blob([scriptText], { type: contentType });
                    const cachedResponse = new Response(blob, {
                        headers: {
                            'Content-Type': contentType,
                            'Content-Length': blob.size.toString(),
                        },
                    });
                    yield cache.put(cacheKey, cachedResponse);
                }
                else {
                    throw new Error(`Failed to fetch script at ${url}`);
                }
                response = yield cache.match(cacheKey);
            }
        }
        else {
            response = yield fetch(url);
        }
        if (!response) {
            throw new Error(`Failed to fetch or cache script at ${url}`);
        }
        const contentType = response.headers.get('Content-Type');
        if (!contentType) {
            throw new Error(`Unexpected MIME type: ${contentType} for ${url}`);
        }
        const scriptText = yield response.text();
        const blob = new Blob([scriptText], { type: contentType });
        return URL.createObjectURL(blob);
    });
}
const fetchUrls = (paths, token, env) => {
    const url = (env === EffectEnv.Int
        ? "https://models-auth.us-east-2.int.infra.intelligence.webex.com/generate"
        : "https://models-auth.intelligence.webex.com/generate");
    const body = { items: Array.isArray(paths) ? paths : [paths] };
    const options = {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
    };
    return fetch(url, options).then((resp) => __awaiter(void 0, void 0, void 0, function* () {
        if (!resp.ok) {
            let bodyText = '';
            try {
                bodyText = yield resp.text();
            }
            catch (err) {
                bodyText = '[unable to read response body]';
            }
            throw new Error(`fetchUrls failed: ${resp.status} ${resp.statusText} - ${bodyText}`);
        }
        return resp.json();
    }));
};
const fetchSignedUrl = (path, token, env) => __awaiter(void 0, void 0, void 0, function* () {
    return fetchUrls(path, token, env)
        .then((resp) => { var _a, _b; return (_b = (_a = resp.items) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.url; })
        .catch((e) => {
        const errorType = (e === null || e === void 0 ? void 0 : e.name) || typeof e;
        const errorCode = (e === null || e === void 0 ? void 0 : e.code) || '';
        const errorStatus = (e === null || e === void 0 ? void 0 : e.status) || '';
        const errorMessage = (e === null || e === void 0 ? void 0 : e.message) || String(e);
        const details = [errorType, errorCode, errorStatus, errorMessage].filter(Boolean).join(' - ');
        throw new Error(`Fetching signed url failed. Please check the auth token and try again. (${details})`);
    });
});

var FutureState;
(function (FutureState) {
    FutureState["Pending"] = "pending";
    FutureState["Resolved"] = "resolved";
    FutureState["Rejected"] = "rejected";
})(FutureState || (FutureState = {}));
class Future {
    constructor(promise) {
        this.state = FutureState.Pending;
        this.promise =
            promise !== null && promise !== void 0 ? promise : new Promise((resolve, reject) => {
                this.resolve = (arg) => {
                    this.state = FutureState.Resolved;
                    resolve(arg);
                };
                this.reject = (e) => {
                    this.state = FutureState.Rejected;
                    reject(e);
                };
            });
    }
}

const hasSimdSupport = () => __awaiter(void 0, void 0, void 0, function* () {
    return WebAssembly.validate(new Uint8Array([
        0, 97, 115, 109, 1, 0, 0, 0, 1, 5, 1, 96, 0, 1, 123, 3, 2, 1, 0, 10, 10, 1, 8, 0, 65, 0, 253,
        15, 253, 98, 11,
    ]));
});

const omitKey = (key, obj) => Object.fromEntries(Object.entries(obj).filter((i) => i[0] !== key));

class NoiseReductionEffect extends BaseMicrophoneEffect {
    constructor(options) {
        super(options === null || options === void 0 ? void 0 : options.audioContext);
        this.isReady = false;
        this.currentModel = DEFAULT_NOISE_REDUCTION_MODEL;
        this.disableFuture = new Future();
        this.enableFuture = new Future();
        this.options = Object.assign(Object.assign({}, defaultOptions$3), options);
        this.currentModel = this.options.model || DEFAULT_NOISE_REDUCTION_MODEL;
        logger.log('noise reduction: init effect', omitKey('authToken', this.options));
    }
    get isLoaded() {
        return !!this.loadFuture;
    }
    get model() {
        return this.currentModel;
    }
    fetchUrl(url) {
        return __awaiter(this, void 0, void 0, function* () {
            let validatedUrl = url;
            if (!this.options.authToken) {
                throw new Error('noise reduction: auth token is required');
            }
            if (!this.options.avoidSimd) {
                let simdSupported;
                try {
                    simdSupported = yield NoiseReductionEffect.supportsSimd();
                }
                catch (e) {
                    const isErrorLike = (err) => typeof err === 'object' && err !== null;
                    const message = isErrorLike(e) && typeof e.message === 'string' ? e.message : String(e);
                    const name = isErrorLike(e) && typeof e.name === 'string' ? e.name : '';
                    const stack = isErrorLike(e) && typeof e.stack === 'string' ? e.stack : '';
                    const details = [
                        name && `name: ${name}`,
                        message && `message: ${message}`,
                        stack && `stack: ${stack}`,
                    ]
                        .filter(Boolean)
                        .join(' | ');
                    throw new Error(`noise reduction: supportsSimd() check failed. Details: ${details}`);
                }
                if (simdSupported) {
                    validatedUrl = url.replace('.js', '.simd.js');
                    logger.log('noise reduction: SIMD enabled');
                }
            }
            return fetchSignedUrl(validatedUrl, this.options.authToken, this.options.env).catch((e) => {
                throw new Error(`noise reduction: ${e.message}`);
            });
        });
    }
    loadMediaStream(input) {
        const _super = Object.create(null, {
            loadMedia: { get: () => super.loadMedia }
        });
        return __awaiter(this, void 0, void 0, function* () {
            logger.log('noise reduction: load stream', input);
            const [audioTrack] = input.getAudioTracks();
            if (!audioTrack) {
                throw new Error('noise reduction: load stream failed, no audio track found');
            }
            const trackSettings = audioTrack.getSettings();
            this.logInputDiagnostics(audioTrack);
            yield _super.loadMedia.call(this, audioTrack, input);
            this.outputStream = yield this.createOutputStream();
            if (!this.outputStream) {
                throw new Error('noise reduction: failed to create output stream');
            }
            this.outputStream.getAudioTracks()[0].enabled = audioTrack.enabled;
            const [outputTrack] = this.outputStream.getAudioTracks();
            this.logOutputResampleCheck(trackSettings, outputTrack);
            if (this.currentModel === NoiseReductionModels.ST) {
                yield this.handleAgcForModel(NoiseReductionModels.ST);
            }
            return this.outputStream;
        });
    }
    loadMediaStreamTrack(input) {
        const _super = Object.create(null, {
            loadMediaStreamTrack: { get: () => super.loadMediaStreamTrack }
        });
        return __awaiter(this, void 0, void 0, function* () {
            const trackSettings = input.getSettings();
            this.logInputDiagnostics(input);
            yield _super.loadMediaStreamTrack.call(this, input);
            [this.outputTrack] = (yield this.createOutputStream()).getTracks();
            if (!this.outputTrack) {
                throw new Error('noise reduction: failed to create output track');
            }
            this.logOutputResampleCheck(trackSettings, this.outputTrack);
            if (this.currentModel === NoiseReductionModels.ST) {
                yield this.handleAgcForModel(NoiseReductionModels.ST);
            }
            return this.outputTrack;
        });
    }
    createOutputStream() {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.inputStream) {
                throw new Error('noise reduction: failed to create input stream');
            }
            const mediaStreamDestination = this.audioContext.createMediaStreamDestination();
            [this.effectTrack] = mediaStreamDestination.stream.getTracks();
            if (this.loadFuture) {
                return this.loadFuture.promise;
            }
            this.loadFuture = new Future();
            yield ((_a = this.disposeFuture) === null || _a === void 0 ? void 0 : _a.promise);
            this.disposeFuture = new Future();
            if (this.options.mode === NoiseReductionMode.Legacy) {
                return this.loadLegacy(this.inputStream, mediaStreamDestination);
            }
            return this.loadWorklet(this.inputStream, mediaStreamDestination);
        });
    }
    preloadAssets() {
        return __awaiter(this, void 0, void 0, function* () {
            const start = performance.now();
            logger.log('noise reduction: preloading assets', this.options.mode);
            try {
                yield this.ensureProcessorLoaded();
                const end = performance.now();
                const duration = Math.round(end - start) / 1000;
                logger.log(`noise reduction: preload completed in ${duration} seconds`);
            }
            catch (error) {
                logger.error('noise reduction: preload failed:', error);
                throw error;
            }
        });
    }
    ensureProcessorLoaded() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.processorLoadFuture) {
                logger.log('noise reduction: processor already loading, waiting for it to complete');
                yield this.processorLoadFuture.promise;
                return;
            }
            if (this.isReady) {
                logger.log('noise reduction: processor already loaded, skipping load');
                return;
            }
            this.processorLoadFuture = new Future();
            try {
                if (this.options.mode === NoiseReductionMode.Worklet) {
                    yield this.fetchAndLoadWorkletProcessor();
                }
                else if (this.options.mode === NoiseReductionMode.Legacy) {
                    yield this.fetchAndLoadLegacyProcessor();
                }
                this.processorLoadFuture.resolve();
            }
            catch (error) {
                this.processorLoadFuture.reject(error);
                this.processorLoadFuture = undefined;
                throw error;
            }
        });
    }
    fetchAndLoadWorkletProcessor() {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            const defaultProcessorPath = "denoise/processors/2.4.2/noise-reduction-processor.worklet.js";
            const stProcessorPath = "denoise/processors/3.0.0/noise-reduction-processor.worklet.js";
            if (this.currentModel === NoiseReductionModels.ST) ;
            const processorPath = this.currentModel === NoiseReductionModels.ST ? stProcessorPath : defaultProcessorPath;
            let workletProcessorUrl;
            if (this.options.workletProcessorUrl) {
                workletProcessorUrl = this.options.workletProcessorUrl;
            }
            else if (this.options.baseUrl) {
                workletProcessorUrl = `${this.options.baseUrl}/${processorPath}`;
            }
            else {
                logger.log('noise reduction: fetch worklet processor signed URL');
                try {
                    workletProcessorUrl = yield this.fetchUrl(processorPath);
                }
                catch (error) {
                    logger.error('noise reduction: failed to fetch worklet processor signed URL', error);
                    throw new Error('noise reduction: failed to fetch worklet processor signed URL');
                }
            }
            logger.log('noise reduction: fetch worklet processor', workletProcessorUrl);
            logger.log('noise reduction: add worklet processor to audio context');
            try {
                const workletProcessorBlobUrl = yield fetchAndCacheBlobUrl(workletProcessorUrl);
                yield this.audioContext.audioWorklet.addModule(workletProcessorBlobUrl);
            }
            catch (error) {
                logger.error('noise reduction: failed to load worklet processor from cache', error);
                yield ((_b = (_a = this.audioContext) === null || _a === void 0 ? void 0 : _a.audioWorklet) === null || _b === void 0 ? void 0 : _b.addModule(workletProcessorUrl));
            }
        });
    }
    fetchAndLoadLegacyProcessor() {
        return __awaiter(this, void 0, void 0, function* () {
            const defaultPath = "denoise/processors/2.4.2/noise-reduction-processor.legacy.js";
            const stLegacyPath = "denoise/processors/3.0.0/noise-reduction-processor.legacy.js";
            if (this.currentModel === NoiseReductionModels.ST && !stLegacyPath) {
                throw new Error('noise reduction: env var DENOISE_ST_LEGACY_PROCESSOR_PATH is required for ST model but was not provided');
            }
            const legacyProcessorPath = this.currentModel === NoiseReductionModels.ST && stLegacyPath ? stLegacyPath : defaultPath;
            let legacyProcessorUrl;
            if (this.options.legacyProcessorUrl) {
                legacyProcessorUrl = this.options.legacyProcessorUrl;
            }
            else if (this.options.baseUrl) {
                legacyProcessorUrl = `${this.options.baseUrl}/${legacyProcessorPath}`;
            }
            else {
                legacyProcessorUrl = yield this.fetchUrl(legacyProcessorPath);
            }
            logger.log('noise reduction: fetch legacy processor url', legacyProcessorUrl);
            logger.log('noise reduction: append legacy processor to global scope');
            yield appendScript(legacyProcessorUrl);
        });
    }
    loadWorklet(stream, destination) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.loadFuture) {
                throw new Error('noise reduction: the load future was not initialized correctly');
            }
            logger.log('noise reduction: load worklet');
            try {
                yield this.ensureProcessorLoaded();
                if (!AudioWorkletNode) {
                    throw new Error('noise reduction: `AudioWorkletNode` unavailable in current scope');
                }
                logger.log('noise reduction: attach worklet processor');
                this.workletNode = new AudioWorkletNode(this.audioContext, 'noise-reduction-worklet-processor');
                this.workletNode.port.onmessage = (ev) => {
                    var _a, _b, _c, _d, _e;
                    logger.log(`noise reduction: worklet processor ${ev.data.type.toLowerCase()}`);
                    switch (ev.data.type) {
                        case NoiseReductionEffect.Events.Ready: {
                            this.isReady = true;
                            if (this.currentModel !== NoiseReductionModels.BNR) {
                                logger.log(`noise reduction: switching worklet to initial model: ${this.currentModel}`);
                                (_a = this.workletNode) === null || _a === void 0 ? void 0 : _a.port.postMessage({ type: 'SET_MODEL', model: this.currentModel });
                            }
                            (_b = this.loadFuture) === null || _b === void 0 ? void 0 : _b.resolve(destination.stream);
                            break;
                        }
                        case NoiseReductionEffect.Events.Disabled: {
                            this.handleDisabled();
                            break;
                        }
                        case NoiseReductionEffect.Events.Enabled: {
                            this.handleEnabled();
                            break;
                        }
                        case NoiseReductionEffect.Events.Disposed: {
                            this.isEnabled = false;
                            this.isReady = false;
                            (_c = this.disposeFuture) === null || _c === void 0 ? void 0 : _c.resolve();
                            this.emit(EffectEvent.Disposed);
                            break;
                        }
                        case NoiseReductionEffect.Events.Error: {
                            const { payload } = ev.data;
                            const errorMessage = typeof payload === 'object' && (payload === null || payload === void 0 ? void 0 : payload.error)
                                ? `[${payload.model || NoiseReductionEffect.UNKNOWN_MODEL_LABEL}] ${payload.error}`
                                : String(payload);
                            const err = new Error(`noise reduction: worklet processor error, "${errorMessage}"`);
                            (_d = this.loadFuture) === null || _d === void 0 ? void 0 : _d.reject(err);
                            (_e = this.modelChangeFuture) === null || _e === void 0 ? void 0 : _e.reject(err);
                            logger.error(err);
                            break;
                        }
                        case NoiseReductionEffect.Events.ModelChanged: {
                            this.handleModelChanged(ev.data.payload);
                            break;
                        }
                        case NoiseReductionEffect.Events.Warning: {
                            const warningPayload = ev.data.payload;
                            const warningMessage = typeof warningPayload === 'object' && (warningPayload === null || warningPayload === void 0 ? void 0 : warningPayload.message)
                                ? warningPayload.message
                                : String(warningPayload);
                            logger.warn(`noise reduction: worklet warning - ${warningMessage}`);
                            break;
                        }
                        default:
                            logger.warn('noise reduction: worklet processor unhandled message', ev.data);
                    }
                };
                this.workletNode.connect(destination);
                this.sourceNode = this.audioContext.createMediaStreamSource(stream);
                this.sourceNode.connect(this.workletNode);
            }
            catch (e) {
                const errorMessage = e instanceof Error ? e.message : e;
                this.loadFuture.reject(new Error(`noise reduction: failed to load worklet processor: ${errorMessage}`));
            }
            return this.loadFuture.promise;
        });
    }
    loadLegacy(stream, destination) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.loadFuture) {
                throw new Error('noise reduction: the load future was not initialized correctly');
            }
            logger.log('noise reduction: load legacy');
            try {
                yield this.ensureProcessorLoaded();
                this.legacyProcessor = new window.WebMediaEffects.NoiseReductionLegacyProcessor(this.audioContext.sampleRate);
                const bufferLengthMap = { 16000: 256, 32000: 512, 48000: 512 };
                const bufferLength = bufferLengthMap[this.audioContext.sampleRate];
                this.legacyScriptNode = this.audioContext.createScriptProcessor(bufferLength, 1, 1);
                this.legacyScriptNode.connect(destination);
                this.legacyScriptNode.onaudioprocess = this.legacyProcessor.processAudioChunk;
                this.sourceNode = this.audioContext.createMediaStreamSource(stream);
                this.sourceNode.connect(this.legacyScriptNode);
                this.isReady = true;
                this.loadFuture.resolve(destination.stream);
            }
            catch (e) {
                this.loadFuture.reject(new Error('noise reduction: failed to load legacy processor'));
            }
            return this.loadFuture.promise;
        });
    }
    enable() {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            logger.log('noise reduction: enable effect');
            if (!this.isReady) {
                throw new Error('noise reduction: not setup or ready');
            }
            this.enableFuture = new Future();
            (_a = this.workletNode) === null || _a === void 0 ? void 0 : _a.port.postMessage('ENABLE');
            (_b = this.legacyProcessor) === null || _b === void 0 ? void 0 : _b.setEnabled(true).then(() => this.handleEnabled());
            return this.enableFuture.promise;
        });
    }
    disable() {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            logger.log('noise reduction: disable effect');
            if (!this.isReady) {
                throw new Error('noise reduction: not setup or ready');
            }
            this.disableFuture = new Future();
            (_a = this.workletNode) === null || _a === void 0 ? void 0 : _a.port.postMessage('DISABLE');
            (_b = this.legacyProcessor) === null || _b === void 0 ? void 0 : _b.setEnabled(false).then(() => this.handleDisabled());
            return this.disableFuture.promise;
        });
    }
    dispose() {
        const _super = Object.create(null, {
            dispose: { get: () => super.dispose }
        });
        var _a, _b, _c, _d, _e, _f, _g;
        return __awaiter(this, void 0, void 0, function* () {
            logger.log('noise reduction: dispose effect');
            if (this.previousAgcState !== undefined && this.currentModel === NoiseReductionModels.ST) {
                try {
                    const audioTrack = this.inputTrack;
                    if (audioTrack) {
                        yield audioTrack.applyConstraints({
                            autoGainControl: this.previousAgcState,
                        });
                        logger.log('noise reduction: AGC restored on dispose');
                    }
                }
                catch (error) {
                    logger.warn('noise reduction: failed to restore AGC on dispose', error);
                }
            }
            this.previousAgcState = undefined;
            this.modelChangeFuture = undefined;
            if (!this.loadFuture) {
                return;
            }
            yield this.loadFuture.promise;
            this.loadFuture = undefined;
            (_a = this.workletNode) === null || _a === void 0 ? void 0 : _a.port.postMessage('DISPOSE');
            (_b = this.legacyProcessor) === null || _b === void 0 ? void 0 : _b.dispose().then((_c = this.disposeFuture) === null || _c === void 0 ? void 0 : _c.resolve);
            yield ((_d = this.disposeFuture) === null || _d === void 0 ? void 0 : _d.promise);
            (_e = this.workletNode) === null || _e === void 0 ? void 0 : _e.disconnect();
            (_f = this.legacyScriptNode) === null || _f === void 0 ? void 0 : _f.disconnect();
            (_g = this.sourceNode) === null || _g === void 0 ? void 0 : _g.disconnect();
            yield _super.dispose.call(this);
            logger.log('noise reduction: effect disposed');
        });
    }
    handleDisabled() {
        const _super = Object.create(null, {
            disable: { get: () => super.disable }
        });
        return __awaiter(this, void 0, void 0, function* () {
            yield _super.disable.call(this);
            this.disableFuture.resolve();
        });
    }
    handleEnabled() {
        const _super = Object.create(null, {
            enable: { get: () => super.enable }
        });
        return __awaiter(this, void 0, void 0, function* () {
            yield _super.enable.call(this);
            this.enableFuture.resolve();
        });
    }
    setModel(model) {
        return __awaiter(this, void 0, void 0, function* () {
            if (model === this.currentModel) {
                logger.log(`noise reduction: already using ${model} model`);
                return;
            }
            if (this.options.mode === NoiseReductionMode.Legacy) {
                this.currentModel = model;
                if (model === NoiseReductionModels.ST) {
                    logger.warn('noise reduction: ST model is not supported in Legacy mode. ' +
                        'The model will be stored but processing will use BNR. ' +
                        'Consider switching to Worklet mode for ST support.');
                }
                return;
            }
            if (!this.isReady) {
                this.currentModel = model;
                logger.log(`noise reduction: model will be set to ${model} when ready`);
                return;
            }
            if (!this.workletNode) {
                throw new Error('noise reduction: cannot set model, worklet not loaded');
            }
            logger.log(`noise reduction: switching model from ${this.currentModel} to ${model}`);
            this.modelChangeFuture = new Future();
            logger.log(`noise reduction: sending SET_MODEL message to worklet`);
            this.workletNode.port.postMessage({ type: 'SET_MODEL', model });
            const MODEL_CHANGE_TIMEOUT = 5000;
            let timeoutId;
            const timeoutPromise = new Promise((_, reject) => {
                timeoutId = setTimeout(() => {
                    reject(new Error(`noise reduction: model change to ${model} timed out after ${MODEL_CHANGE_TIMEOUT}ms in ${this.options.mode} mode`));
                }, MODEL_CHANGE_TIMEOUT);
            });
            try {
                yield Promise.race([this.modelChangeFuture.promise, timeoutPromise]);
                logger.log(`noise reduction: model change confirmed, now handling AGC`);
            }
            catch (error) {
                logger.error('noise reduction: model change failed', error);
                throw error;
            }
            finally {
                if (timeoutId !== undefined) {
                    clearTimeout(timeoutId);
                }
                this.modelChangeFuture = undefined;
            }
            yield this.handleAgcForModel(model);
        });
    }
    handleModelChanged(model) {
        var _a;
        this.currentModel = model;
        (_a = this.modelChangeFuture) === null || _a === void 0 ? void 0 : _a.resolve();
        this.emit(EffectEvent.ModelChanged, model);
        logger.log(`noise reduction: model changed to ${model}`);
    }
    handleAgcForModel(model) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const audioTrack = this.inputTrack;
            if (!audioTrack) {
                return;
            }
            try {
                const capabilities = (_a = audioTrack.getCapabilities) === null || _a === void 0 ? void 0 : _a.call(audioTrack);
                const hasAgcSupport = (capabilities === null || capabilities === void 0 ? void 0 : capabilities.autoGainControl) !== undefined;
                if (!hasAgcSupport) {
                    logger.log('noise reduction: AGC not supported by this track');
                    return;
                }
                const currentSettings = audioTrack.getSettings();
                if (model === NoiseReductionModels.ST) {
                    this.previousAgcState = currentSettings.autoGainControl;
                    if (currentSettings.autoGainControl) {
                        logger.log('noise reduction: disabling AGC for ST model');
                        yield audioTrack.applyConstraints({
                            autoGainControl: false,
                        });
                    }
                }
                else if (model === NoiseReductionModels.BNR && this.previousAgcState !== undefined) {
                    logger.log(`noise reduction: restoring AGC to ${this.previousAgcState} for BNR model`);
                    yield audioTrack.applyConstraints({
                        autoGainControl: this.previousAgcState,
                    });
                    this.previousAgcState = undefined;
                }
            }
            catch (error) {
                logger.warn('noise reduction: failed to update AGC setting', error);
            }
        });
    }
    logInputDiagnostics(audioTrack) {
        const trackSettings = audioTrack.getSettings();
        logger.log('noise reduction: applied track settings', {
            sampleRate: trackSettings.sampleRate,
            channelCount: trackSettings.channelCount,
            echoCancellation: trackSettings.echoCancellation,
            noiseSuppression: trackSettings.noiseSuppression,
            autoGainControl: trackSettings.autoGainControl,
            deviceId: trackSettings.deviceId,
            latency: trackSettings.latency,
        });
        logger.log('noise reduction: audio context latency', {
            baseLatency: this.audioContext.baseLatency,
            outputLatency: this.audioContext.outputLatency,
        });
        const deviceSampleRate = trackSettings.sampleRate;
        const hasDeviceRate = deviceSampleRate && Number.isFinite(deviceSampleRate) && deviceSampleRate > 0;
        const resampleMismatch = hasDeviceRate && trackSettings.sampleRate !== this.audioContext.sampleRate;
        if (resampleMismatch) {
            logger.warn('noise reduction: resample mismatch detected', {
                deviceSampleRate: trackSettings.sampleRate,
                contextSampleRate: this.audioContext.sampleRate,
                ratio: Number(this.audioContext.sampleRate / deviceSampleRate).toFixed(6),
            });
        }
        const label = (audioTrack.label || '').toLowerCase();
        const isExternalDevice = /usb|pchero|headset|airpods|bluetooth/.test(label);
        if (isExternalDevice) {
            logger.log('noise reduction: external audio device detected', {
                label: audioTrack.label,
                deviceSampleRate: trackSettings.sampleRate,
            });
        }
    }
    logOutputResampleCheck(inputSettings, outputTrack) {
        var _a;
        const outputSettings = ((_a = outputTrack.getSettings) === null || _a === void 0 ? void 0 : _a.call(outputTrack)) || {};
        const inRate = inputSettings.sampleRate;
        const outRate = outputSettings.sampleRate;
        if (inRate && outRate && inRate !== outRate) {
            const inputMismatchedContext = inRate !== this.audioContext.sampleRate;
            const mismatchAlreadyLogged = inputMismatchedContext && outRate === this.audioContext.sampleRate;
            if (!mismatchAlreadyLogged) {
                logger.warn('noise reduction: unexpected resample mismatch detected', {
                    inputSampleRate: inRate,
                    outputSampleRate: outRate,
                    ratio: Number(outRate / inRate).toFixed(6),
                });
            }
        }
    }
    updateSampleRate(sampleRate) {
        const supportedRates = [16000, 32000, 48000];
        if (!supportedRates.includes(sampleRate)) {
            throw new Error(`noise reduction: unsupported sample rate ${sampleRate}Hz, must be one of ${supportedRates.join(', ')}Hz`);
        }
        if (this.isReady || this.loadFuture) {
            throw new Error('noise reduction: cannot update sample rate after effect is loaded or loading');
        }
        if (this.audioContext.sampleRate === sampleRate) {
            logger.log(`noise reduction: sample rate already at ${sampleRate}Hz`);
            return;
        }
        logger.log(`noise reduction: updating sample rate to ${sampleRate}Hz`);
        try {
            this.audioContext.close().catch((e) => {
                logger.warn('noise reduction: error closing audio context', e);
            });
            const newAudioContext = new AudioContext({ sampleRate });
            this.processorLoadFuture = undefined;
            super.updateAudioContext(newAudioContext);
            logger.log(`noise reduction: sample rate updated to ${sampleRate}Hz`);
        }
        catch (error) {
            const message = error instanceof Error ? error.message : String(error);
            logger.error(`noise reduction: failed to update sample rate: ${message}`);
            throw new Error(`noise reduction: failed to update sample rate: ${message}`);
        }
    }
    updateAudioContext(audioContext) {
        const _super = Object.create(null, {
            updateAudioContext: { get: () => super.updateAudioContext }
        });
        var _a, _b, _c;
        return __awaiter(this, void 0, void 0, function* () {
            logger.log('noise reduction: updating audio context');
            const wasLoaded = this.isLoaded;
            const wasReady = this.isReady;
            const wasEnabled = this.isEnabled;
            const inputStream = this.inputStream;
            if (wasLoaded || wasReady) {
                logger.log('noise reduction: disposing current pipeline');
                (_a = this.workletNode) === null || _a === void 0 ? void 0 : _a.disconnect();
                (_b = this.legacyScriptNode) === null || _b === void 0 ? void 0 : _b.disconnect();
                (_c = this.sourceNode) === null || _c === void 0 ? void 0 : _c.disconnect();
                this.isReady = false;
                this.processorLoadFuture = undefined;
                const loadFutureBackup = this.loadFuture;
                this.loadFuture = undefined;
                try {
                    _super.updateAudioContext.call(this, audioContext);
                    logger.log('noise reduction: audio context updated successfully');
                    if (wasLoaded && inputStream) {
                        logger.log('noise reduction: reloading effect with new audio context');
                        yield this.loadMediaStream(inputStream);
                        if (wasEnabled) {
                            yield this.enable();
                        }
                        if (loadFutureBackup && this.outputStream) {
                            loadFutureBackup.resolve(this.outputStream);
                        }
                    }
                }
                catch (error) {
                    const message = error instanceof Error ? error.message : String(error);
                    logger.error(`noise reduction: failed to update audio context: ${message}`);
                    if (loadFutureBackup) {
                        loadFutureBackup.reject(new Error(`noise reduction: failed to update audio context: ${message}`));
                    }
                    throw error;
                }
            }
            else {
                logger.log('noise reduction: simple audio context update (not loaded yet)');
                _super.updateAudioContext.call(this, audioContext);
            }
        });
    }
}
NoiseReductionEffect.UNKNOWN_MODEL_LABEL = 'unknown';
NoiseReductionEffect.kind = 'noise-reduction-effect';
NoiseReductionEffect.supportsSimd = hasSimdSupport;
NoiseReductionEffect.Events = {
    Ready: 'READY',
    Disabled: 'DISABLED',
    Disposed: 'DISPOSED',
    Enabled: 'ENABLED',
    Error: 'ERROR',
    ModelChanged: 'MODEL_CHANGED',
    Warning: 'WARNING',
};
const createNoiseReductionEffect = (options, preloadAssets) => __awaiter(void 0, void 0, void 0, function* () { return createEffect(NoiseReductionEffect, options, preloadAssets); });

function memoize(func) {
    const cache = new Map();
    return function doMemoize(arg) {
        if (cache.has(arg)) {
            return cache.get(arg);
        }
        const result = func(arg);
        cache.set(arg, result);
        return result;
    };
}

const NUM_BOX_COORDS = 4;
const NUM_COORDS_PER_KEYPOINT = 3;
const BOX_COORDS_END_IDX = 4;
const CONFIDENCE_OFFSET = 1;
function decodeModelOutputShape(shape) {
    const [batchSize, numAnchors, gridHeight, gridWidth, totalCoords] = shape;
    const numKeypoints = (totalCoords - (NUM_BOX_COORDS + CONFIDENCE_OFFSET)) / NUM_COORDS_PER_KEYPOINT;
    const indexOfConfidence = BOX_COORDS_END_IDX + numKeypoints * NUM_COORDS_PER_KEYPOINT;
    const decodedShape = {
        batchSize,
        numAnchors,
        gridHeight,
        gridWidth,
        numBoxCoords: NUM_BOX_COORDS,
        numLandmarks: numKeypoints * NUM_COORDS_PER_KEYPOINT,
        numKeypoints,
        indexOfConfidence,
    };
    return decodedShape;
}
function centerToCornerCoords(box) {
    return {
        centerX: box.centerX - box.width / 2,
        centerY: box.centerY - box.height / 2,
        width: box.centerX + box.width / 2,
        height: box.centerY + box.height / 2,
        score: box.score,
        landmarks: box.landmarks,
    };
}
function extractBoxesAndScores(output, numAnchors, height, width, numBoxCoords, numLandmarks) {
    const numBoxes = numAnchors * height * width;
    const stride = numBoxCoords + numLandmarks * 3 + 1;
    const boxes = [];
    for (let i = 0; i < numBoxes; i += 1) {
        const baseIndex = i * stride;
        const scoreIndex = baseIndex + stride - 1;
        const landmarks = [];
        const box = centerToCornerCoords({
            centerX: output[baseIndex],
            centerY: output[baseIndex + 1],
            width: output[baseIndex + 2],
            height: output[baseIndex + 3],
            score: output[scoreIndex],
            landmarks,
        });
        boxes.push(box);
    }
    return boxes;
}
function computeIoU(boxA, boxB) {
    const xA = Math.max(boxA.centerX, boxB.centerX);
    const yA = Math.max(boxA.centerY, boxB.centerY);
    const xB = Math.min(boxA.width, boxB.width);
    const yB = Math.min(boxA.height, boxB.height);
    const intersectionArea = Math.max(0, xB - xA) * Math.max(0, yB - yA);
    const boxAArea = (boxA.width - boxA.centerX) * (boxA.height - boxA.centerY);
    const boxBArea = (boxB.width - boxB.centerX) * (boxB.height - boxB.centerY);
    const iou = intersectionArea / (boxAArea + boxBArea - intersectionArea);
    return iou;
}
function applyNMS(boxes, nmsThreshold) {
    const sortedBoxes = boxes.sort((a, b) => b.score - a.score);
    const selectedBoxes = [];
    sortedBoxes.forEach((currentBox) => {
        let keep = true;
        for (let i = 0; i < selectedBoxes.length; i += 1) {
            const selectedBox = selectedBoxes[i];
            const iou = computeIoU({
                centerX: currentBox.centerX,
                centerY: currentBox.centerY,
                width: currentBox.width,
                height: currentBox.height,
            }, {
                centerX: selectedBox.centerX,
                centerY: selectedBox.centerY,
                width: selectedBox.width,
                height: selectedBox.height,
            });
            if (iou > nmsThreshold) {
                keep = false;
                break;
            }
        }
        if (keep) {
            selectedBoxes.push(currentBox);
        }
    });
    return selectedBoxes;
}
const memoizedDecodeModelOutputShape = memoize(decodeModelOutputShape);
function getNumFaces(output, shape, filterBoxesThreshold = 0.3, nmsThreshold = 0.3) {
    const decodedShape = memoizedDecodeModelOutputShape(shape);
    const boxes = extractBoxesAndScores(output, decodedShape.numAnchors, decodedShape.gridHeight, decodedShape.gridWidth, decodedShape.numBoxCoords, decodedShape.numKeypoints);
    const filteredBoxes = boxes.filter((box) => box.score > filterBoxesThreshold);
    const finalBoxes = applyNMS(filteredBoxes, nmsThreshold);
    return finalBoxes.length;
}

var BeRightBackAction;
(function (BeRightBackAction) {
    BeRightBackAction["TurnOn"] = "turn-on";
    BeRightBackAction["TurnOff"] = "turn-off";
})(BeRightBackAction || (BeRightBackAction = {}));
var BeRightBackState;
(function (BeRightBackState) {
    BeRightBackState["Idle"] = "idle";
    BeRightBackState["Off"] = "off";
    BeRightBackState["On"] = "on";
})(BeRightBackState || (BeRightBackState = {}));
var BeRightBackEvent;
(function (BeRightBackEvent) {
    BeRightBackEvent["On"] = "on";
    BeRightBackEvent["Off"] = "off";
    BeRightBackEvent["StateChange"] = "state-change";
})(BeRightBackEvent || (BeRightBackEvent = {}));
var BeRightBackPluginMode;
(function (BeRightBackPluginMode) {
    BeRightBackPluginMode["Off"] = "off";
    BeRightBackPluginMode["Conservative"] = "conservative";
    BeRightBackPluginMode["Aggressive"] = "aggressive";
})(BeRightBackPluginMode || (BeRightBackPluginMode = {}));

class DebugBuffer {
    constructor(bufferSize = 30) {
        this.buffer = [];
        this.bufferSize = bufferSize;
    }
    add(entry) {
        this.buffer.push(entry);
        if (this.buffer.length > this.bufferSize) {
            this.buffer.shift();
        }
    }
    getDebugInfo() {
        return [...this.buffer];
    }
    getLatestDebugInfo() {
        return this.buffer[this.buffer.length - 1];
    }
    clear() {
        this.buffer = [];
    }
    setBufferSize(size) {
        this.bufferSize = size;
    }
}

const defaultOptions$2 = {
    motionIouThreshold: 0.5,
    onHysteresisMaxMs: 3000,
    offHysteresisMaxMs: 2000,
};
class BeRightBack extends EventEmitter {
    constructor(options = defaultOptions$2) {
        super();
        this.debugBuffer = new DebugBuffer();
        this.state = BeRightBackState.Idle;
        this.onHysteresisDurationMs = 0;
        this.offHysteresisDurationMs = 0;
        this.lastTimestampMs = 0;
        this.isEnabled = true;
        this.isFirstFrame = true;
        this.updateOptions(options);
    }
    destroy() {
        this.removeAllListeners();
    }
    getDebugInfo() {
        return this.debugBuffer.getDebugInfo();
    }
    getLatestDebugInfo() {
        return this.debugBuffer.getLatestDebugInfo();
    }
    getState() {
        return this.state;
    }
    setEnable(enable) {
        this.isEnabled = enable;
        if (!enable) {
            this.changeState(BeRightBackState.Idle);
            this.resetHysteresis();
            this.isFirstFrame = true;
        }
    }
    updateOptions(newOptions) {
        this.options = Object.assign(Object.assign({}, defaultOptions$2), newOptions);
        if (this.options.motionIouThreshold < 0 || this.options.motionIouThreshold > 1) {
            throw new Error('Invalid motionIouThreshold: must be between 0 and 1.');
        }
        if (this.options.onHysteresisMaxMs < 0) {
            throw new Error('Invalid onHysteresisMaxMs: must be greater than 0.');
        }
        if (this.options.offHysteresisMaxMs < 0) {
            throw new Error('Invalid offHysteresisMaxMs: must be greater than 0.');
        }
    }
    processFrame(faces, shape, motion, timestampMs) {
        if (!this.isEnabled) {
            return BeRightBackState.Idle;
        }
        const numFaces = getNumFaces(faces, shape);
        const isMoving = motion < this.options.motionIouThreshold;
        if (this.isFirstFrame) {
            this.initializeFirstFrameState(timestampMs);
        }
        const action = this.determineActionFromInferenceResult(numFaces, isMoving);
        const timeDiffMs = timestampMs - this.lastTimestampMs;
        this.handleHysteresisAndStateChange(action, timeDiffMs);
        this.lastTimestampMs = timestampMs;
        this.debugBuffer.add({
            action,
            isMoving,
            offHysteresisDurationMs: this.offHysteresisDurationMs,
            onHysteresisDurationMs: this.onHysteresisDurationMs,
            numFaces,
            state: this.state,
            timeDiffMs,
        });
        return this.state;
    }
    initializeFirstFrameState(timestampMs) {
        this.isFirstFrame = false;
        this.lastTimestampMs = timestampMs;
        this.changeState(BeRightBackState.Off);
        this.resetHysteresis();
    }
    determineActionFromInferenceResult(numFaces, isMoving) {
        if (this.state === BeRightBackState.On) {
            return numFaces > 0 ? BeRightBackAction.TurnOff : BeRightBackAction.TurnOn;
        }
        if (numFaces > 0 || isMoving) {
            return BeRightBackAction.TurnOff;
        }
        return BeRightBackAction.TurnOn;
    }
    handleHysteresisAndStateChange(action, timeDiffMs) {
        if (this.state === BeRightBackState.Off && action === BeRightBackAction.TurnOn) {
            this.accumulateHysteresisAndAttemptTurnOn(timeDiffMs);
        }
        else if (this.state === BeRightBackState.On && action === BeRightBackAction.TurnOff) {
            this.accumulateHysteresisAndAttemptTurnOff(timeDiffMs);
        }
        else {
            this.resetHysteresis();
        }
    }
    accumulateHysteresisAndAttemptTurnOn(timeDiffMs) {
        this.onHysteresisDurationMs += timeDiffMs;
        if (this.onHysteresisDurationMs >= this.options.onHysteresisMaxMs) {
            this.changeState(BeRightBackState.On);
            this.resetHysteresis();
        }
    }
    accumulateHysteresisAndAttemptTurnOff(timeDiffMs) {
        this.offHysteresisDurationMs += timeDiffMs;
        if (this.offHysteresisDurationMs >= this.options.offHysteresisMaxMs) {
            this.changeState(BeRightBackState.Off);
            this.resetHysteresis();
        }
    }
    changeState(newState) {
        if (this.state !== newState) {
            this.emit(BeRightBackEvent.StateChange, newState, this.state);
        }
        if (newState === BeRightBackState.On) {
            this.emit(BeRightBackEvent.On);
        }
        if (newState === BeRightBackState.Off) {
            this.emit(BeRightBackEvent.Off);
        }
        this.state = newState;
    }
    resetHysteresis() {
        this.onHysteresisDurationMs = 0;
        this.offHysteresisDurationMs = 0;
    }
}

class BaseInferencePlugin {
    constructor(options) {
        this.isEffectEnabled = false;
        this.isInitialized = false;
        this.options = Object.assign(Object.assign({}, this.getDefaultOptions()), options);
        this.core = this.createCore();
        this.onEffectEnabled = this.onEffectEnabled.bind(this);
        this.onEffectDisabled = this.onEffectDisabled.bind(this);
        this.onCoreCreated();
    }
    initialize(effect) {
        this.isEffectEnabled = effect.isEnabled;
        this.isInitialized = true;
        const handleEffectEnabled = () => {
            this.isEffectEnabled = true;
            this.onEffectEnabled();
        };
        const handleEffectDisabled = () => {
            this.isEffectEnabled = false;
            this.onEffectDisabled();
        };
        effect.on(EffectEvent.Enabled, handleEffectEnabled);
        effect.on(EffectEvent.Disabled, handleEffectDisabled);
        this.baseInferenceCleanup = () => {
            effect.off(EffectEvent.Enabled, handleEffectEnabled);
            effect.off(EffectEvent.Disabled, handleEffectDisabled);
        };
    }
    dispose() {
        var _a;
        this.isInitialized = false;
        this.destroyCore();
        (_a = this.baseInferenceCleanup) === null || _a === void 0 ? void 0 : _a.call(this);
    }
    updateOptions(newOptions) {
        this.options = Object.assign(Object.assign({}, this.options), newOptions);
        this.onOptionsUpdated();
    }
    getDefaultOptions(options) {
        return {};
    }
    createCore() {
        return undefined;
    }
    onCoreCreated() {
    }
    onEffectEnabled() {
    }
    onEffectDisabled() {
    }
    cleanup() {
    }
    onOptionsUpdated() {
    }
}

class BaseEventEmitterPlugin extends BaseInferencePlugin {
    constructor(options) {
        super(options);
        if (this.core) {
            this.on = this.core.on.bind(this.core);
            this.off = this.core.off.bind(this.core);
            this.once = this.core.once.bind(this.core);
        }
    }
    destroyCore() {
        var _a;
        (_a = this.core) === null || _a === void 0 ? void 0 : _a.removeAllListeners();
    }
}

class BaseAfterInferencePlugin extends BaseEventEmitterPlugin {
    constructor(options) {
        super(options);
        this.onAfterInference = this.onAfterInference.bind(this);
    }
    initialize(effect) {
        super.initialize(effect);
        effect.addAfterInferenceCallback(this.onAfterInference);
        this.afterInferenceCleanup = () => {
            effect.removeAfterInferenceCallback(this.onAfterInference);
        };
    }
    dispose() {
        var _a;
        (_a = this.afterInferenceCleanup) === null || _a === void 0 ? void 0 : _a.call(this);
        super.dispose();
    }
}

const filterValidEntries = (obj) => {
    return Object.fromEntries(Object.entries(obj || {}).filter(([, value]) => value !== undefined && value !== null && value.toString() !== ''));
};
function getPluginOptions(mode, modesMap, defaultMode, coreOptions) {
    const validMode = Object.values(modesMap).includes(modesMap[mode]) ? mode : defaultMode;
    return Object.assign(Object.assign({}, modesMap[validMode]), filterValidEntries(coreOptions));
}

const modesMap$1 = {
    [BeRightBackPluginMode.Off]: {
        motionIouThreshold: 0,
        onHysteresisMaxMs: 0,
        offHysteresisMaxMs: 0,
    },
    [BeRightBackPluginMode.Conservative]: {
        motionIouThreshold: 0.9,
        onHysteresisMaxMs: 3000,
        offHysteresisMaxMs: 2000,
    },
    [BeRightBackPluginMode.Aggressive]: {
        motionIouThreshold: 0.95,
        onHysteresisMaxMs: 1500,
        offHysteresisMaxMs: 1000,
    },
};
function getOptionsByMode(mode) {
    return modesMap$1[mode];
}
function makeBeRightBackOptions(mode, coreOptions) {
    const defaultMode = BeRightBackPluginMode.Conservative;
    return getPluginOptions(mode, modesMap$1, defaultMode, coreOptions);
}

class BeRightBackPlugin extends BaseAfterInferencePlugin {
    getDefaultOptions() {
        return {
            mode: BeRightBackPluginMode.Conservative,
        };
    }
    createCore() {
        const options = makeBeRightBackOptions(this.options.mode, this.options.coreOptions);
        return new BeRightBack(options);
    }
    onAfterInference(timestamp, result) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.isEffectEnabled) {
                return;
            }
            if (this.options.mode === BeRightBackPluginMode.Off) {
                return;
            }
            if ((_a = result === null || result === void 0 ? void 0 : result.facesAndLandmarks) === null || _a === void 0 ? void 0 : _a.dims.length) {
                (_b = this.core) === null || _b === void 0 ? void 0 : _b.processFrame(result.facesAndLandmarks.data, result.facesAndLandmarks.dims, result.motion, timestamp);
            }
        });
    }
    onOptionsUpdated() {
        var _a;
        const options = makeBeRightBackOptions(this.options.mode, this.options.coreOptions);
        (_a = this.core) === null || _a === void 0 ? void 0 : _a.updateOptions(options);
    }
    getDebugInfo() {
        var _a, _b;
        return (_b = (_a = this.core) === null || _a === void 0 ? void 0 : _a.getDebugInfo()) !== null && _b !== void 0 ? _b : [];
    }
    getLatestDebugInfo() {
        var _a;
        return (_a = this.core) === null || _a === void 0 ? void 0 : _a.getLatestDebugInfo();
    }
}

const defaultTheme = {
    colors: {
        primary: '#ffffff',
        secondary: '#3b82f6',
        success: '#10b981',
        warning: '#f59e0b',
        error: '#ef4444',
        muted: '#9ca3af',
        white: '#ffffff',
        background: 'rgba(17, 24, 39, 0.95)',
        border: '#374151',
    },
};
class DebugOverlayCSS {
    static injectStyles(theme = defaultTheme) {
        if (this.stylesInjected || document.getElementById('vb-debug-styles'))
            return;
        const styles = `
    #vb-debug-overlay {
      position: fixed;
      top: 20px;
      right: 20px;
      width: 350px;
      min-width: 300px;
      max-width: 800px;
      min-height: 300px;
      max-height: 100vh;
      background: ${theme.colors.background};
      color: ${theme.colors.primary};
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
      font-size: 12px;
      padding: 0;
      border-radius: 12px;
      border: 1px solid ${theme.colors.border};
      z-index: 10000;
      box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
      backdrop-filter: blur(10px);
      display: none;
      overflow: hidden;
      resize: both;
      user-select: none;
    }

    .vb-debug-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 8px 6px;
      border-bottom: 1px solid ${theme.colors.border};
      background: rgba(55, 65, 81, 0.5);
      cursor: move;
      user-select: none;
    }

    .vb-debug-title {
      font-weight: 600;
      color: ${theme.colors.white};
      flex: 1;
      pointer-events: none;
      font-size: 12px;
      margin-left: 10px;
    }

    .vb-debug-close {
      background: transparent;
      border: 1px solid ${theme.colors.border};
      color: ${theme.colors.muted};
      width: 28px;
      height: 28px;
      border-radius: 6px;
      cursor: pointer;
      font-size: 14px;
      font-weight: bold;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.2s ease;
      pointer-events: auto;
    }

    .vb-debug-close:hover {
      background: ${theme.colors.error};
      border-color: ${theme.colors.error};
      color: white;
    }

    .vb-debug-content {
      padding: 2px 12px;
      overflow-y: auto;
      height: calc(100% - 72px);
      flex: 1;
    }

    .vb-debug-section {
      margin: 6px 0;
    }

    .vb-debug-section-title {
      color: ${theme.colors.secondary};
      font-weight: 600;
      margin-bottom: 4px;
      font-size: 12px;
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }

    .vb-debug-divider {
      border: none;
      margin: 6px 0;
      opacity: 0.6;
    }

    .vb-debug-row {
      margin: 0px 0;
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 4px 6px;
      border-radius: 2px;
      transition: background-color 0.1s ease;
    }

    /* Subtle alternating row colors */
    .vb-debug-row:nth-child(even) {
      background: rgba(255, 255, 255, 0.03);
    }

    .vb-debug-row:nth-child(odd) {
      background: rgba(255, 255, 255, 0.00);
    }

    .vb-debug-row:hover {
      background: rgba(255, 255, 255, 0.1);
    }

    .vb-debug-label {
      color: ${theme.colors.muted};
      font-weight: 500;
      font-size: 11px;
      flex: 1;
    }

    .vb-debug-value {
      font-weight: 600;
      font-size: 11px;
      text-align: right;
      margin-left: 12px;
    }

    .vb-debug-value--good { 
      color: ${theme.colors.success}; 
      background: rgba(16, 185, 129, 0.1);
      padding: 2px 6px;
      border-radius: 4px;
    }
    
    .vb-debug-value--warning { 
      color: ${theme.colors.warning}; 
      background: rgba(245, 158, 11, 0.1);
      padding: 2px 6px;
      border-radius: 4px;
    }
    
    .vb-debug-value--error { 
      color: ${theme.colors.error}; 
      background: rgba(239, 68, 68, 0.1);
      padding: 2px 6px;
      border-radius: 4px;
    }
    
    .vb-debug-value--info { 
      color: ${theme.colors.muted}; 
    }

    /* =====  COLLAPSIBLE STYLES ===== */
    .vb-debug-section-header {
      display: flex;
      align-items: start;
      cursor: pointer;
      padding: 0;
      user-select: none;
      transition: background-color 0.1s ease;
    }

    .vb-debug-section-header:hover {
      background: rgba(255, 255, 255, 0.05);
      border-radius: 2px;
    }

    .vb-debug-section-toggle {
      margin-right: 8px;
      font-size: 12px;
      transition: transform 0.1s ease;
      color: ${theme.colors.muted};
    }

    .vb-debug-section-toggle--expanded {
      transform: rotate(90deg);
    }

    .vb-debug-section-content {
      margin-left: 10px;
      margin-top: 0px;
      overflow: hidden;
      transition: max-height 0.05s ease-out, opacity 0.05s ease-out;
    }

    .vb-debug-section-content--collapsed {
      max-height: 0;
      opacity: 0;
      margin-top: 0;
    }

    .vb-debug-section-content--expanded {
      max-height: 1000px;
      opacity: 1;
    }
    /* ===== END COLLAPSIBLE STYLES ===== */

    .vb-debug-resize-handle {
      position: absolute;
      bottom: 0;
      right: 0;
      width: 24px;
      height: 24px;
      cursor: nw-resize;
      background: linear-gradient(-45deg, transparent 40%, ${theme.colors.border} 40%, ${theme.colors.border} 60%, transparent 60%);
      border-bottom-right-radius: 10px;
      opacity: 0.6;
      transition: opacity 0.2s ease;
    }

    .vb-debug-resize-handle:hover {
      opacity: 1;
    }

    .vb-debug-instructions {
      margin-top: 10px;
      padding-top: 8px;
      padding-left: 16px;
      height: 24px;
      border-top: 1px solid ${theme.colors.border};
      font-size: 10px;
      color: ${theme.colors.muted};
      line-height: 1.5;
    }

    .vb-debug-instructions strong {
      color: ${theme.colors.primary};
      font-weight: 600;
    }

    /* Status indicators */
    .vb-debug-status-indicator {
      display: inline-block;
      width: 6px;
      height: 6px;
      border-radius: 50%;
      margin-right: 6px;
    }

    .vb-debug-status-indicator--active {
      background: ${theme.colors.success};
      box-shadow: 0 0 6px ${theme.colors.success};
    }

    .vb-debug-status-indicator--inactive {
      background: ${theme.colors.error};
    }

    .vb-debug-error-indicator {
      background: rgba(239, 68, 68, 0.1);
      border: 1px solid ${theme.colors.error};
      border-radius: 4px;
      padding: 8px;
      margin: 8px 0;
      font-size: 11px;
      line-height: 1.4;
    }

    .vb-debug-error-message {
      color: ${theme.colors.muted};
    }    
  `;
        const styleSheet = document.createElement('style');
        styleSheet.id = 'vb-debug-styles';
        styleSheet.textContent = styles;
        document.head.appendChild(styleSheet);
        this.stylesInjected = true;
    }
    static removeStyles() {
        const existingStyles = document.getElementById('vb-debug-styles');
        if (existingStyles) {
            existingStyles.remove();
            this.stylesInjected = false;
        }
    }
}
DebugOverlayCSS.stylesInjected = false;
class DebugOverlayDOM {
    constructor(theme = defaultTheme) {
        this.collapsedSections = new Set();
        this.theme = theme;
    }
    create(tag, className, content) {
        const el = document.createElement(tag);
        if (className)
            el.className = className;
        if (content)
            el.innerHTML = content;
        return el;
    }
    createSection(title, emoji) {
        const container = this.create('div');
        container.appendChild(this.create('hr', 'vb-debug-divider'));
        container.appendChild(this.create('div', 'vb-debug-section-title', `${title}`));
        return container;
    }
    createCollapsibleSection(title, sectionId, defaultExpanded = true) {
        const container = this.create('div', 'vb-debug-section');
        container.appendChild(this.create('hr', 'vb-debug-divider'));
        const header = this.create('div', 'vb-debug-section-header');
        const toggle = this.create('span', 'vb-debug-section-toggle', '▶');
        const titleElement = this.create('div', 'vb-debug-section-title', title);
        header.appendChild(toggle);
        header.appendChild(titleElement);
        const content = this.create('div', 'vb-debug-section-content');
        const isExpanded = defaultExpanded && !this.collapsedSections.has(sectionId);
        this.updateSectionState(toggle, content, isExpanded);
        header.addEventListener('click', () => {
            const wasExpanded = content.classList.contains('vb-debug-section-content--expanded');
            const nowExpanded = !wasExpanded;
            this.updateSectionState(toggle, content, nowExpanded);
            if (nowExpanded) {
                this.collapsedSections.delete(sectionId);
            }
            else {
                this.collapsedSections.add(sectionId);
            }
        });
        container.appendChild(header);
        container.appendChild(content);
        return { container, content };
    }
    updateSectionState(toggle, content, expanded) {
        if (expanded) {
            toggle.classList.add('vb-debug-section-toggle--expanded');
            content.classList.remove('vb-debug-section-content--collapsed');
            content.classList.add('vb-debug-section-content--expanded');
        }
        else {
            toggle.classList.remove('vb-debug-section-toggle--expanded');
            content.classList.remove('vb-debug-section-content--expanded');
            content.classList.add('vb-debug-section-content--collapsed');
        }
    }
    createRow(label, value, color) {
        const row = this.create('div', 'vb-debug-row');
        const labelSpan = this.create('span', 'vb-debug-label', `${label}`);
        const valueSpan = this.create('span', 'vb-debug-value', this.formatValue(value));
        if (color) {
            valueSpan.style.color = color;
        }
        row.appendChild(labelSpan);
        row.appendChild(valueSpan);
        return row;
    }
    createConditionalRow(condition, label, value, color) {
        return condition ? this.createRow(label, value, color) : null;
    }
    createOverlay(onClose) {
        DebugOverlayCSS.injectStyles(this.theme);
        const overlay = this.create('div');
        overlay.id = 'vb-debug-overlay';
        const header = this.create('div', 'vb-debug-header');
        const title = this.create('div', 'vb-debug-title', 'Virtual Background Debug');
        const closeButton = this.create('button', 'vb-debug-close', '✕');
        closeButton.title = 'Close Debug Overlay';
        closeButton.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            onClose();
        });
        header.appendChild(title);
        header.appendChild(closeButton);
        const content = this.create('div', 'vb-debug-content');
        const statsContainer = this.create('div');
        statsContainer.id = 'vb-debug-stats';
        const instructions = this.create('div', 'vb-debug-instructions', '<strong>Ctrl+Shift+F12</strong> to toggle');
        content.appendChild(statsContainer);
        content.appendChild(instructions);
        const resizeHandle = this.create('div', 'vb-debug-resize-handle');
        overlay.appendChild(header);
        overlay.appendChild(content);
        overlay.appendChild(resizeHandle);
        return { overlay, statsContainer, header, resizeHandle };
    }
    appendElements(container, elements) {
        elements.forEach((element) => {
            if (element) {
                container.appendChild(element);
            }
        });
    }
    formatValue(value) {
        if (typeof value === 'boolean') {
            return value ? 'Yes' : 'No';
        }
        if (typeof value === 'number') {
            return value.toFixed(1);
        }
        if (typeof value === 'object' && value !== null) {
            return JSON.stringify(value);
        }
        return String(value);
    }
}
class DebugOverlayStyles {
    constructor(theme = defaultTheme) {
        this.theme = theme;
    }
    getStatusColor(value) {
        return value ? this.theme.colors.success : this.theme.colors.error;
    }
    getPerformanceColor(value, threshold) {
        return value > threshold ? this.theme.colors.error : this.theme.colors.success;
    }
    getFPSColor(fps, targetFps = 30) {
        const percentage = (fps / targetFps) * 100;
        if (percentage >= 90)
            return this.theme.colors.success;
        if (percentage >= 70)
            return this.theme.colors.warning;
        return this.theme.colors.error;
    }
    getFrameTimeColor(frameTime, targetFps = 30) {
        const expectedFrameTime = 1000 / targetFps;
        const ratio = frameTime / expectedFrameTime;
        if (ratio <= 1.1)
            return this.theme.colors.success;
        if (ratio <= 1.4)
            return this.theme.colors.warning;
        return this.theme.colors.error;
    }
    getPressureColor(state) {
        switch (state.toLowerCase()) {
            case 'critical':
                return this.theme.colors.error;
            case 'serious':
                return this.theme.colors.warning;
            case 'fair':
                return this.theme.colors.warning;
            case 'nominal':
                return this.theme.colors.success;
            case 'unknown':
                return this.theme.colors.muted;
            default:
                return this.theme.colors.muted;
        }
    }
}

class DebugOverlay extends EventEmitter {
    constructor(options) {
        super();
        this.sectionsCreated = false;
        this.sectionElements = new Map();
        this.isVisible = false;
        this.isResizing = false;
        this.startX = 0;
        this.startY = 0;
        this.startWidth = 400;
        this.startHeight = 500;
        this.stats = DebugOverlay.getDefaultStats();
        this.options = Object.assign({ showOnInit: false, updateInterval: 250, maxErrors: 10, disableKeyboardShortcut: false }, options);
        this.dom = new DebugOverlayDOM();
        this.styles = new DebugOverlayStyles();
        this.createOverlay();
        this.setupPerformanceMonitoring();
        if (!this.options.disableKeyboardShortcut) {
            this.setupKeyboardListener();
        }
        if (this.options.showOnInit) {
            this.show();
        }
    }
    show() {
        if (this.overlay && !this.isVisible) {
            this.overlay.style.display = 'block';
            this.isVisible = true;
            this.updateDisplayStats();
            this.startUpdateInterval();
        }
    }
    hide() {
        if (this.overlay && this.isVisible) {
            this.overlay.style.display = 'none';
            this.isVisible = false;
            this.stopUpdateInterval();
        }
    }
    toggle() {
        if (this.isVisible) {
            this.hide();
        }
        else {
            this.show();
        }
    }
    get visible() {
        return this.isVisible;
    }
    setStatsProvider(getStatsCallback) {
        this.getStats = getStatsCallback;
        this.resetSections();
        this.updateDisplayStats();
        if (this.isVisible) {
            this.startUpdateInterval();
        }
        logger.log('debug overlay: attached stats callback');
    }
    updateDisplayStats() {
        if (!this.getStats) {
            return;
        }
        let statsCollectionFailed = false;
        try {
            this.stats = this.getStats();
        }
        catch (error) {
            logger.warn('debug overlay: error updating stats', error);
            this.stats = DebugOverlay.getDefaultStats();
            statsCollectionFailed = true;
        }
        if (this.isVisible) {
            this.renderStats();
            if (statsCollectionFailed) {
                this.showStatsError();
            }
            else {
                this.hideStatsError();
            }
        }
    }
    showStatsError() {
        if (!this.statsContainer)
            return;
        const existingError = this.statsContainer.querySelector('.vb-debug-error-indicator');
        if (existingError) {
            existingError.remove();
        }
        const errorSection = this.dom.create('div', 'vb-debug-section');
        errorSection.appendChild(this.dom.create('hr', 'vb-debug-divider'));
        const errorTitle = this.dom.create('div', 'vb-debug-section-title', 'Stats Collection Failed');
        errorTitle.style.color = this.styles.getStatusColor(false);
        const errorMessage = this.dom.create('div', 'vb-debug-error-message', 'Unable to collect performance statistics from the effect. The overlay will show default values only.');
        errorMessage.className = 'vb-debug-error-indicator';
        errorMessage.style.color = this.styles.getStatusColor(false);
        errorMessage.style.fontSize = '11px';
        errorMessage.style.padding = '8px';
        errorMessage.style.backgroundColor = 'rgba(239, 68, 68, 0.1)';
        errorMessage.style.borderRadius = '4px';
        errorMessage.style.border = `1px solid ${this.styles.getStatusColor(false)}`;
        errorMessage.style.marginBottom = '8px';
        errorSection.appendChild(errorTitle);
        errorSection.appendChild(errorMessage);
        this.statsContainer.insertBefore(errorSection, this.statsContainer.firstChild);
    }
    hideStatsError() {
        if (!this.statsContainer)
            return;
        const errorIndicator = this.statsContainer.querySelector('.vb-debug-error-indicator');
        if (errorIndicator) {
            const errorSection = errorIndicator.closest('.vb-debug-section');
            if (errorSection) {
                errorSection.remove();
            }
        }
    }
    startUpdateInterval() {
        if (this.updateInterval) {
            clearInterval(this.updateInterval);
        }
        this.updateInterval = setInterval(() => {
            this.updateDisplayStats();
        }, this.options.updateInterval);
    }
    stopUpdateInterval() {
        if (this.updateInterval) {
            clearInterval(this.updateInterval);
            this.updateInterval = undefined;
        }
    }
    static getDefaultStats() {
        return {
            frameRate: 0,
            modelReady: false,
            effectEnabled: false,
            trackEnabled: false,
            inputDimensions: { width: 0, height: 0 },
            outputDimensions: { width: 0, height: 0 },
            mode: 'unknown',
            quality: 'unknown',
            frameInterval: 0,
            lastFrameTime: 0,
            pluginCount: 0,
            errors: [],
            cpuCores: navigator.hardwareConcurrency || 0,
            imageCaptureActive: false,
            trackProcessorActive: false,
        };
    }
    createOverlay() {
        const { overlay, statsContainer, header, resizeHandle } = this.dom.createOverlay(() => this.hide());
        this.overlay = overlay;
        this.statsContainer = statsContainer;
        this.header = header;
        this.resizeHandle = resizeHandle;
        this.setupDragToMove();
        this.setupResizeHandlers();
        document.body.appendChild(this.overlay);
    }
    createStatsSections() {
        if (!this.statsContainer || this.sectionsCreated)
            return;
        this.statsContainer.innerHTML = '';
        const statusSection = this.dom.createCollapsibleSection('Status', 'status', true);
        this.sectionElements.set('status', statusSection);
        this.statsContainer.appendChild(statusSection.container);
        const videoSection = this.dom.createCollapsibleSection('Video', 'video', true);
        this.sectionElements.set('video', videoSection);
        this.statsContainer.appendChild(videoSection.container);
        const inferenceSection = this.dom.createCollapsibleSection('Inference', 'inference', true);
        this.sectionElements.set('inference', inferenceSection);
        this.statsContainer.appendChild(inferenceSection.container);
        const performanceSection = this.dom.createCollapsibleSection('Performance', 'performance', true);
        this.sectionElements.set('performance', performanceSection);
        this.statsContainer.appendChild(performanceSection.container);
        const systemSection = this.dom.createCollapsibleSection('System', 'system', true);
        this.sectionElements.set('system', systemSection);
        this.statsContainer.appendChild(systemSection.container);
        this.sectionsCreated = true;
    }
    renderStats() {
        var _a;
        if (!this.statsContainer)
            return;
        this.createStatsSections();
        const now = Date.now();
        const timeSinceLastFrame = now - this.stats.lastFrameTime;
        const statusSection = this.sectionElements.get('status');
        if (statusSection) {
            this.updateSectionContent(statusSection.content, [
                {
                    label: 'Model Ready',
                    value: this.stats.modelReady,
                    color: this.styles.getStatusColor(this.stats.modelReady),
                },
                {
                    label: 'Effect Enabled',
                    value: this.stats.effectEnabled,
                    color: this.styles.getStatusColor(this.stats.effectEnabled),
                },
                {
                    label: 'Track Enabled',
                    value: this.stats.trackEnabled,
                    color: this.styles.getStatusColor(this.stats.trackEnabled),
                },
                {
                    label: 'Image Capture',
                    value: this.stats.imageCaptureActive,
                    color: this.styles.getStatusColor(this.stats.imageCaptureActive),
                },
                {
                    label: 'Track Processor',
                    value: this.stats.trackProcessorActive,
                    color: this.styles.getStatusColor(this.stats.trackProcessorActive),
                },
            ]);
        }
        const performanceSection = this.sectionElements.get('performance');
        if (performanceSection) {
            this.updateSectionContent(performanceSection.content, [
                {
                    label: 'Compute Pressure',
                    value: this.stats.computePressure
                        ? `${this.stats.computePressure.state} (${this.stats.computePressure.source})`
                        : 'Not available',
                    color: this.stats.computePressure
                        ? this.styles.getPressureColor(this.stats.computePressure.state)
                        : this.styles.getPressureColor('nominal'),
                },
                { label: 'CPU Cores', value: this.stats.cpuCores },
                {
                    label: 'Device Memory',
                    value: this.stats.deviceMemory ? `${this.stats.deviceMemory} GB` : 'Unknown',
                },
                {
                    label: 'Memory Usage',
                    value: this.stats.memoryUsage ? `${this.stats.memoryUsage} MB` : 'Unknown',
                },
            ]);
        }
        const videoSection = this.sectionElements.get('video');
        if (videoSection) {
            this.updateSectionContent(videoSection.content, [
                {
                    label: 'Input Resolution',
                    value: this.stats.videoWidth && this.stats.videoHeight
                        ? `${this.stats.videoWidth}x${this.stats.videoHeight}`
                        : 'Unknown',
                },
                {
                    label: 'Target FPS',
                    value: `${this.stats.frameRate} fps`,
                    color: this.styles.getStatusColor(true),
                },
                {
                    label: 'Actual FPS',
                    value: this.stats.averageFrameTime
                        ? `${(1000 / this.stats.averageFrameTime).toFixed(1)} fps`
                        : 'Calculating...',
                    color: this.stats.averageFrameTime
                        ? this.styles.getFPSColor(1000 / this.stats.averageFrameTime, this.stats.frameRate)
                        : undefined,
                },
                { label: 'Target Frame Time', value: `${this.stats.frameInterval.toFixed(1)} ms` },
                {
                    label: 'Actual Frame Time',
                    value: this.stats.averageFrameTime
                        ? `${this.stats.averageFrameTime.toFixed(1)} ms`
                        : 'Calculating...',
                    color: this.stats.averageFrameTime
                        ? this.styles.getFrameTimeColor(this.stats.averageFrameTime, this.stats.frameRate)
                        : undefined,
                },
                {
                    label: 'Frame Drops',
                    value: (_a = this.stats.frameDrops) !== null && _a !== void 0 ? _a : 'Calculating...',
                    color: this.stats.frameDrops !== undefined
                        ? this.styles.getPerformanceColor(this.stats.frameDrops, 5)
                        : undefined,
                },
                {
                    label: 'Last Frame',
                    value: this.stats.lastFrameTime > 0 ? `${timeSinceLastFrame} ms ago` : 'No frames yet',
                    color: this.stats.lastFrameTime > 0
                        ? timeSinceLastFrame > 100
                            ? this.styles.getPressureColor('critical')
                            : this.styles.getPressureColor('nominal')
                        : this.styles.getPressureColor('unknown'),
                },
            ]);
        }
        const systemSection = this.sectionElements.get('system');
        if (systemSection) {
            this.updateSectionContent(systemSection.content, [
                {
                    label: 'Input Size',
                    value: `${this.stats.inputDimensions.width}x${this.stats.inputDimensions.height}`,
                },
                {
                    label: 'Output Size',
                    value: `${this.stats.outputDimensions.width}x${this.stats.outputDimensions.height}`,
                },
                {
                    label: 'Max Texture',
                    value: this.stats.maxTextureSize ? `${this.stats.maxTextureSize}px` : 'Unknown',
                },
                {
                    label: 'Connection',
                    value: this.stats.effectiveType || this.stats.connectionType || 'Unknown',
                },
                {
                    label: 'Memory Usage',
                    value: this.stats.memoryUsage ? `${this.stats.memoryUsage} MB` : 'Unknown',
                },
                { label: 'Plugin Count', value: this.stats.pluginCount },
                { label: 'GPU', value: this.stats.webglRenderer || 'Unknown' },
            ]);
        }
        const inferenceSection = this.sectionElements.get('inference');
        if (inferenceSection) {
            this.updateSectionContent(inferenceSection.content, [
                { label: 'Mode', value: this.stats.mode },
                { label: 'Quality', value: this.stats.quality },
                {
                    label: 'Total Pipeline',
                    value: this.stats.totalFrameTime
                        ? `${this.stats.totalFrameTime.toFixed(1)} ms${this.stats.inferenceSkipped ? ' (skipped)' : ''}`
                        : 'No data',
                    color: this.stats.totalFrameTime
                        ? this.styles.getFrameTimeColor(this.stats.totalFrameTime, this.stats.frameRate)
                        : undefined,
                },
                {
                    label: 'Model Inference',
                    value: this.stats.modelInferenceTime
                        ? `${this.stats.modelInferenceTime.toFixed(1)} ms`
                        : 'No data',
                    color: this.stats.modelInferenceTime
                        ? this.styles.getFrameTimeColor(this.stats.modelInferenceTime, this.stats.frameRate)
                        : undefined,
                },
                {
                    label: 'Preprocessing',
                    value: this.stats.preprocessingTime
                        ? `${this.stats.preprocessingTime.toFixed(1)} ms`
                        : 'No data',
                },
                {
                    label: 'Postprocessing',
                    value: this.stats.postprocessingTime
                        ? `${this.stats.postprocessingTime.toFixed(1)} ms`
                        : 'No data',
                },
                {
                    label: 'Model Max FPS',
                    value: this.stats.modelInferenceFPS
                        ? `${this.stats.modelInferenceFPS.toFixed(1)} fps`
                        : 'Calculating...',
                    color: this.stats.modelInferenceFPS
                        ? this.styles.getFPSColor(this.stats.modelInferenceFPS, this.stats.frameRate)
                        : undefined,
                },
            ]);
        }
    }
    updateSectionContent(sectionContent, rows) {
        const existingRows = Array.from(sectionContent.children);
        rows.forEach((rowData, index) => {
            let row = existingRows[index];
            if (!row) {
                row = this.dom.createRow(rowData.label, rowData.value, rowData.color);
                sectionContent.appendChild(row);
            }
            else {
                const labelElement = row.querySelector('.vb-debug-label');
                const valueElement = row.querySelector('.vb-debug-value');
                if (labelElement) {
                    labelElement.textContent = rowData.label;
                }
                if (valueElement) {
                    valueElement.textContent = this.dom.formatValue(rowData.value);
                    valueElement.style.color = rowData.color || '';
                }
            }
        });
        while (sectionContent.children.length > rows.length) {
            if (sectionContent.lastChild) {
                sectionContent.removeChild(sectionContent.lastChild);
            }
        }
    }
    resetSections() {
        this.sectionsCreated = false;
        try {
            this.sectionElements.clear();
        }
        catch (error) {
            this.sectionElements = new Map();
        }
        if (this.statsContainer) {
            this.statsContainer.innerHTML = '';
        }
    }
    setupKeyboardListener() {
        document.addEventListener('keydown', (event) => {
            if (event.ctrlKey && event.shiftKey && event.key === 'F12') {
                event.preventDefault();
                this.toggle();
            }
        });
    }
    setupPerformanceMonitoring() {
        if ('PressureObserver' in window && window.PressureObserver) {
            try {
                this.pressureObserver = new window.PressureObserver((records) => {
                    const latestRecord = records[records.length - 1];
                    const validStates = ['nominal', 'fair', 'serious', 'critical'];
                    const state = validStates.includes(latestRecord.state)
                        ? latestRecord.state
                        : 'nominal';
                    this.stats.computePressure = {
                        state,
                        source: latestRecord.source,
                    };
                    if (this.isVisible) {
                        this.renderStats();
                    }
                });
                this.pressureObserver.observe('cpu');
            }
            catch (error) {
                logger.debug('Compute Pressure API not available or failed to initialize');
            }
        }
    }
    setupDragToMove() {
        if (!this.header || !this.overlay)
            return;
        let isDragging = false;
        let startX = 0;
        let startY = 0;
        let initialLeft = 0;
        let initialTop = 0;
        this.header.addEventListener('mousedown', (e) => {
            var _a;
            if (e.target !== this.header && !((_a = this.header) === null || _a === void 0 ? void 0 : _a.contains(e.target)))
                return;
            if (e.target.tagName === 'BUTTON')
                return;
            isDragging = true;
            startX = e.clientX;
            startY = e.clientY;
            if (!this.overlay)
                return;
            const rect = this.overlay.getBoundingClientRect();
            initialLeft = rect.left;
            initialTop = rect.top;
            document.body.style.cursor = 'move';
            e.preventDefault();
        });
        document.addEventListener('mousemove', (e) => {
            if (!isDragging || !this.overlay)
                return;
            const deltaX = e.clientX - startX;
            const deltaY = e.clientY - startY;
            const newLeft = Math.max(0, initialLeft + deltaX);
            const newTop = Math.max(0, initialTop + deltaY);
            this.overlay.style.left = `${newLeft}px`;
            this.overlay.style.top = `${newTop}px`;
            this.overlay.style.right = 'auto';
        });
        document.addEventListener('mouseup', () => {
            if (isDragging) {
                isDragging = false;
                document.body.style.cursor = '';
            }
        });
    }
    setupResizeHandlers() {
        if (!this.resizeHandle || !this.overlay)
            return;
        this.resizeHandle.addEventListener('mousedown', (e) => {
            if (!this.overlay)
                return;
            this.isResizing = true;
            this.startX = e.clientX;
            this.startY = e.clientY;
            this.startWidth = this.overlay.offsetWidth;
            this.startHeight = this.overlay.offsetHeight;
            document.body.style.cursor = 'nw-resize';
            e.preventDefault();
            e.stopPropagation();
        });
        document.addEventListener('mousemove', (e) => {
            if (!this.isResizing || !this.overlay)
                return;
            const newWidth = Math.max(300, this.startWidth + (e.clientX - this.startX));
            const newHeight = Math.max(300, this.startHeight + (e.clientY - this.startY));
            this.overlay.style.width = `${newWidth}px`;
            this.overlay.style.height = `${newHeight}px`;
        });
        document.addEventListener('mouseup', () => {
            if (this.isResizing) {
                this.isResizing = false;
                document.body.style.cursor = '';
            }
        });
    }
    dispose() {
        var _a;
        this.stopUpdateInterval();
        if (this.pressureObserver) {
            try {
                this.pressureObserver.disconnect();
            }
            catch (error) {
            }
        }
        DebugOverlayCSS.removeStyles();
        (_a = this.overlay) === null || _a === void 0 ? void 0 : _a.remove();
        this.removeAllListeners();
        document.body.style.cursor = '';
        logger.log('debug overlay: disposed');
    }
}

const DEFAULT_DEBUG_OVERLAY_OPTIONS = {
    debug: false,
    coreOptions: {
        showOnInit: false,
        updateInterval: 250,
        maxErrors: 10,
    },
};

class DebugOverlayPlugin extends BaseAfterInferencePlugin {
    getDefaultOptions() {
        return DEFAULT_DEBUG_OVERLAY_OPTIONS;
    }
    createCore() {
        return new DebugOverlay(this.options.coreOptions);
    }
    onCoreCreated() {
        if (this.options.debug) {
            logger.log('debug overlay: initializing with options:', this.options);
        }
    }
    initialize(effect) {
        try {
            logger.log('debug overlay: starting plugin initialization...');
            super.initialize(effect);
            logger.log('debug overlay: base plugin initialized, attaching to effect...');
            this.core.setStatsProvider(() => effect.getStats());
            if (this.options.debug) {
                logger.log('debug overlay: plugin initialized and attached to effect');
            }
            logger.log('debug overlay: 🎥 Virtual Background Debug Plugin initialized');
            logger.log('debug overlay: Press Ctrl+Shift+F12 to toggle debug overlay');
        }
        catch (error) {
            logger.error('debug overlay: failed to initialize plugin:', error);
            throw error;
        }
    }
    getDebugOverlay() {
        return this.core;
    }
    show() {
        this.core.show();
    }
    hide() {
        this.core.hide();
    }
    toggle() {
        this.core.toggle();
    }
    get visible() {
        return this.core.visible;
    }
    onAfterInference(timestamp, inferenceResult) {
        return __awaiter(this, void 0, void 0, function* () {
        });
    }
    dispose() {
        if (this.options.debug) {
            logger.log('debug overlay: disposing plugin and cleaning up overlay');
        }
        this.core.dispose();
        super.dispose();
        if (this.options.debug) {
            logger.log('debug overlay: plugin disposed successfully');
        }
    }
}

var FrameSkipperInferenceReason;
(function (FrameSkipperInferenceReason) {
    FrameSkipperInferenceReason["Initial"] = "initial";
    FrameSkipperInferenceReason["HighMotion"] = "high-motion";
    FrameSkipperInferenceReason["TimeForced"] = "time-forced";
    FrameSkipperInferenceReason["SkipIntervalElapsed"] = "skip-interval-elapsed";
})(FrameSkipperInferenceReason || (FrameSkipperInferenceReason = {}));
var FrameSkipperPluginMode;
(function (FrameSkipperPluginMode) {
    FrameSkipperPluginMode["Off"] = "off";
    FrameSkipperPluginMode["Minimal"] = "minimal";
    FrameSkipperPluginMode["Conservative"] = "conservative";
    FrameSkipperPluginMode["Aggressive"] = "aggressive";
})(FrameSkipperPluginMode || (FrameSkipperPluginMode = {}));

const defaultOptions$1 = {
    baseMaxSkipTime: 1000,
    baseMinSkipTime: 100,
    forcedInferenceInterval: 1000,
    highMotionThreshold: 0.1,
    historySize: 10,
    skipTimeIncrement: 100,
    smoothingFactor: 0.8,
};
class AdaptiveFrameSkipper extends EventEmitter {
    constructor(options = defaultOptions$1) {
        super();
        this.options = options;
        this.debugBuffer = new DebugBuffer();
        this.motionData = [];
        this.lastInferenceTime = Date.now();
        this.lastSmoothedMotion = 0;
        this.currentSkipTime = this.options.baseMaxSkipTime;
    }
    destroy() {
        this.motionData = [];
        this.lastInferenceTime = 0;
        this.currentSkipTime = 0;
        this.debugBuffer.clear();
    }
    getDebugInfo() {
        return this.debugBuffer.getDebugInfo();
    }
    getLatestDebugInfo() {
        return this.debugBuffer.getLatestDebugInfo();
    }
    shouldPerformInference(motionValue, motionTimestamp) {
        var _a;
        if (!motionValue || !motionTimestamp) {
            return true;
        }
        const smoothedMotion = this.calculateSmoothedMotion(motionValue);
        this.updateMotionData(smoothedMotion, motionTimestamp);
        const averageMotion = this.calculateAverageMotion();
        const currentTime = Date.now();
        const timeSinceLastInference = currentTime - this.lastInferenceTime;
        this.adjustSkipTime(averageMotion);
        const isHighMotion = averageMotion < this.options.highMotionThreshold;
        const isTimeForced = timeSinceLastInference >= this.options.forcedInferenceInterval;
        const isSkipIntervalElapsed = timeSinceLastInference >= this.currentSkipTime;
        const shouldInfer = isHighMotion || isTimeForced || isSkipIntervalElapsed;
        if (shouldInfer) {
            this.lastInferenceTime = currentTime;
        }
        {
            const lastReason = (_a = this.getLatestDebugInfo()) === null || _a === void 0 ? void 0 : _a.lastInferenceReason;
            let lastInferenceReason = lastReason || FrameSkipperInferenceReason.Initial;
            if (isHighMotion) {
                lastInferenceReason = FrameSkipperInferenceReason.HighMotion;
            }
            else if (isTimeForced) {
                lastInferenceReason = FrameSkipperInferenceReason.TimeForced;
            }
            else if (isSkipIntervalElapsed) {
                lastInferenceReason = FrameSkipperInferenceReason.SkipIntervalElapsed;
            }
            this.debugBuffer.add({
                averageMotion,
                currentTime,
                currentSkipTime: this.currentSkipTime,
                isHighMotion,
                isSkipIntervalElapsed,
                isTimeForced,
                lastInferenceReason,
                lastInferenceTime: this.lastInferenceTime,
                motionValue,
                shouldInfer,
                smoothedMotion,
                timeSinceLastInference,
            });
        }
        return shouldInfer;
    }
    updateOptions(newOptions) {
        this.options = Object.assign(Object.assign({}, this.options), newOptions);
        this.currentSkipTime = this.options.baseMaxSkipTime;
        this.motionData = [];
    }
    updateMotionData(value, timestamp) {
        this.motionData.push({ value, timestamp });
        if (this.motionData.length > this.options.historySize) {
            this.motionData.shift();
        }
    }
    adjustSkipTime(averageMotion) {
        const adjustmentDirection = averageMotion < this.options.highMotionThreshold ? -1 : 1;
        const adjustmentAmount = this.options.skipTimeIncrement * adjustmentDirection;
        this.currentSkipTime = Math.max(this.options.baseMinSkipTime, Math.min(this.options.baseMaxSkipTime, this.currentSkipTime + adjustmentAmount));
    }
    calculateSmoothedMotion(currentMotion) {
        this.lastSmoothedMotion =
            this.options.smoothingFactor * currentMotion +
                (1 - this.options.smoothingFactor) * this.lastSmoothedMotion;
        return this.lastSmoothedMotion;
    }
    calculateAverageMotion() {
        return this.motionData.reduce((acc, data) => acc + data.value, 0) / this.motionData.length;
    }
}

class BaseBeforeInferencePlugin extends BaseEventEmitterPlugin {
    constructor(options) {
        super(options);
        this.onBeforeInference = this.onBeforeInference.bind(this);
    }
    initialize(effect) {
        super.initialize(effect);
        effect.addBeforeInferenceCallback(this.onBeforeInference);
    }
}

const modesMap = {
    [FrameSkipperPluginMode.Off]: {
        baseMinSkipTime: 0,
        baseMaxSkipTime: 0,
        historySize: 0,
        skipTimeIncrement: 0,
        forcedInferenceInterval: 0,
        highMotionThreshold: 0,
        smoothingFactor: 0,
    },
    [FrameSkipperPluginMode.Minimal]: {
        baseMinSkipTime: 0,
        baseMaxSkipTime: 125,
        historySize: 20,
        skipTimeIncrement: 50,
        forcedInferenceInterval: 150,
        highMotionThreshold: 0.97,
        smoothingFactor: 0.1,
    },
    [FrameSkipperPluginMode.Conservative]: {
        baseMinSkipTime: 50,
        baseMaxSkipTime: 250,
        historySize: 25,
        skipTimeIncrement: 50,
        forcedInferenceInterval: 300,
        highMotionThreshold: 0.96,
        smoothingFactor: 0.5,
    },
    [FrameSkipperPluginMode.Aggressive]: {
        baseMinSkipTime: 150,
        baseMaxSkipTime: 350,
        historySize: 20,
        skipTimeIncrement: 50,
        forcedInferenceInterval: 500,
        highMotionThreshold: 0.94,
        smoothingFactor: 0.9,
    },
};
function makeFrameSkipperOptions(mode, coreOptions) {
    const defaultMode = FrameSkipperPluginMode.Conservative;
    return getPluginOptions(mode, modesMap, defaultMode, coreOptions);
}

class FrameSkipperPlugin extends BaseBeforeInferencePlugin {
    getDefaultOptions() {
        return {
            mode: FrameSkipperPluginMode.Conservative,
        };
    }
    createCore() {
        const options = makeFrameSkipperOptions(this.options.mode, this.options.coreOptions);
        return new AdaptiveFrameSkipper(options);
    }
    destroyCore() {
        var _a;
        (_a = this.core) === null || _a === void 0 ? void 0 : _a.destroy();
    }
    onBeforeInference(timestamp, lastResult) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            let shouldInfer = true;
            if (this.options.mode !== FrameSkipperPluginMode.Off) {
                shouldInfer = (_b = (_a = this.core) === null || _a === void 0 ? void 0 : _a.shouldPerformInference(lastResult.motion, timestamp)) !== null && _b !== void 0 ? _b : true;
            }
            return { shouldInfer };
        });
    }
    onOptionsUpdated() {
        var _a;
        const options = makeFrameSkipperOptions(this.options.mode, this.options.coreOptions);
        (_a = this.core) === null || _a === void 0 ? void 0 : _a.updateOptions(options);
    }
    getDebugInfo() {
        var _a, _b;
        return (_b = (_a = this.core) === null || _a === void 0 ? void 0 : _a.getDebugInfo()) !== null && _b !== void 0 ? _b : [];
    }
    getLatestDebugInfo() {
        var _a;
        return (_a = this.core) === null || _a === void 0 ? void 0 : _a.getLatestDebugInfo();
    }
}

var RateEstimatorStatus;
(function (RateEstimatorStatus) {
    RateEstimatorStatus["Idle"] = "idle";
    RateEstimatorStatus["Init"] = "init";
    RateEstimatorStatus["Lagging"] = "lagging";
    RateEstimatorStatus["Low"] = "low";
    RateEstimatorStatus["Ok"] = "ok";
})(RateEstimatorStatus || (RateEstimatorStatus = {}));
var RateEstimatorEvent;
(function (RateEstimatorEvent) {
    RateEstimatorEvent["RateOk"] = "rate-ok";
    RateEstimatorEvent["RateLow"] = "rate-low";
    RateEstimatorEvent["RateLagging"] = "rate-lagging";
})(RateEstimatorEvent || (RateEstimatorEvent = {}));

const makeRateEstimatorDefaultOptions = (targetRate) => Object.freeze({
    targetRate,
    hysteresisMargin: 0.05,
    lowDuration: 5,
    lowThreshold: targetRate * 0.8,
    minSamples: Math.max(30, targetRate),
    maxSamples: Math.max(120, targetRate * 4),
});

class RateEstimator extends EventEmitter {
    constructor(targetRate, options) {
        super();
        this.timestamps = [];
        this.status = RateEstimatorStatus.Idle;
        if (typeof targetRate !== 'number' || targetRate <= 0) {
            throw new Error('Invalid target rate: must be a positive number.');
        }
        const defaultOptions = makeRateEstimatorDefaultOptions(targetRate);
        this.options = Object.assign(Object.assign({}, defaultOptions), options);
    }
    dispose() {
        this.removeAllListeners();
        this.status = RateEstimatorStatus.Idle;
        this.estimatedRate = undefined;
        this.lowRateStartTime = undefined;
        this.timestamps = [];
    }
    getEstimatedValues() {
        return {
            rate: this.estimatedRate,
            status: this.status,
            threshold: this.options.lowThreshold,
            targetRate: this.options.targetRate,
        };
    }
    getOptions() {
        return this.options;
    }
    getTimestamps() {
        return this.timestamps;
    }
    addTimestamp(timestamp) {
        if (this.status === RateEstimatorStatus.Idle) {
            this.status = RateEstimatorStatus.Init;
        }
        this.timestamps.unshift(timestamp);
        if (this.timestamps.length > this.options.maxSamples) {
            this.timestamps.pop();
        }
        if (this.timestamps.length >= this.options.minSamples) {
            this.calculateAndUpdateRate();
            this.evaluateRate(timestamp);
        }
    }
    calculateAndUpdateRate() {
        const sampleCount = this.timestamps.length - 1;
        const timeDifference = (this.timestamps[0] - this.timestamps[sampleCount]) / 1000;
        const estimatedRate = sampleCount / timeDifference;
        this.estimatedRate = Number(estimatedRate.toFixed(1));
    }
    evaluateRate(timestamp) {
        if (this.isRateLow()) {
            this.handleLowRate(timestamp);
            return;
        }
        if (this.lowRateStartTime) {
            this.handleReturnToNormalRate();
            return;
        }
        this.status = RateEstimatorStatus.Ok;
    }
    isRateLow() {
        const { hysteresisMargin, lowThreshold } = this.options;
        if (!this.estimatedRate) {
            return false;
        }
        const tolerance = lowThreshold * hysteresisMargin;
        const lowerBound = lowThreshold - tolerance;
        const upperBound = lowThreshold + tolerance;
        if (this.status === RateEstimatorStatus.Ok || this.status === RateEstimatorStatus.Init) {
            return this.estimatedRate < lowerBound;
        }
        return this.estimatedRate < upperBound;
    }
    handleLowRate(timestamp) {
        var _a;
        if (this.estimatedRate === undefined) {
            return;
        }
        if (this.status === RateEstimatorStatus.Lagging) {
            return;
        }
        if (this.status !== RateEstimatorStatus.Low) {
            this.emit(RateEstimatorEvent.RateLow, this.estimatedRate);
            this.status = RateEstimatorStatus.Low;
            (_a = this.lowRateStartTime) !== null && _a !== void 0 ? _a : (this.lowRateStartTime = timestamp);
            return;
        }
        if (this.status === RateEstimatorStatus.Low) {
            const timeElapsed = timestamp - (this.lowRateStartTime || 0);
            if (timeElapsed >= this.options.lowDuration * 1000) {
                this.emit(RateEstimatorEvent.RateLagging, this.estimatedRate);
                this.status = RateEstimatorStatus.Lagging;
            }
        }
    }
    handleReturnToNormalRate() {
        if (this.estimatedRate !== undefined) {
            this.emit(RateEstimatorEvent.RateOk, this.estimatedRate);
            this.status = RateEstimatorStatus.Ok;
            this.lowRateStartTime = undefined;
        }
    }
    reset() {
        this.timestamps = [];
        this.estimatedRate = undefined;
        this.lowRateStartTime = undefined;
        this.status = RateEstimatorStatus.Idle;
    }
    updateOptions(newOptions) {
        this.options = Object.assign(Object.assign({}, this.options), newOptions);
    }
}

class RateEstimationPlugin extends BaseAfterInferencePlugin {
    createCore() {
        var _a;
        return new RateEstimator(this.options.targetRate, (_a = this.options) === null || _a === void 0 ? void 0 : _a.coreOptions);
    }
    onCoreCreated() {
        var _a, _b, _c;
        const { targetRate } = this.options;
        if (this.options.debug) {
            logger.log('rate estimator: initializing with options:', this.options);
        }
        (_a = this.core) === null || _a === void 0 ? void 0 : _a.on(RateEstimatorEvent.RateLow, (rate) => {
            if (this.options.debug) {
                logger.log(`rate estimator: rate low, target = ${targetRate}, estimated = ${rate.toFixed(2)}`);
            }
        });
        (_b = this.core) === null || _b === void 0 ? void 0 : _b.on(RateEstimatorEvent.RateOk, (rate) => {
            if (this.options.debug) {
                logger.log(`rate estimator: rate returned to normal, target = ${targetRate}, estimated = ${rate.toFixed(2)}`);
            }
        });
        (_c = this.core) === null || _c === void 0 ? void 0 : _c.on(RateEstimatorEvent.RateLagging, (rate) => {
            if (this.options.debug) {
                logger.log(`rate estimator: rate lagging, target = ${targetRate}, estimated = ${rate.toFixed(2)}`);
            }
        });
    }
    onEffectDisabled() {
        var _a;
        (_a = this.core) === null || _a === void 0 ? void 0 : _a.reset();
    }
    onEffectEnabled() {
        var _a;
        (_a = this.core) === null || _a === void 0 ? void 0 : _a.reset();
    }
    destroyCore() {
        var _a;
        (_a = this.core) === null || _a === void 0 ? void 0 : _a.dispose();
    }
    onAfterInference(timestamp) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            (_a = this.core) === null || _a === void 0 ? void 0 : _a.addTimestamp(timestamp);
        });
    }
    onOptionsUpdated() {
        var _a;
        (_a = this.core) === null || _a === void 0 ? void 0 : _a.updateOptions(this.options);
    }
    getRate() {
        var _a, _b;
        return (_b = (_a = this.core) === null || _a === void 0 ? void 0 : _a.getEstimatedValues()) === null || _b === void 0 ? void 0 : _b.rate;
    }
    getThreshold() {
        var _a, _b;
        return (_b = (_a = this.core) === null || _a === void 0 ? void 0 : _a.getEstimatedValues()) === null || _b === void 0 ? void 0 : _b.threshold;
    }
    getStatus() {
        var _a;
        return (_a = this.core) === null || _a === void 0 ? void 0 : _a.status;
    }
    getTargetRate() {
        var _a, _b;
        return (_b = (_a = this.core) === null || _a === void 0 ? void 0 : _a.getEstimatedValues()) === null || _b === void 0 ? void 0 : _b.targetRate;
    }
}

class SkippedFrameRatePlugin extends BaseAfterInferencePlugin {
    constructor() {
        super(...arguments);
        this.frameHistory = [];
        this.frameWindowSize = 5000;
    }
    onAfterInference(timestamp, result) {
        return __awaiter(this, void 0, void 0, function* () {
            this.frameHistory.push({ timestamp, skipped: !!(result === null || result === void 0 ? void 0 : result.skipped) });
            this.frameHistory = this.frameHistory.filter((frame) => timestamp - frame.timestamp <= this.frameWindowSize);
        });
    }
    getSkippedPercentage() {
        const totalFrames = this.frameHistory.length;
        const skippedFrames = this.frameHistory.filter((frame) => frame.skipped).length;
        return totalFrames > 0 ? (skippedFrames / totalFrames) * 100 : 0;
    }
    getSkippedFrameCount() {
        return this.frameHistory.filter((frame) => frame.skipped).length;
    }
    getTotalFrameCount() {
        return this.frameHistory.length;
    }
}

var bundle = {exports: {}};

(function (module, exports) {
(function (global, factory) {
    factory(exports) ;
})(commonjsGlobal, (function (exports) {
    var createAddUniqueNumber = function createAddUniqueNumber(generateUniqueNumber) {
      return function (set) {
        var number = generateUniqueNumber(set);
        set.add(number);
        return number;
      };
    };

    var createCache = function createCache(lastNumberWeakMap) {
      return function (collection, nextNumber) {
        lastNumberWeakMap.set(collection, nextNumber);
        return nextNumber;
      };
    };

    /*
     * The value of the constant Number.MAX_SAFE_INTEGER equals (2 ** 53 - 1) but it
     * is fairly new.
     */
    var MAX_SAFE_INTEGER = Number.MAX_SAFE_INTEGER === undefined ? 9007199254740991 : Number.MAX_SAFE_INTEGER;
    var TWO_TO_THE_POWER_OF_TWENTY_NINE = 536870912;
    var TWO_TO_THE_POWER_OF_THIRTY = TWO_TO_THE_POWER_OF_TWENTY_NINE * 2;
    var createGenerateUniqueNumber = function createGenerateUniqueNumber(cache, lastNumberWeakMap) {
      return function (collection) {
        var lastNumber = lastNumberWeakMap.get(collection);
        /*
         * Let's try the cheapest algorithm first. It might fail to produce a new
         * number, but it is so cheap that it is okay to take the risk. Just
         * increase the last number by one or reset it to 0 if we reached the upper
         * bound of SMIs (which stands for small integers). When the last number is
         * unknown it is assumed that the collection contains zero based consecutive
         * numbers.
         */
        var nextNumber = lastNumber === undefined ? collection.size : lastNumber < TWO_TO_THE_POWER_OF_THIRTY ? lastNumber + 1 : 0;
        if (!collection.has(nextNumber)) {
          return cache(collection, nextNumber);
        }
        /*
         * If there are less than half of 2 ** 30 numbers stored in the collection,
         * the chance to generate a new random number in the range from 0 to 2 ** 30
         * is at least 50%. It's benifitial to use only SMIs because they perform
         * much better in any environment based on V8.
         */
        if (collection.size < TWO_TO_THE_POWER_OF_TWENTY_NINE) {
          while (collection.has(nextNumber)) {
            nextNumber = Math.floor(Math.random() * TWO_TO_THE_POWER_OF_THIRTY);
          }
          return cache(collection, nextNumber);
        }
        // Quickly check if there is a theoretical chance to generate a new number.
        if (collection.size > MAX_SAFE_INTEGER) {
          throw new Error('Congratulations, you created a collection of unique numbers which uses all available integers!');
        }
        // Otherwise use the full scale of safely usable integers.
        while (collection.has(nextNumber)) {
          nextNumber = Math.floor(Math.random() * MAX_SAFE_INTEGER);
        }
        return cache(collection, nextNumber);
      };
    };

    var LAST_NUMBER_WEAK_MAP = new WeakMap();
    var cache = createCache(LAST_NUMBER_WEAK_MAP);
    var generateUniqueNumber = createGenerateUniqueNumber(cache, LAST_NUMBER_WEAK_MAP);
    var addUniqueNumber = createAddUniqueNumber(generateUniqueNumber);

    exports.addUniqueNumber = addUniqueNumber;
    exports.generateUniqueNumber = generateUniqueNumber;

}));
}(bundle, bundle.exports));

const isMessagePort = (sender) => {
    return typeof sender.start === 'function';
};

const PORT_MAP = new WeakMap();

const extendBrokerImplementation = (partialBrokerImplementation) => ({
    ...partialBrokerImplementation,
    connect: ({ call }) => {
        return async () => {
            const { port1, port2 } = new MessageChannel();
            const portId = await call('connect', { port: port1 }, [port1]);
            PORT_MAP.set(port2, portId);
            return port2;
        };
    },
    disconnect: ({ call }) => {
        return async (port) => {
            const portId = PORT_MAP.get(port);
            if (portId === undefined) {
                throw new Error('The given port is not connected.');
            }
            await call('disconnect', { portId });
        };
    },
    isSupported: ({ call }) => {
        return () => call('isSupported');
    }
});

const ONGOING_REQUESTS = new WeakMap();
const createOrGetOngoingRequests = (sender) => {
    if (ONGOING_REQUESTS.has(sender)) {
        // @todo TypeScript needs to be convinced that has() works as expected.
        return ONGOING_REQUESTS.get(sender);
    }
    const ongoingRequests = new Map();
    ONGOING_REQUESTS.set(sender, ongoingRequests);
    return ongoingRequests;
};
const createBroker = (brokerImplementation) => {
    const fullBrokerImplementation = extendBrokerImplementation(brokerImplementation);
    return (sender) => {
        const ongoingRequests = createOrGetOngoingRequests(sender);
        sender.addEventListener('message', (({ data: message }) => {
            const { id } = message;
            if (id !== null && ongoingRequests.has(id)) {
                const { reject, resolve } = ongoingRequests.get(id);
                ongoingRequests.delete(id);
                if (message.error === undefined) {
                    resolve(message.result);
                }
                else {
                    reject(new Error(message.error.message));
                }
            }
        }));
        if (isMessagePort(sender)) {
            sender.start();
        }
        const call = (method, params = null, transferables = []) => {
            return new Promise((resolve, reject) => {
                const id = bundle.exports.generateUniqueNumber(ongoingRequests);
                ongoingRequests.set(id, { reject, resolve });
                if (params === null) {
                    sender.postMessage({ id, method }, transferables);
                }
                else {
                    sender.postMessage({ id, method, params }, transferables);
                }
            });
        };
        const notify = (method, params, transferables = []) => {
            sender.postMessage({ id: null, method, params }, transferables);
        };
        let functions = {};
        for (const [key, handler] of Object.entries(fullBrokerImplementation)) {
            functions = { ...functions, [key]: handler({ call, notify }) };
        }
        return { ...functions };
    };
};

// Prefilling the Maps with a function indexed by zero is necessary to be compliant with the specification.
const scheduledIntervalsState = new Map([[0, null]]); // tslint:disable-line no-empty
const scheduledTimeoutsState = new Map([[0, null]]); // tslint:disable-line no-empty
const wrap = createBroker({
    clearInterval: ({ call }) => {
        return (timerId) => {
            if (typeof scheduledIntervalsState.get(timerId) === 'symbol') {
                scheduledIntervalsState.set(timerId, null);
                call('clear', { timerId, timerType: 'interval' }).then(() => {
                    scheduledIntervalsState.delete(timerId);
                });
            }
        };
    },
    clearTimeout: ({ call }) => {
        return (timerId) => {
            if (typeof scheduledTimeoutsState.get(timerId) === 'symbol') {
                scheduledTimeoutsState.set(timerId, null);
                call('clear', { timerId, timerType: 'timeout' }).then(() => {
                    scheduledTimeoutsState.delete(timerId);
                });
            }
        };
    },
    setInterval: ({ call }) => {
        return (func, delay = 0, ...args) => {
            const symbol = Symbol();
            const timerId = bundle.exports.generateUniqueNumber(scheduledIntervalsState);
            scheduledIntervalsState.set(timerId, symbol);
            const schedule = () => call('set', {
                delay,
                now: performance.timeOrigin + performance.now(),
                timerId,
                timerType: 'interval'
            }).then(() => {
                const state = scheduledIntervalsState.get(timerId);
                if (state === undefined) {
                    throw new Error('The timer is in an undefined state.');
                }
                if (state === symbol) {
                    func(...args);
                    // Doublecheck if the interval should still be rescheduled because it could have been cleared inside of func().
                    if (scheduledIntervalsState.get(timerId) === symbol) {
                        schedule();
                    }
                }
            });
            schedule();
            return timerId;
        };
    },
    setTimeout: ({ call }) => {
        return (func, delay = 0, ...args) => {
            const symbol = Symbol();
            const timerId = bundle.exports.generateUniqueNumber(scheduledTimeoutsState);
            scheduledTimeoutsState.set(timerId, symbol);
            call('set', {
                delay,
                now: performance.timeOrigin + performance.now(),
                timerId,
                timerType: 'timeout'
            }).then(() => {
                const state = scheduledTimeoutsState.get(timerId);
                if (state === undefined) {
                    throw new Error('The timer is in an undefined state.');
                }
                if (state === symbol) {
                    // A timeout can be savely deleted because it is only called once.
                    scheduledTimeoutsState.delete(timerId);
                    func(...args);
                }
            });
            return timerId;
        };
    }
});
const load = (url) => {
    const worker = new Worker(url);
    return wrap(worker);
};

const createLoadOrReturnBroker = (loadBroker, worker) => {
    let broker = null;
    return () => {
        if (broker !== null) {
            return broker;
        }
        const blob = new Blob([worker], { type: 'application/javascript; charset=utf-8' });
        const url = URL.createObjectURL(blob);
        broker = loadBroker(url);
        // Bug #1: Edge up until v18 didn't like the URL to be revoked directly.
        setTimeout(() => URL.revokeObjectURL(url));
        return broker;
    };
};

// This is the minified and stringified code of the worker-timers-worker package.
const worker = `(()=>{var e={455:function(e,t){!function(e){"use strict";var t=function(e){return function(t){var r=e(t);return t.add(r),r}},r=function(e){return function(t,r){return e.set(t,r),r}},n=void 0===Number.MAX_SAFE_INTEGER?9007199254740991:Number.MAX_SAFE_INTEGER,o=536870912,s=2*o,a=function(e,t){return function(r){var a=t.get(r),i=void 0===a?r.size:a<s?a+1:0;if(!r.has(i))return e(r,i);if(r.size<o){for(;r.has(i);)i=Math.floor(Math.random()*s);return e(r,i)}if(r.size>n)throw new Error("Congratulations, you created a collection of unique numbers which uses all available integers!");for(;r.has(i);)i=Math.floor(Math.random()*n);return e(r,i)}},i=new WeakMap,u=r(i),c=a(u,i),d=t(c);e.addUniqueNumber=d,e.generateUniqueNumber=c}(t)}},t={};function r(n){var o=t[n];if(void 0!==o)return o.exports;var s=t[n]={exports:{}};return e[n].call(s.exports,s,s.exports,r),s.exports}(()=>{"use strict";const e=-32603,t=-32602,n=-32601,o=(e,t)=>Object.assign(new Error(e),{status:t}),s=t=>o('The handler of the method called "'.concat(t,'" returned an unexpected result.'),e),a=(t,r)=>async({data:{id:a,method:i,params:u}})=>{const c=r[i];try{if(void 0===c)throw(e=>o('The requested method called "'.concat(e,'" is not supported.'),n))(i);const r=void 0===u?c():c(u);if(void 0===r)throw(t=>o('The handler of the method called "'.concat(t,'" returned no required result.'),e))(i);const d=r instanceof Promise?await r:r;if(null===a){if(void 0!==d.result)throw s(i)}else{if(void 0===d.result)throw s(i);const{result:e,transferables:r=[]}=d;t.postMessage({id:a,result:e},r)}}catch(e){const{message:r,status:n=-32603}=e;t.postMessage({error:{code:n,message:r},id:a})}};var i=r(455);const u=new Map,c=(e,r,n)=>({...r,connect:({port:t})=>{t.start();const n=e(t,r),o=(0,i.generateUniqueNumber)(u);return u.set(o,(()=>{n(),t.close(),u.delete(o)})),{result:o}},disconnect:({portId:e})=>{const r=u.get(e);if(void 0===r)throw(e=>o('The specified parameter called "portId" with the given value "'.concat(e,'" does not identify a port connected to this worker.'),t))(e);return r(),{result:null}},isSupported:async()=>{if(await new Promise((e=>{const t=new ArrayBuffer(0),{port1:r,port2:n}=new MessageChannel;r.onmessage=({data:t})=>e(null!==t),n.postMessage(t,[t])}))){const e=n();return{result:e instanceof Promise?await e:e}}return{result:!1}}}),d=(e,t,r=()=>!0)=>{const n=c(d,t,r),o=a(e,n);return e.addEventListener("message",o),()=>e.removeEventListener("message",o)},l=e=>t=>{const r=e.get(t);if(void 0===r)return Promise.resolve(!1);const[n,o]=r;return clearTimeout(n),e.delete(t),o(!1),Promise.resolve(!0)},f=(e,t,r)=>(n,o,s)=>{const{expected:a,remainingDelay:i}=e(n,o);return new Promise((e=>{t.set(s,[setTimeout(r,i,a,t,e,s),e])}))},m=(e,t)=>{const r=performance.now(),n=e+t-r-performance.timeOrigin;return{expected:r+n,remainingDelay:n}},p=(e,t,r,n)=>{const o=e-performance.now();o>0?t.set(n,[setTimeout(p,o,e,t,r,n),r]):(t.delete(n),r(!0))},h=new Map,v=l(h),w=new Map,g=l(w),M=f(m,h,p),y=f(m,w,p);d(self,{clear:async({timerId:e,timerType:t})=>({result:await("interval"===t?v(e):g(e))}),set:async({delay:e,now:t,timerId:r,timerType:n})=>({result:await("interval"===n?M:y)(e,t,r)})})})()})();`; // tslint:disable-line:max-line-length

const loadOrReturnBroker = createLoadOrReturnBroker(load, worker);
const clearTimeout$1 = (timerId) => loadOrReturnBroker().clearTimeout(timerId);
const setTimeout$1 = (...args) => loadOrReturnBroker().setTimeout(...args);

class PluginManager {
    constructor() {
        this.plugins = new Map();
        this.effect = null;
    }
    register(name, plugin) {
        if (this.plugins.has(name)) {
            logger.warn(`plugin manager: plugin with name ${name} already registered.`);
            return;
        }
        this.plugins.set(name, plugin);
    }
    initialize(effect) {
        logger.info('plugin manager: initializing plugins');
        this.effect = effect;
        this.plugins.forEach((plugin, name) => {
            if (plugin.isInitialized) {
                logger.warn(`plugin manager: "${name}" plugin already initialized.`);
                return;
            }
            plugin.initialize(effect);
        });
    }
    dispose() {
        if (!this.effect) {
            logger.warn('plugin manager: dispose called before effect is initialized.');
            return;
        }
        this.getPlugins().forEach((plugin) => {
            plugin.dispose(this.effect);
        });
        this.plugins.clear();
        this.effect = null;
    }
    getPlugin(name) {
        return this.plugins.get(name);
    }
    getPlugins() {
        return this.plugins;
    }
    getPluginCount() {
        return this.plugins.size;
    }
}

const generateUuid = () => String(Math.random() * 1e17);
const injectStyles = () => {
    const styleId = 'web-media-effects-styles';
    let styleEl = document.getElementById(styleId);
    if (!styleEl) {
        styleEl = document.createElement('style');
        styleEl.id = styleId;
        styleEl.textContent = `
      .web-media-effects-hidden {
        bottom: 0;
        opacity: 0;
        position: fixed;
        z-index: -2147483647;
        height: 0px;
        width: 0px;
      }
    `;
        document.head.appendChild(styleEl);
    }
};
const getOrCreateEl = (kind, height, width, uniqueId = generateUuid(), addToDom = false) => {
    injectStyles();
    const id = `web-media-effects-${kind}-${uniqueId}`;
    let el = document.getElementById(id);
    if (!el) {
        el = document.createElement(kind);
        el.id = id;
        el.classList.add('web-media-effects-hidden');
        el.setAttribute('height', height.toString());
        el.setAttribute('width', width.toString());
        if (addToDom) {
            document.body.appendChild(el);
        }
    }
    return el;
};
const getCanvasEl = (height, width, uniqueId, showInDom) => getOrCreateEl('canvas', height, width, uniqueId, showInDom);
const getVideoEl = (height, width, uniqueId) => getOrCreateEl('video', height, width, uniqueId);

const isIos = () => {
    var _a, _b, _c;
    return Boolean(['iPad Simulator', 'iPhone Simulator', 'iPod Simulator', 'iPad', 'iPhone', 'iPod'].includes((_a = globalThis === null || globalThis === void 0 ? void 0 : globalThis.navigator) === null || _a === void 0 ? void 0 : _a.platform) ||
        (((_c = (_b = globalThis === null || globalThis === void 0 ? void 0 : globalThis.navigator) === null || _b === void 0 ? void 0 : _b.userAgent) === null || _c === void 0 ? void 0 : _c.includes('Mac')) && 'ontouchend' in (globalThis === null || globalThis === void 0 ? void 0 : globalThis.document)));
};
const portraitMql = () => { var _a; return (_a = globalThis === null || globalThis === void 0 ? void 0 : globalThis.matchMedia) === null || _a === void 0 ? void 0 : _a.call(globalThis, '(orientation: portrait)'); };

const drawImage = (srcEl, destEl) => {
    const { height: sh, width: sw } = srcEl;
    const { height: dh, width: dw } = destEl;
    const ctx = destEl.getContext('2d');
    if (!ctx)
        return;
    ctx.clearRect(0, 0, dw, dh);
    ctx.drawImage(srcEl, 0, 0, sw, sh, 0, 0, dw, dh);
};

const getBackgroundConfig = (backgroundSource, video = false, mirror = false) => ({
    type: 'replacement',
    horizontalMirror: mirror,
    background: backgroundSource,
    static: !video,
});

const loadImageFromUrl = (url) => new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
        resolve(image);
    };
    image.onerror = (e) => reject(e);
    image.src = url;
    image.crossOrigin = 'anonymous';
});
const loadVideoFromUrl = (url) => new Promise((resolve, reject) => {
    const video = getOrCreateEl('video', 0, 0, undefined, true);
    video.onloadeddata = () => {
        video.height = video.videoHeight;
        video.width = video.videoWidth;
        resolve(video);
    };
    video.onerror = () => reject();
    video.autoplay = true;
    video.playsInline = true;
    video.loop = true;
    video.muted = true;
    video.src = url;
    video.crossOrigin = 'anonymous';
});

class DebugStatsGenerator {
    constructor() {
        this.frameTimings = [];
        this.lastFrameTimestamp = 0;
        this.modelInferenceTimings = [];
        this.recentErrors = [];
        this.currentInferenceStats = {
            lastFrameTime: 0,
            totalFrameTime: 0,
            modelInferenceTime: 0,
            preprocessingTime: 0,
            postprocessingTime: 0,
            inferenceSkipped: false,
        };
        this.webglInfo = this.getWebGLInfo();
        this.initializeComputePressureMonitoring();
    }
    trackFrameProcessing(timestamp, result) {
        const now = performance.now();
        if (this.lastFrameTimestamp > 0) {
            const frameTime = now - this.lastFrameTimestamp;
            this.frameTimings.push(frameTime);
            if (this.frameTimings.length > 60) {
                this.frameTimings.shift();
            }
        }
        this.lastFrameTimestamp = now;
        if ((result === null || result === void 0 ? void 0 : result.stats) &&
            !result.stats.inferenceSkipped &&
            result.stats.modelInferenceTime &&
            result.stats.modelInferenceTime > 0) {
            this.modelInferenceTimings.push(result.stats.modelInferenceTime);
            if (this.modelInferenceTimings.length > 30) {
                this.modelInferenceTimings.shift();
            }
        }
    }
    updateInferenceResult(timestamp, result) {
        this.trackFrameProcessing(timestamp, result);
        this.currentInferenceStats.lastFrameTime = timestamp;
        if (result === null || result === void 0 ? void 0 : result.stats) {
            this.currentInferenceStats.totalFrameTime = result.stats.totalFrameTime || 0;
            this.currentInferenceStats.modelInferenceTime = result.stats.modelInferenceTime || 0;
            this.currentInferenceStats.preprocessingTime = result.stats.preprocessingTime || 0;
            this.currentInferenceStats.postprocessingTime = result.stats.postprocessingTime || 0;
            this.currentInferenceStats.inferenceSkipped = result.stats.inferenceSkipped || false;
        }
    }
    generateStats(effect) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o;
        try {
            const stats = Object.assign(Object.assign({ frameRate: effect.frameRate || 0, modelReady: effect.isReady || false, effectEnabled: effect.isEnabled || false, trackEnabled: ((_a = effect.inputTrack) === null || _a === void 0 ? void 0 : _a.enabled) || false, mode: ((_b = effect.options) === null || _b === void 0 ? void 0 : _b.mode) || 'unknown', quality: ((_c = effect.options) === null || _c === void 0 ? void 0 : _c.quality) || 'unknown', frameInterval: effect.frameInterval || 0, lastFrameTime: this.currentInferenceStats.lastFrameTime, inputDimensions: {
                    width: ((_d = effect.inputEl) === null || _d === void 0 ? void 0 : _d.width) || 0,
                    height: ((_e = effect.inputEl) === null || _e === void 0 ? void 0 : _e.height) || 0,
                }, outputDimensions: {
                    width: ((_f = effect.outputEl) === null || _f === void 0 ? void 0 : _f.width) || 0,
                    height: ((_g = effect.outputEl) === null || _g === void 0 ? void 0 : _g.height) || 0,
                }, videoWidth: (_h = effect.trackSettings) === null || _h === void 0 ? void 0 : _h.width, videoHeight: (_j = effect.trackSettings) === null || _j === void 0 ? void 0 : _j.height, videoFrameRate: (_k = effect.trackSettings) === null || _k === void 0 ? void 0 : _k.frameRate, imageCaptureActive: !!effect.imageCapture, trackProcessorActive: !!effect.trackProcessor, pluginCount: ((_o = (_m = (_l = effect.getPluginManager) === null || _l === void 0 ? void 0 : _l.call(effect)) === null || _m === void 0 ? void 0 : _m.getPluginCount) === null || _o === void 0 ? void 0 : _o.call(_m)) || 0, cpuCores: navigator.hardwareConcurrency || 0, deviceMemory: this.getDeviceMemory(), connectionType: this.getConnectionType(), effectiveType: this.getEffectiveConnectionType(), memoryUsage: this.getMemoryUsage() }, this.webglInfo), { totalFrameTime: this.currentInferenceStats.totalFrameTime, modelInferenceTime: this.currentInferenceStats.modelInferenceTime, preprocessingTime: this.currentInferenceStats.preprocessingTime, postprocessingTime: this.currentInferenceStats.postprocessingTime, inferenceSkipped: this.currentInferenceStats.inferenceSkipped, modelInferenceFPS: this.calculateModelInferenceFPS(), averageFrameTime: this.calculateAverageFrameTime(), frameDrops: this.getFrameDrops(effect.frameRate || 30), computePressure: this.computePressure, errors: [...this.recentErrors] });
            return stats;
        }
        catch (error) {
            return this.getDefaultStats();
        }
    }
    reset() {
        this.frameTimings = [];
        this.modelInferenceTimings = [];
        this.lastFrameTimestamp = 0;
        this.recentErrors = [];
        this.currentInferenceStats = {
            lastFrameTime: 0,
            totalFrameTime: 0,
            modelInferenceTime: 0,
            preprocessingTime: 0,
            postprocessingTime: 0,
            inferenceSkipped: false,
        };
    }
    dispose() {
        if (this.pressureObserver) {
            try {
                this.pressureObserver.disconnect();
            }
            catch (error) {
            }
            this.pressureObserver = undefined;
        }
    }
    addError(error) {
        const errorMessage = typeof error === 'string' ? error : error.message;
        this.recentErrors.push(errorMessage);
        if (this.recentErrors.length > 10) {
            this.recentErrors.shift();
        }
    }
    getDefaultStats() {
        return Object.assign(Object.assign({ frameRate: 0, modelReady: false, effectEnabled: false, trackEnabled: false, mode: 'unknown', quality: 'unknown', frameInterval: 0, lastFrameTime: 0, inputDimensions: { width: 0, height: 0 }, outputDimensions: { width: 0, height: 0 }, videoWidth: undefined, videoHeight: undefined, videoFrameRate: undefined, cpuCores: navigator.hardwareConcurrency || 0, deviceMemory: this.getDeviceMemory(), connectionType: this.getConnectionType(), effectiveType: this.getEffectiveConnectionType(), memoryUsage: this.getMemoryUsage() }, this.webglInfo), { imageCaptureActive: false, trackProcessorActive: false, pluginCount: 0, totalFrameTime: undefined, modelInferenceTime: undefined, preprocessingTime: undefined, postprocessingTime: undefined, inferenceSkipped: undefined, modelInferenceFPS: undefined, averageFrameTime: undefined, frameDrops: 0, computePressure: undefined, errors: [] });
    }
    initializeComputePressureMonitoring() {
        if ('PressureObserver' in window && window.PressureObserver) {
            try {
                this.pressureObserver = new window.PressureObserver((changes) => {
                    const lastChange = changes[changes.length - 1];
                    const validStates = ['nominal', 'fair', 'serious', 'critical'];
                    const state = validStates.includes(lastChange.state) ? lastChange.state : 'nominal';
                    this.computePressure = {
                        state,
                        source: lastChange.source,
                    };
                });
                this.pressureObserver.observe('cpu');
            }
            catch (error) {
                this.computePressure = undefined;
            }
        }
    }
    calculateModelInferenceFPS() {
        if (this.modelInferenceTimings.length < 3) {
            return undefined;
        }
        const avgInferenceTime = this.modelInferenceTimings.reduce((a, b) => a + b, 0) / this.modelInferenceTimings.length;
        if (avgInferenceTime <= 0) {
            return undefined;
        }
        return 1000 / avgInferenceTime;
    }
    calculateAverageFrameTime() {
        if (this.frameTimings.length < 2) {
            return undefined;
        }
        const avgTime = this.frameTimings.reduce((a, b) => a + b, 0) / this.frameTimings.length;
        return avgTime;
    }
    getFrameDrops(targetFrameRate) {
        const expectedFrameTime = 1000 / targetFrameRate;
        const dropThreshold = expectedFrameTime * 2;
        return this.frameTimings.filter((time) => time > dropThreshold).length;
    }
    getDeviceMemory() {
        return navigator.deviceMemory;
    }
    getConnectionType() {
        const { connection } = navigator;
        return connection === null || connection === void 0 ? void 0 : connection.type;
    }
    getEffectiveConnectionType() {
        const { connection } = navigator;
        return connection === null || connection === void 0 ? void 0 : connection.effectiveType;
    }
    getMemoryUsage() {
        if ('memory' in performance) {
            const { memory } = performance;
            if (memory) {
                return Math.round(memory.usedJSHeapSize / 1024 / 1024);
            }
        }
        return undefined;
    }
    getWebGLInfo() {
        let canvas = null;
        let gl = null;
        try {
            canvas = document.createElement('canvas');
            gl = (canvas.getContext('webgl') ||
                canvas.getContext('experimental-webgl'));
            if (!gl) {
                return {};
            }
            const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
            const vendor = debugInfo
                ? gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL)
                : gl.getParameter(gl.VENDOR);
            const renderer = debugInfo
                ? gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL)
                : gl.getParameter(gl.RENDERER);
            const maxTextureSize = gl.getParameter(gl.MAX_TEXTURE_SIZE);
            return {
                webglVendor: vendor,
                webglRenderer: renderer,
                maxTextureSize,
            };
        }
        catch (error) {
            return {};
        }
        finally {
            if (gl) {
                const loseContext = gl.getExtension('WEBGL_lose_context');
                if (loseContext) {
                    loseContext.loseContext();
                }
            }
            canvas = null;
            gl = null;
        }
    }
}

var VirtualBackgroundMode;
(function (VirtualBackgroundMode) {
    VirtualBackgroundMode["Blur"] = "BLUR";
    VirtualBackgroundMode["Image"] = "IMAGE";
    VirtualBackgroundMode["Video"] = "VIDEO";
    VirtualBackgroundMode["Passthrough"] = "PASSTHROUGH";
})(VirtualBackgroundMode || (VirtualBackgroundMode = {}));

const defaultOptions = Object.freeze({
    mode: VirtualBackgroundMode.Blur,
    blurStrength: R.STRONG,
    quality: E$1.LOW,
    authToken: '',
    mirror: false,
});

const DEFAULT_FRAME_RATE = 24;
const omitAuthToken = (obj) => omitKey('authToken', obj);
class VirtualBackgroundEffect extends BaseCameraEffect {
    constructor(options) {
        super();
        this.defaultOptions = defaultOptions;
        this.uniqueId = Date.now();
        this.lastFrameTime = 0;
        this.restoreEffectOnTrackEnable = false;
        this.pluginManager = new PluginManager();
        this.beforeInferenceCallbacks = [];
        this.afterInferenceCallbacks = [];
        this.configBuilder = new g();
        this.debugStatsGenerator = new DebugStatsGenerator();
        this.handleOrientationChange = (mql) => {
            const isPortrait = mql.matches;
            if (!this.trackSettings) {
                throw new Error(`virtual background: unable to determine input track settings`);
            }
            let { height, width } = this.trackSettings;
            if (!height || !width) {
                logger.log(`virtual background: orientation change detected but unknown height or width`, {
                    height,
                    width,
                });
                return;
            }
            logger.log(`virtual background: orientation change detected`, {
                height,
                width,
            });
            if ((isPortrait && height < width) || (!isPortrait && height > width)) {
                [height, width] = [width, height];
            }
            logger.log(`virtual background: orientation changed to ${isPortrait ? 'portrait' : 'landscape'}`, {
                height,
                width,
            });
            [this.videoEl, this.inputEl, this.outputEl, this.backgroundEl].forEach((el) => {
                if (height) {
                    el === null || el === void 0 ? void 0 : el.setAttribute('height', height.toString());
                }
                if (width) {
                    el === null || el === void 0 ? void 0 : el.setAttribute('width', width.toString());
                }
            });
        };
        this.registerPlugin = (name, plugin) => {
            logger.log('virtual background: registering plugin', name, plugin);
            this.pluginManager.register(name, plugin);
        };
        this.initializePlugins = () => {
            logger.log('virtual background: initializing plugins');
            this.pluginManager.initialize(this);
        };
        this.getPlugin = (name) => {
            return this.pluginManager.getPlugin(name);
        };
        this.getPluginManager = () => {
            return this.pluginManager;
        };
        this.options = Object.assign(Object.assign({}, defaultOptions), options);
        logger.log('virtual background: init effect', omitAuthToken(this.options));
        this.loadModel = this.loadModel.bind(this);
        this.handleFrame = this.handleFrame.bind(this);
        this.handleOrientationChange = this.handleOrientationChange.bind(this);
        this.handleVideoLoadedMetadata = this.handleVideoLoadedMetadata.bind(this);
        this.handleVideoResize = this.handleVideoResize.bind(this);
        this.handleAssetUrlResolver = this.handleAssetUrlResolver.bind(this);
        this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    }
    get isReady() {
        var _a;
        return !!((_a = this.model) === null || _a === void 0 ? void 0 : _a.isLoaded());
    }
    get isLoaded() {
        return !!this.loadFuture;
    }
    get frameRate() {
        var _a, _b, _c;
        return (_c = (_a = this.options.frameRate) !== null && _a !== void 0 ? _a : (_b = this.trackSettings) === null || _b === void 0 ? void 0 : _b.frameRate) !== null && _c !== void 0 ? _c : DEFAULT_FRAME_RATE;
    }
    get frameInterval() {
        return Math.round(1000 / this.frameRate);
    }
    preloadAssets() {
        return __awaiter(this, void 0, void 0, function* () {
            const start = performance.now();
            if (this.preloadFuture) {
                yield this.preloadFuture.promise;
                return;
            }
            if (this.disposeFuture) {
                yield this.disposeFuture.promise;
                this.disposeFuture = undefined;
            }
            this.preloadFuture = new Future();
            logger.log('virtual background: preloading assets');
            try {
                this.configureBuilder();
                const preloadConfig = this.configBuilder.build(false);
                logger.log('virtual background: preload configuration', preloadConfig);
                yield e(preloadConfig);
                const end = performance.now();
                const duration = Math.round(end - start) / 1000;
                logger.log(`virtual background: preload completed in ${duration} seconds`);
                this.preloadFuture.resolve();
            }
            catch (error) {
                this.preloadFuture.reject(error);
                this.preloadFuture = undefined;
                throw error;
            }
        });
    }
    loadMediaStream(input) {
        const _super = Object.create(null, {
            loadMedia: { get: () => super.loadMedia }
        });
        return __awaiter(this, void 0, void 0, function* () {
            logger.log('virtual background: load stream', input);
            const [videoTrack] = input.getVideoTracks();
            if (!videoTrack) {
                throw new Error('virtual background: load stream failed, no video track found');
            }
            yield _super.loadMedia.call(this, videoTrack, input);
            yield this.loadDomAndModel();
            if (!this.outputStream) {
                throw new Error('virtual background: failed to create output stream');
            }
            logger.log('virtual background: setting new track enabled state', videoTrack.enabled);
            this.outputStream.getVideoTracks()[0].enabled = videoTrack.enabled;
            return this.outputStream;
        });
    }
    loadMediaStreamTrack(input) {
        const _super = Object.create(null, {
            loadMedia: { get: () => super.loadMedia }
        });
        return __awaiter(this, void 0, void 0, function* () {
            logger.log('virtual background: load track', input);
            _super.loadMedia.call(this, input);
            yield this.loadDomAndModel();
            if (!this.outputTrack) {
                throw new Error('virtual background: failed to create output track');
            }
            return this.outputTrack;
        });
    }
    loadDomAndModel() {
        var _a, _b, _c, _d, _e;
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.inputStream) {
                throw new Error('virtual background: failed to create input stream');
            }
            if (((_a = this.updateFuture) === null || _a === void 0 ? void 0 : _a.state) === FutureState.Pending) {
                logger.log('virtual background: waiting for update to finish');
                yield this.updateFuture.promise;
            }
            if (!this.loadFuture) {
                this.loadFuture = new Future();
                yield ((_b = this.disposeFuture) === null || _b === void 0 ? void 0 : _b.promise);
                this.disposeFuture = undefined;
                this.trackSettings = (_c = this.inputTrack) === null || _c === void 0 ? void 0 : _c.getSettings();
                if (!this.trackSettings) {
                    throw new Error('virtual background: unable to determine input track settings');
                }
                this.setupDom(this.trackSettings.height, this.trackSettings.width);
                this.detectQuirks();
                yield this.loadModel();
                if (!this.videoEl) {
                    throw new Error('virtual background: unable to create input video element');
                }
                this.videoEl.srcObject = this.inputStream;
                logger.log(`virtual background: capturing output stream at frameRate = ${this.frameRate}`);
                const effectStream = (_d = this.outputEl) === null || _d === void 0 ? void 0 : _d.captureStream(this.frameRate);
                this.effectTrack = effectStream === null || effectStream === void 0 ? void 0 : effectStream.getVideoTracks()[0];
                this.requestFrame =
                    document.visibilityState === 'visible'
                        ? this.requestAnimationFrameWrapper
                        : this.setTimeoutWrapper;
                this.setupVisibilityChange();
                try {
                    this.lastFrameTime = performance.now();
                    (_e = this.requestFrame) === null || _e === void 0 ? void 0 : _e.call(this);
                    this.loadFuture.resolve();
                }
                catch (error) {
                    this.loadFuture.reject(error);
                    throw error;
                }
            }
            return this.loadFuture.promise;
        });
    }
    setupVisibilityChange() {
        if (this.options.preventBackgroundThrottling) {
            logger.log('virtual background: adding visibility change listener to prevent throttling');
            document.addEventListener('visibilitychange', this.handleVisibilityChange);
        }
    }
    handleVisibilityChange() {
        this.clearTimers();
        this.requestFrame =
            document.visibilityState === 'visible'
                ? this.requestAnimationFrameWrapper
                : this.setTimeoutWrapper;
        this.requestFrame();
    }
    requestAnimationFrameWrapper() {
        if (this.rafId) {
            cancelAnimationFrame(this.rafId);
        }
        this.rafId = requestAnimationFrame(this.handleFrame);
    }
    setTimeoutWrapper() {
        if (this.setTimeoutId) {
            clearTimeout$1(this.setTimeoutId);
        }
        this.setTimeoutId = setTimeout$1(this.handleFrame, this.frameInterval);
    }
    clearTimers() {
        if (this.rafId) {
            cancelAnimationFrame(this.rafId);
            this.rafId = undefined;
        }
        if (this.setTimeoutId) {
            clearTimeout$1(this.setTimeoutId);
            this.setTimeoutId = undefined;
        }
    }
    configureBuilder() {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        const env = this.options.env === EffectEnv.Int ? 'int' : 'prod';
        this.configBuilder = new g(env);
        this.configBuilder
            .setMaskQuality(this.options.quality)
            .setAssetUrlResolver(this.handleAssetUrlResolver)
            .setMaskGenerationIntervalMs(this.frameInterval);
        if (this.options.baseUrl) {
            this.configBuilder.setBaseUrl(this.options.baseUrl);
        }
        const overrides = this.options.modelOverrides;
        if (overrides) {
            if (((_a = overrides.inputSize) === null || _a === void 0 ? void 0 : _a.height) && ((_b = overrides.inputSize) === null || _b === void 0 ? void 0 : _b.width)) {
                const { height, width } = overrides.inputSize;
                this.configBuilder.setMaskInputSize(height, width);
            }
            if (((_c = overrides.outputSize) === null || _c === void 0 ? void 0 : _c.height) && ((_d = overrides.outputSize) === null || _d === void 0 ? void 0 : _d.width)) {
                const { height, width } = overrides.outputSize;
                this.configBuilder.setMaskOutputSize(height, width);
            }
            if (((_e = overrides.upscaleSize) === null || _e === void 0 ? void 0 : _e.height) && ((_f = overrides.upscaleSize) === null || _f === void 0 ? void 0 : _f.width)) {
                const { height, width } = overrides.upscaleSize;
                this.configBuilder.setMaskUpscaleSize(height, width);
            }
            if (overrides.modelRank) {
                this.configBuilder.setMaskModelRank(overrides.modelRank);
            }
            if (overrides.workerUri) {
                this.configBuilder.setMaskWorkerUri(overrides.workerUri);
            }
            if (overrides.executionProviders) {
                this.configBuilder.setExecutionProviders(overrides.executionProviders);
            }
            if (overrides.modelUri) {
                this.configBuilder.setMaskModelUri(overrides.modelUri);
            }
            if (overrides.generationIntervalMs) {
                this.configBuilder.setMaskGenerationIntervalMs(overrides.generationIntervalMs);
            }
        }
        if (((_g = this.trackSettings) === null || _g === void 0 ? void 0 : _g.height) && ((_h = this.trackSettings) === null || _h === void 0 ? void 0 : _h.width)) {
            this.configBuilder.setInputSize(this.trackSettings.height, this.trackSettings.width);
        }
        logger.log('virtual background: configuration builder set', this.configBuilder);
    }
    createPipeline(renderConfig) {
        return __awaiter(this, void 0, void 0, function* () {
            logger.log('virtual background: creating pipeline', renderConfig);
            return new Promise((resolve) => {
                if (!this.outputEl || !this.inputEl || !this.videoEl) {
                    throw new Error('virtual background: unable to create pipeline, missing elements');
                }
                this.configureBuilder();
                this.configBuilder.setRenderConfig(renderConfig);
                const pipelineConfig = this.configBuilder.build();
                logger.log('virtual background: pipeline created', pipelineConfig);
                resolve(new D(this.inputEl, this.outputEl, pipelineConfig, this.videoEl));
            });
        });
    }
    loadModel() {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            let config;
            logger.log('virtual background: loading model with mode:', this.options.mode);
            switch (this.options.mode) {
                case VirtualBackgroundMode.Blur: {
                    if (!this.options.blurStrength) {
                        throw new Error('virtual background: missing `blurStrength` option');
                    }
                    config = g.getBlurConfig(this.options.blurStrength, this.options.mirror);
                    break;
                }
                case VirtualBackgroundMode.Image: {
                    if (!this.options.bgImageUrl) {
                        throw new Error('virtual background: missing `bgImageUrl` option');
                    }
                    if (!this.backgroundEl) {
                        throw new Error('virtual background: missing hidden background element');
                    }
                    const image = yield loadImageFromUrl(this.options.bgImageUrl);
                    yield this.optimizeBackgroundImage(image);
                    config = getBackgroundConfig(this.backgroundEl, false, this.options.mirror);
                    break;
                }
                case VirtualBackgroundMode.Video: {
                    if (!this.options.bgVideoUrl) {
                        throw new Error('virtual background: missing `bgVideoUrl` option');
                    }
                    this.virtualVideoEl = yield loadVideoFromUrl(this.options.bgVideoUrl);
                    config = getBackgroundConfig(this.virtualVideoEl, true, this.options.mirror);
                    break;
                }
                case VirtualBackgroundMode.Passthrough:
                    config = g.getPassthroughConfig(this.options.mirror);
                    break;
                default:
                    throw new Error('virtual background: invalid options supplied to the effect');
            }
            yield ((_a = this.model) === null || _a === void 0 ? void 0 : _a.destroy());
            this.model = yield this.createPipeline(config);
            return (_b = this.model) === null || _b === void 0 ? void 0 : _b.load();
        });
    }
    handleFrame(timestamp = performance.now()) {
        var _a, _b, _c, _d;
        return __awaiter(this, void 0, void 0, function* () {
            const elapsedSinceLastFrame = timestamp - this.lastFrameTime;
            const frameTimestamp = Date.now();
            if (((_a = this.disposeFuture) === null || _a === void 0 ? void 0 : _a.state) === FutureState.Pending) {
                logger.log('virtual background: waiting for dispose to finish');
                yield ((_b = this.disposeFuture) === null || _b === void 0 ? void 0 : _b.promise);
            }
            if (elapsedSinceLastFrame < this.frameInterval) {
                (_c = this.requestFrame) === null || _c === void 0 ? void 0 : _c.call(this);
                return;
            }
            this.lastFrameTime = timestamp;
            try {
                if (!this.isReady) {
                    return;
                }
                yield this.manageEffectRestoration();
                if (!this.isEnabled || !this.model) {
                    return;
                }
                const { canvasToProcess, shouldInfer } = yield this.executeBeforeInferenceCallbacks(frameTimestamp);
                if (canvasToProcess && shouldInfer) {
                    this.prepareInputCanvas(canvasToProcess);
                }
                this.lastInferenceResult = yield this.model.onFrame(frameTimestamp, shouldInfer);
                try {
                    this.debugStatsGenerator.updateInferenceResult(frameTimestamp, this.lastInferenceResult);
                }
                catch (e) {
                    logger.log('virtual background: issue updating debug stats', e);
                }
                yield this.executeAfterInferenceCallbacks(this.lastInferenceResult.timestamp, this.lastInferenceResult);
            }
            catch (e) {
                logger.log('virtual background: issue rendering frame', e);
            }
            (_d = this.requestFrame) === null || _d === void 0 ? void 0 : _d.call(this);
        });
    }
    manageEffectRestoration() {
        var _a, _b, _c, _d;
        return __awaiter(this, void 0, void 0, function* () {
            const inputTrackEnabled = ((_a = this.inputTrack) === null || _a === void 0 ? void 0 : _a.enabled) && !((_b = this.inputTrack) === null || _b === void 0 ? void 0 : _b.muted);
            if (this.restoreEffectOnTrackEnable && !inputTrackEnabled) {
                (_c = this.requestFrame) === null || _c === void 0 ? void 0 : _c.call(this);
                return;
            }
            if (this.restoreEffectOnTrackEnable && inputTrackEnabled) {
                logger.log('virtual background: re-enabling effect as the track is now enabled');
                this.restoreEffectOnTrackEnable = false;
                yield this.enable();
                return;
            }
            if (this.isEnabled && !inputTrackEnabled) {
                logger.log('virtual background: input track disabled; disabling effect');
                this.restoreEffectOnTrackEnable = true;
                yield this.disable();
                (_d = this.requestFrame) === null || _d === void 0 ? void 0 : _d.call(this);
            }
        });
    }
    prepareInputCanvas(canvasToProcess) {
        var _a;
        if (!this.videoEl) {
            throw new Error('virtual background: missing hidden video element');
        }
        if (!this.inputEl) {
            throw new Error('virtual background: missing input canvas element');
        }
        drawImage(this.videoEl, canvasToProcess);
        (_a = this.inputCanvasContext) === null || _a === void 0 ? void 0 : _a.drawImage(canvasToProcess, 0, 0, this.inputEl.width, this.inputEl.height);
    }
    executeBeforeInferenceCallbacks(frameTimestamp) {
        return __awaiter(this, void 0, void 0, function* () {
            let canvasToProcess = this.inputEl;
            let shouldInfer = true;
            if (this.beforeInferenceCallbacks.length) {
                try {
                    yield this.beforeInferenceCallbacks.reduce((prevPromise, cb) => __awaiter(this, void 0, void 0, function* () {
                        yield prevPromise;
                        if (this.lastInferenceResult) {
                            const result = yield cb(frameTimestamp, this.lastInferenceResult, canvasToProcess);
                            shouldInfer && (shouldInfer = result.shouldInfer);
                            canvasToProcess = result.modifiedCanvas || canvasToProcess;
                        }
                    }), Promise.resolve());
                }
                catch (e) {
                    logger.log('virtual background: issue invoking before inference callbacks', e);
                }
            }
            return { canvasToProcess, shouldInfer };
        });
    }
    executeAfterInferenceCallbacks(frameTimestamp, inferenceResult) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                this.afterInferenceCallbacks.forEach((cb) => cb(frameTimestamp, inferenceResult));
            }
            catch (e) {
                logger.log('virtual background: issue invoking after inference callbacks', e);
            }
        });
    }
    setupDom(height, width) {
        logger.log('virtual background: setting up dom with dimension', { height, width });
        if (!height || !width) {
            throw new Error(`virtual background: missing dimensions: height=${height}, width=${width})`);
        }
        this.uniqueId = Date.now();
        this.videoEl = getVideoEl(height, width, this.uniqueId.toString());
        this.inputEl = getCanvasEl(height, width, `input-${this.uniqueId}`);
        this.outputEl = getCanvasEl(height, width, `output-${this.uniqueId}`, true);
        this.backgroundEl = getCanvasEl(height, width, `background-${this.uniqueId}`);
        this.inputCanvasContext = this.inputEl.getContext('2d', { willReadFrequently: true });
        this.videoEl.setAttribute('autoplay', '');
        this.videoEl.setAttribute('playsinline', '');
        this.videoEl.setAttribute('muted', '');
        this.videoEl.addEventListener('loadedmetadata', this.handleVideoLoadedMetadata);
        this.videoEl.addEventListener('resize', this.handleVideoResize, false);
    }
    detectQuirks() {
        var _a, _b;
        (_a = this.inputEl) === null || _a === void 0 ? void 0 : _a.getContext('2d', { willReadFrequently: true });
        (_b = this.outputEl) === null || _b === void 0 ? void 0 : _b.getContext('webgl2', { antialias: true });
        if (isIos()) {
            const mql = portraitMql();
            this.handleOrientationChange(mql);
            mql === null || mql === void 0 ? void 0 : mql.addEventListener('change', this.handleOrientationChange);
        }
    }
    handleVideoLoadedMetadata() {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            yield ((_a = this.videoEl) === null || _a === void 0 ? void 0 : _a.play());
        });
    }
    handleVideoResize() {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            yield ((_a = this.loadFuture) === null || _a === void 0 ? void 0 : _a.promise);
            const oldSettings = this.trackSettings;
            const newSettings = (_b = this.inputTrack) === null || _b === void 0 ? void 0 : _b.getSettings();
            if (((newSettings === null || newSettings === void 0 ? void 0 : newSettings.width) && (oldSettings === null || oldSettings === void 0 ? void 0 : oldSettings.width) && (newSettings === null || newSettings === void 0 ? void 0 : newSettings.width) !== (oldSettings === null || oldSettings === void 0 ? void 0 : oldSettings.width)) ||
                ((newSettings === null || newSettings === void 0 ? void 0 : newSettings.height) && (oldSettings === null || oldSettings === void 0 ? void 0 : oldSettings.height) && (newSettings === null || newSettings === void 0 ? void 0 : newSettings.height) !== (oldSettings === null || oldSettings === void 0 ? void 0 : oldSettings.height))) {
                logger.log('virtual background: video resize detected', { oldSettings, newSettings });
                yield this.updateSize(newSettings.width, newSettings.height);
            }
        });
    }
    handleAssetUrlResolver(assetUri, config) {
        return __awaiter(this, void 0, void 0, function* () {
            logger.log('virtual background: resolving asset url', assetUri, config);
            if (this.options.baseUrl) {
                const assetUrl = `${this.options.baseUrl}/${assetUri}`;
                logger.log('virtual background: using base url override as asset url', assetUrl);
                return assetUrl;
            }
            if (!this.options.authToken) {
                throw new Error('virtual background: missing `authToken` for fetching signed model url');
            }
            return fetchSignedUrl(assetUri, this.options.authToken, this.options.env);
        });
    }
    enable() {
        const _super = Object.create(null, {
            enable: { get: () => super.enable }
        });
        var _a, _b, _c, _d, _e, _f, _g, _h;
        return __awaiter(this, void 0, void 0, function* () {
            if (((_a = this.loadFuture) === null || _a === void 0 ? void 0 : _a.state) === FutureState.Rejected) {
                logger.warn('virtual background: model load failed, cannot enable effect');
                return false;
            }
            if (((_b = this.loadFuture) === null || _b === void 0 ? void 0 : _b.state) === FutureState.Pending) {
                yield ((_c = this.loadFuture) === null || _c === void 0 ? void 0 : _c.promise);
            }
            if (((_d = this.updateFuture) === null || _d === void 0 ? void 0 : _d.state) === FutureState.Pending) {
                logger.log('virtual background: waiting for options update before enabling');
                yield this.updateFuture.promise;
            }
            logger.log('virtual background: enabling effect');
            if (this.isReady && this.effectTrack && this.inputTrack) {
                try {
                    yield _super.enable.call(this);
                    (_e = this.model) === null || _e === void 0 ? void 0 : _e.reset();
                    (_f = this.outputStream) === null || _f === void 0 ? void 0 : _f.removeTrack(this.inputTrack);
                    (_g = this.outputStream) === null || _g === void 0 ? void 0 : _g.addTrack(this.effectTrack);
                    (_h = this.requestFrame) === null || _h === void 0 ? void 0 : _h.call(this);
                    return true;
                }
                catch (e) {
                    this.isEnabled = false;
                    throw e;
                }
            }
            return false;
        });
    }
    disable() {
        const _super = Object.create(null, {
            disable: { get: () => super.disable }
        });
        var _a, _b, _c, _d, _e, _f, _g;
        return __awaiter(this, void 0, void 0, function* () {
            if (((_a = this.loadFuture) === null || _a === void 0 ? void 0 : _a.state) === FutureState.Rejected) {
                logger.warn('virtual background: model load failed, disabling effect anyway');
            }
            if (((_b = this.loadFuture) === null || _b === void 0 ? void 0 : _b.state) === FutureState.Pending) {
                yield ((_c = this.loadFuture) === null || _c === void 0 ? void 0 : _c.promise);
            }
            if (((_d = this.updateFuture) === null || _d === void 0 ? void 0 : _d.state) === FutureState.Pending) {
                logger.log('virtual background: waiting for options update before disabling');
                yield this.updateFuture.promise;
            }
            logger.log('virtual background: disabling effect');
            if (this.isReady && this.effectTrack && this.inputTrack) {
                try {
                    yield _super.disable.call(this);
                    this.clearTimers();
                    (_e = this.model) === null || _e === void 0 ? void 0 : _e.reset();
                    (_f = this.outputStream) === null || _f === void 0 ? void 0 : _f.removeTrack(this.effectTrack);
                    (_g = this.outputStream) === null || _g === void 0 ? void 0 : _g.addTrack(this.inputTrack);
                    return true;
                }
                catch (e) {
                    this.isEnabled = true;
                    throw e;
                }
            }
            return false;
        });
    }
    dispose() {
        const _super = Object.create(null, {
            dispose: { get: () => super.dispose }
        });
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q;
        return __awaiter(this, void 0, void 0, function* () {
            logger.log('virtual background: disposing effect');
            if (((_a = this.loadFuture) === null || _a === void 0 ? void 0 : _a.state) === FutureState.Rejected) {
                logger.warn('virtual background: model load failed, cleaning up resources');
            }
            else if (((_b = this.loadFuture) === null || _b === void 0 ? void 0 : _b.state) === FutureState.Pending) {
                this.loadFuture.reject(new Error('virtual background: loading interrupted by dispose'));
            }
            else if (((_c = this.loadFuture) === null || _c === void 0 ? void 0 : _c.state) === FutureState.Resolved) {
                logger.log('virtual background: waiting for loadFuture to resolve');
                yield this.loadFuture.promise;
            }
            yield this.disable();
            yield ((_d = this.model) === null || _d === void 0 ? void 0 : _d.destroy());
            this.disposeFuture = new Future();
            (_e = this.debugStatsGenerator) === null || _e === void 0 ? void 0 : _e.dispose();
            this.getPluginManager().dispose();
            (_f = portraitMql()) === null || _f === void 0 ? void 0 : _f.removeEventListener('change', this.handleOrientationChange);
            (_g = this.videoEl) === null || _g === void 0 ? void 0 : _g.removeEventListener('loadedmetadata', this.handleVideoLoadedMetadata);
            (_h = this.videoEl) === null || _h === void 0 ? void 0 : _h.removeEventListener('resize', this.handleVideoResize);
            document.removeEventListener('visibilitychange', this.handleVisibilityChange);
            (_j = this.inputEl) === null || _j === void 0 ? void 0 : _j.remove();
            (_k = this.outputEl) === null || _k === void 0 ? void 0 : _k.remove();
            (_l = this.videoEl) === null || _l === void 0 ? void 0 : _l.remove();
            (_m = this.backgroundEl) === null || _m === void 0 ? void 0 : _m.remove();
            (_o = this.virtualVideoEl) === null || _o === void 0 ? void 0 : _o.remove();
            delete this.requestFrame;
            delete this.model;
            delete this.inputEl;
            delete this.outputEl;
            delete this.videoEl;
            delete this.backgroundEl;
            delete this.virtualVideoEl;
            try {
                yield _super.dispose.call(this);
                (_p = this.disposeFuture) === null || _p === void 0 ? void 0 : _p.resolve();
                this.emit(EffectEvent.Disposed);
            }
            catch (error) {
                (_q = this.disposeFuture) === null || _q === void 0 ? void 0 : _q.reject(error);
                throw error;
            }
            this.loadFuture = undefined;
        });
    }
    addBeforeInferenceCallback(callback) {
        this.beforeInferenceCallbacks.push(callback);
    }
    addAfterInferenceCallback(callback) {
        this.afterInferenceCallbacks.push(callback);
    }
    removeBeforeInferenceCallback(callback) {
        this.beforeInferenceCallbacks = this.beforeInferenceCallbacks.filter((cb) => cb !== callback);
    }
    removeAfterInferenceCallback(callback) {
        this.afterInferenceCallbacks = this.afterInferenceCallbacks.filter((cb) => cb !== callback);
    }
    setOnFrameProcessedCallback(callback) {
        logger.warn('virtual background: deprecated method, use addBeforeInferenceCallback instead');
        this.addAfterInferenceCallback(callback);
    }
    updateOptions(options) {
        var _a, _b, _c, _d;
        return __awaiter(this, void 0, void 0, function* () {
            if (((_a = this.updateFuture) === null || _a === void 0 ? void 0 : _a.state) === FutureState.Pending) {
                logger.log('virtual background: waiting for previous update to complete');
                try {
                    yield this.updateFuture.promise;
                }
                catch (error) {
                    logger.warn('virtual background: previous update failed, continuing with new update', error);
                }
            }
            if (((_b = this.disposeFuture) === null || _b === void 0 ? void 0 : _b.state) === FutureState.Pending) {
                try {
                    logger.log('virtual background: waiting for model to finish disposing before updating options');
                    yield this.disposeFuture.promise;
                }
                catch (error) {
                    logger.warn('virtual background: previous disposing failed, continuing with update', error);
                }
            }
            if (((_c = this.loadFuture) === null || _c === void 0 ? void 0 : _c.state) === FutureState.Pending) {
                try {
                    logger.log('virtual background: waiting for model to finish loading before updating options');
                    yield this.loadFuture.promise;
                }
                catch (error) {
                    logger.warn('virtual background: previous loading failed, continuing with update', error);
                }
            }
            this.updateFuture = new Future();
            Object.keys(options).forEach((key) => {
                var _a;
                this.options[key] = (_a = options[key]) !== null && _a !== void 0 ? _a : this.options[key];
            });
            if (typeof this.options.preventBackgroundThrottling !== 'undefined') {
                document.removeEventListener('visibilitychange', this.handleVisibilityChange);
                this.setupVisibilityChange();
            }
            logger.log('virtual background: reloading effect with options', omitAuthToken(this.options));
            try {
                this.configureBuilder();
                yield this.loadModel();
                this.updateFuture.resolve();
                (_d = this.requestFrame) === null || _d === void 0 ? void 0 : _d.call(this);
            }
            catch (error) {
                this.updateFuture.reject(error);
                throw error;
            }
        });
    }
    updateSize(width, height) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            if (((_a = this.loadFuture) === null || _a === void 0 ? void 0 : _a.state) === FutureState.Pending) {
                try {
                    yield this.loadFuture.promise;
                }
                catch (error) {
                    logger.warn('virtual background: previous loading failed, continuing with size update', error);
                }
            }
            if (this.videoEl) {
                this.videoEl.width = width;
                this.videoEl.height = height;
            }
            if (this.inputEl) {
                this.inputEl.width = width;
                this.inputEl.height = height;
            }
            if (this.outputEl) {
                this.outputEl.width = width;
                this.outputEl.height = height;
            }
            if (this.backgroundEl) {
                this.backgroundEl.width = width;
                this.backgroundEl.height = height;
            }
            if (this.trackSettings) {
                this.trackSettings.width = width;
                this.trackSettings.height = height;
            }
            logger.log('virtual background: updating size', { width, height });
            this.loadFuture = new Future();
            try {
                yield this.loadModel();
                this.loadFuture.resolve();
                (_b = this.requestFrame) === null || _b === void 0 ? void 0 : _b.call(this);
            }
            catch (error) {
                this.loadFuture.reject(error);
                throw error;
            }
        });
    }
    getStats() {
        return this.debugStatsGenerator.generateStats(this);
    }
    optimizeBackgroundImage(image) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.backgroundEl) {
                return;
            }
            const targetWidth = this.backgroundEl.width;
            const targetHeight = this.backgroundEl.height;
            if (image.width <= targetWidth * 2 && image.height <= targetHeight * 2) {
                drawImage(image, this.backgroundEl);
                return;
            }
            try {
                const tempCanvas = document.createElement('canvas');
                tempCanvas.width = targetWidth;
                tempCanvas.height = targetHeight;
                const ctx = tempCanvas.getContext('2d');
                if (!ctx) {
                    drawImage(image, this.backgroundEl);
                    return;
                }
                ctx.imageSmoothingEnabled = true;
                ctx.imageSmoothingQuality = 'high';
                ctx.drawImage(image, 0, 0, image.width, image.height, 0, 0, targetWidth, targetHeight);
                drawImage(tempCanvas, this.backgroundEl);
                logger.log('virtual background: optimized background image', {
                    originalSize: `${image.width}x${image.height}`,
                    optimizedSize: `${targetWidth}x${targetHeight}`,
                });
            }
            catch (error) {
                logger.warn('virtual background: optimization failed, using original', error);
                drawImage(image, this.backgroundEl);
            }
        });
    }
}
VirtualBackgroundEffect.kind = 'virtual-background-effect';
VirtualBackgroundEffect.BlurStrength = R;
VirtualBackgroundEffect.Quality = E$1;
const createVirtualBackgroundEffect = (options, preloadAssets) => createEffect(VirtualBackgroundEffect, options, preloadAssets);

export { AdaptiveFrameSkipper, BaseCameraEffect, BaseEffect, BaseMicrophoneEffect, BeRightBack, BeRightBackAction, BeRightBackEvent, BeRightBackPlugin, BeRightBackPluginMode, BeRightBackState, R as BlurStrength, DEFAULT_DEBUG_OVERLAY_OPTIONS, DEFAULT_FRAME_RATE, DEFAULT_NOISE_REDUCTION_MODEL, DebugOverlay, DebugOverlayPlugin, EffectEnv, EffectEvent, EffectState, FrameSkipperInferenceReason, FrameSkipperPlugin, FrameSkipperPluginMode, GainEffect, Logger, NoiseReductionEffect, NoiseReductionMode, NoiseReductionModels, E$1 as Quality, RateEstimationPlugin, RateEstimator, RateEstimatorEvent, RateEstimatorStatus, SkippedFrameRatePlugin, VirtualBackgroundEffect, VirtualBackgroundMode, createEffect, createNoiseReductionEffect, createVirtualBackgroundEffect, getOptionsByMode, logger, makeBeRightBackOptions, makeFrameSkipperOptions, setLogHandler, setLogLevel };
//# sourceMappingURL=index.js.map
